import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AssetValuationDetailsModal from './AssetValuationDetailsModal';
import BarcodeAssetReports from './BarcodeAssetReports';
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Row,
    Form,
    FormGroup,
    Label,
    Input,
    InputGroup,
    InputGroupText,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner,
    Table,
} from 'reactstrap';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import Pagination from '../../../components/Pagination/Pagination';
import { enqueueSnackbar } from 'notistack';
import {
    getAllAssetValuations,
    getAllAssets,
    getAssetsByCategory,
    searchAssets,
    getAllClients,
    getAllProjects,
    getAssetTotalsByCategory,
    getAssetValuation,
    getAssetsByValuation,
    getBarcodePdfFiles,
    getAssetPhotosByAsset,
    getAssetPhotosByBuilding,
    reviewAsset,
    markAssetReviewNotCompleted,
    clearAssetReviewNotCompletedRemark,
    unreviewAsset,
    deleteAsset,
    deleteAssetValuation,
    updateAssetMultipart,
    getAssetSecurityOverviews,
    createAssetSecurityOverview,
    updateAssetSecurityOverview,
    getAssetBuildings,
    getAstBuildingTypes,
    getAstAssetObservedConditions,
    getAllAstInstitutionBuildings,
    exportIncompleteAssets,
    createAssetBuildingMultipart,
    updateAssetBuildingMultipart,
    deleteAssetBuilding,
    uploadAssetFile,
    createAssetPhoto,
    updateAssetPhoto,
    getAssetBuildingConstructions,
    createAssetBuildingConstruction,
    updateAssetBuildingConstruction,
    getAssetBuildingAccommodations,
    createAssetBuildingAccommodations,
    updateAssetBuildingAccommodations,
    deleteAssetBuildingAccommodation,
    getSiteWorks,
    createSiteWork,
    updateSiteWork,
    deleteSiteWork,
    getSiteWorkPhotosBySiteWork,
    createSiteWorkPhoto,
    updateSiteWorkPhoto,
    deleteSiteWorkPhoto,
} from '../../../services/ValitanApiService';
import { exportToCSV, exportToExcel, exportToPDF } from '../../../utils/exportUtils';
import {
    isOtherSelection,
    resolveAssetNameDisplay,
    resolveBuildingNameDisplay,
    resolveOtherDisplayValue,
} from '../../../utils/otherValueDisplay';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import Swal from 'sweetalert2';
import Switch from "react-switch";

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import AssetDetailForm from '../../../components/DataReview/AssetDetailForm';
import AssetPhotoGallery from './components/AssetPhotoGallery';
import {
    BuildingDetailHeader,
    BuildingEditStepper,
    BuildingListCard,
    BuildingSummaryCard,
} from './components/BuildingReviewComponents';
import AssetReviewFooter from './components/AssetReviewFooter';
import BarcodePdfModal from './components/BarcodePdfModal';
import BuildingReviewFooter from './components/BuildingReviewFooter';
import ImagePreviewModal from './components/ImagePreviewModal';
import { CategoryFilterTabs, ReviewFilterTabs } from './components/ReviewFilterTabs';
import ReviewWizardModal from './components/ReviewWizardModal';
import {
    ACCOMMODATION_TYPE_OPTIONS,
    ASSET_PHOTO_TYPE_OPTIONS,
    BARCODE_PREFIX_TO_CATEGORY,
    BRAND_BLUE,
    BUILDING_EDIT_STEPS,
    BUILDING_USE_STATUS_OPTIONS,
    CATEGORY_CODES_WITH_POST_PHOTO_TAB,
    CEILING_OPTIONS,
    DOOR_OPTIONS,
    FLOOR_OPTIONS,
    INCOME_LEVEL_OPTIONS,
    REVIEW_REMARK_ISSUE_TAB,
    ROAD_TYPE_OPTIONS,
    ROOF_OPTIONS,
    SITE_WORK_TYPE_OPTIONS,
    WALL_OPTIONS,
    WINDOW_OPTIONS,
    YEAR_OPTIONS,
} from './constants/assetValuationConstants';
import { spinnerStyle } from './styles/assetValuationStyles';
import {
    createBuildingDraftFromEntity,
    createEmptySecurityOverview,
    createEmptySiteWorkDraft,
    extractCouncilIdFromAsset,
    formatDateValue,
    formatNumberValue,
    getCategoryCode,
    getReviewBucket,
    getReviewBucketLabel,
    getReviewNotCompletedRemark,
    getValuationCategoryCodes,
    isAssetReviewed,
    isReviewNotCompleted,
    normalizeDisplayValue,
    parseListResponse,
    pickValidTabValue,
    renderCellValue,
    renderStatusBadge,
    resolveServerFileUrl,
    toHumanReadableAssetName,
} from './utils/assetValuationUtils';

// Add spinner animation style
const normalizeUrlList = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    return value ? [value] : [];
};

const AssetValuationListTables = ({
    reviewFilter = "all",
    showReviewTabs = false,
    defaultReviewTab = "unreviewed",
    showCategoryTabs = false,
    categoryTabs = [],
    defaultCategoryTab = "all",
    showCategoryStats = false,
    showDateFilters = true,
    showAddButton = true,
    actionMode = "full",
    tableMode = "valuations",
    breadcrumbTitle = "Valuations management",
    breadcrumbItem = "Asset Valuations",
    cardTitle = "Asset Valuations",
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const viewParam = queryParams.get("view");
    const isReportsView = String(viewParam || "").toLowerCase() === "reports";
    const availableReviewTabs = useMemo(() => {
        if (!showReviewTabs) return ["all"];

        return ["unreviewed", "reviewed", "review-not-completed", REVIEW_REMARK_ISSUE_TAB];
    }, [showReviewTabs]);
    const availableCategoryTabs = useMemo(() => {
        if (!showCategoryTabs) return ["all"];
        return categoryTabs.map((tab) => String(tab?.value || "")).filter(Boolean);
    }, [showCategoryTabs, categoryTabs]);
    const resolvedInitialReviewTab = useMemo(
        () =>
            pickValidTabValue(
                queryParams.get("reviewTab"),
                availableReviewTabs,
                showReviewTabs ? defaultReviewTab : "all"
            ),
        [queryParams, availableReviewTabs, showReviewTabs, defaultReviewTab]
    );
    const resolvedInitialCategoryTab = useMemo(
        () =>
            pickValidTabValue(
                queryParams.get("categoryTab"),
                availableCategoryTabs,
                showCategoryTabs ? defaultCategoryTab : "all"
            ),
        [queryParams, availableCategoryTabs, showCategoryTabs, defaultCategoryTab]
    );

    const [valuations, setValuations] = useState([]);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedValuation, setSelectedValuation] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [barcodePdfModalOpen, setBarcodePdfModalOpen] = useState(false);
    const [loadingBarcodePdfs, setLoadingBarcodePdfs] = useState(false);
    const [barcodePdfsByCategory, setBarcodePdfsByCategory] = useState({});
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [reviewData, setReviewData] = useState(null);
    const [reviewStepIndex, setReviewStepIndex] = useState(0);
    const [editingStepKey, setEditingStepKey] = useState(null);
    const [stepDrafts, setStepDrafts] = useState({});
    const [savingStepKey, setSavingStepKey] = useState(null);
    const [detailAsset, setDetailAsset] = useState(null);
    const [updatingAsset, setUpdatingAsset] = useState(false);
    const [reviewToggleAssetId, setReviewToggleAssetId] = useState(null);
    const [finishingReview, setFinishingReview] = useState(false);
    const [assetPhotosByAssetId, setAssetPhotosByAssetId] = useState({});
    const [assetPhotosLoadingForAssetId, setAssetPhotosLoadingForAssetId] = useState(null);
    const [activeReviewTab, setActiveReviewTab] = useState(resolvedInitialReviewTab);
    const [activeCategoryTab, setActiveCategoryTab] = useState(resolvedInitialCategoryTab);
    const [serverCategoryTotalsByCode, setServerCategoryTotalsByCode] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editModalLoading, setEditModalLoading] = useState(false);
    const [editAssets, setEditAssets] = useState([]);
    const [selectedEditAssetId, setSelectedEditAssetId] = useState("");
    const [selectedEditAssetDraft, setSelectedEditAssetDraft] = useState(null);
    const [savingEditAsset, setSavingEditAsset] = useState(false);
    const [editModalCouncilId, setEditModalCouncilId] = useState("");
    const [editBuildingStepIndex, setEditBuildingStepIndex] = useState(0);
    const [highestSavedStepIndex, setHighestSavedStepIndex] = useState(0);
    const [securityOverviewDraft, setSecurityOverviewDraft] = useState(createEmptySecurityOverview);
    const [savingSecurityOverview, setSavingSecurityOverview] = useState(false);
    const [loadingSecurityOverview, setLoadingSecurityOverview] = useState(false);
    const [editBuildings, setEditBuildings] = useState([]);
    const [loadingEditBuildings, setLoadingEditBuildings] = useState(false);
    const [buildingTypes, setBuildingTypes] = useState([]);
    const [institutionBuildingOptions, setInstitutionBuildingOptions] = useState([]);
    const [buildingObservedConditions, setBuildingObservedConditions] = useState([]);
    const [buildingDrafts, setBuildingDrafts] = useState({});
    const [savingBuildingSlot, setSavingBuildingSlot] = useState(null);
    const [buildingStepTabsBySlot, setBuildingStepTabsBySlot] = useState({});
    const [dismissedBuildingSlots, setDismissedBuildingSlots] = useState(() => new Set());
    const [selectedBuildingDetailSlot, setSelectedBuildingDetailSlot] = useState(null);
    const [constructionDrafts, setConstructionDrafts] = useState({});
    const [savingConstructionSlot, setSavingConstructionSlot] = useState(null);
    const [buildingAccommodationsByBuildingId, setBuildingAccommodationsByBuildingId] = useState({});
    const [accommodationDrafts, setAccommodationDrafts] = useState({});
    const [loadingBuildingAccommodationsForBuildingId, setLoadingBuildingAccommodationsForBuildingId] = useState(null);
    const [savingAccommodationSlot, setSavingAccommodationSlot] = useState(null);
    const [deletingAccommodationId, setDeletingAccommodationId] = useState(null);
    const [buildingPhotosByBuildingId, setBuildingPhotosByBuildingId] = useState({});
    const [loadingBuildingPhotosForBuildingId, setLoadingBuildingPhotosForBuildingId] = useState(null);
    const [updatingPhotoId, setUpdatingPhotoId] = useState(null);
    const [editSiteWorks, setEditSiteWorks] = useState([]);
    const [loadingEditSiteWorks, setLoadingEditSiteWorks] = useState(false);
    const [siteWorkDraft, setSiteWorkDraft] = useState(createEmptySiteWorkDraft);
    const [savingSiteWork, setSavingSiteWork] = useState(false);
    const [deletingSiteWorkId, setDeletingSiteWorkId] = useState(null);
    const [deletingBuildingId, setDeletingBuildingId] = useState(null);
    const [deletingAssetId, setDeletingAssetId] = useState(null);
    const [siteWorkPhotosBySiteWorkId, setSiteWorkPhotosBySiteWorkId] = useState({});
    const [loadingSiteWorkPhotosForSiteWorkId, setLoadingSiteWorkPhotosForSiteWorkId] = useState(null);
    const [updatingSiteWorkPhotoId, setUpdatingSiteWorkPhotoId] = useState(null);
    const loadedEditSiteWorksKeyRef = useRef("");
    const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
    const [imagePreviewUrl, setImagePreviewUrl] = useState("");
    const [imagePreviewTitle, setImagePreviewTitle] = useState("");
    const showEditOnlyAction = actionMode === "edit-only";

    const toggleModal = () => setModalOpen(!modalOpen);

    useEffect(() => {
        setActiveReviewTab(resolvedInitialReviewTab);
    }, [resolvedInitialReviewTab]);

    useEffect(() => {
        setActiveCategoryTab(resolvedInitialCategoryTab);
    }, [resolvedInitialCategoryTab]);

    useEffect(() => {
        const nextParams = new URLSearchParams(location.search);

        if (showReviewTabs) {
            nextParams.set("reviewTab", activeReviewTab);
        } else {
            nextParams.delete("reviewTab");
        }

        if (showCategoryTabs) {
            nextParams.set("categoryTab", activeCategoryTab);
        } else {
            nextParams.delete("categoryTab");
        }

        const currentSearch = location.search.startsWith("?") ? location.search.slice(1) : location.search;
        const nextSearch = nextParams.toString();

        if (nextSearch !== currentSearch) {
            navigate(
                {
                    pathname: location.pathname,
                    search: nextSearch ? `?${nextSearch}` : "",
                },
                { replace: true }
            );
        }
    }, [
        activeReviewTab,
        activeCategoryTab,
        showReviewTabs,
        showCategoryTabs,
        location.pathname,
        location.search,
        navigate,
    ]);

    useEffect(() => {
        if (!reviewModalOpen) {
            setDetailAsset(null);
        }
    }, [reviewModalOpen]);

    const extractPhotoList = useCallback((response) => {
        const payload = response?.data?.data ?? response?.data ?? response ?? [];
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.content)) return payload.content;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
    }, []);

    const ensureAssetPhotosLoaded = useCallback(
        async (assetId) => {
            if (!assetId || assetPhotosByAssetId[assetId]) return;
            try {
                setAssetPhotosLoadingForAssetId(assetId);
                const response = await getAssetPhotosByAsset(assetId);
                const photos = extractPhotoList(response);
                setAssetPhotosByAssetId((prev) => ({
                    ...prev,
                    [assetId]: photos,
                }));
            } catch (error) {
                console.error("Error loading asset photos:", error);
                setAssetPhotosByAssetId((prev) => ({
                    ...prev,
                    [assetId]: [],
                }));
            } finally {
                setAssetPhotosLoadingForAssetId(null);
            }
        },
        [assetPhotosByAssetId, extractPhotoList]
    );

    useEffect(() => {
        if (!reviewModalOpen) return;
        const assetId = detailAsset?.id;
        if (assetId) {
            ensureAssetPhotosLoaded(assetId);
        }
    }, [detailAsset?.id, ensureAssetPhotosLoaded, reviewModalOpen]);

    const [filters, setFilters] = useState({
        projectId: '',
        clientId: '',
        status: '',
        startDate: '',
        endDate: '',
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [assetSearchFilters, setAssetSearchFilters] = useState({
        search: "",
        name: "",
        description: "",
        otherName: "",
        valuationRef: "",
        valuer: "",
        institutionName: "",
        condition: "",
    });
    const [appliedAssetSearchFilters, setAppliedAssetSearchFilters] = useState({
        search: "",
        name: "",
        description: "",
        otherName: "",
        valuationRef: "",
        valuer: "",
        institutionName: "",
        condition: "",
    });

    const [paginationInfo, setPaginationInfo] = useState({
        pageNumber: 0,
        pageSize: 10,
        totalElements: 0,
        totalPages: 0,
    });

    const isDataReviewAssetMode = tableMode === "assets" && showReviewTabs;
    const normalizeSearchFilters = useCallback(
        (filtersToNormalize) =>
            Object.entries(filtersToNormalize || {}).reduce((acc, [key, value]) => {
                const normalizedValue = typeof value === "string" ? value.trim() : value;
                acc[key] = normalizedValue || "";
                return acc;
            }, {}),
        []
    );
    const reviewTabs = useMemo(() => {
        return [
            { value: "unreviewed", label: "Un Reviewed" },
            { value: "reviewed", label: "Reviewed Data" },
            { value: "review-not-completed", label: "Review Incomplete" },
            { value: REVIEW_REMARK_ISSUE_TAB, label: "Remark Issues" },
        ];
    }, []);

    // ✅ Normalize data for table display
    const normalizeValuation = (v) => ({
        id: v.id,
        valuationRef: v.valuation_ref || '-',
        projectName: v.project?.name || '-',
        clientName: v.client?.name || '-',
        valuerName: v.valuer_user
            ? `${v.valuer_user.first_name ?? ''} ${v.valuer_user.middle_name ?? ''} ${v.valuer_user.last_name ?? ''}`.trim()
            : 'N/A',
        status: v.valuation_status || '-',
        valuationDate: v.valuation_date
            ? new Date(v.valuation_date).toLocaleDateString('en-GB')
            : '-',
        // THIS IS THE KEY LINE:
        raw: v   // ← Keep full original object!
    });

    const normalizeAsset = (a) => ({
        id: a.id,
        assetName: resolveAssetNameDisplay(a),
        categoryCode: getCategoryCode(a) || '-',
        odo: a.odo ?? '-',
        barcode: normalizeDisplayValue(a.barcode) || '-',
        quantity: a.quantity ?? a.qty ?? a.count ?? "-",
        valuerName:
            normalizeDisplayValue(a.valuer_name || a.valuerName) ||
            (a.asset_valuation?.valuer_user
                ? `${a.asset_valuation.valuer_user.first_name ?? ''} ${a.asset_valuation.valuer_user.middle_name ?? ''} ${a.asset_valuation.valuer_user.last_name ?? ''}`.trim()
                : a.valuation?.valuer_user
                    ? `${a.valuation.valuer_user.first_name ?? ''} ${a.valuation.valuer_user.middle_name ?? ''} ${a.valuation.valuer_user.last_name ?? ''}`.trim()
                    : '-') ||
            '-',
        dataReviewerName:
            normalizeDisplayValue(
                a.reviewer
                    ? [
                          a.reviewer.first_name ?? a.reviewer.firstName ?? "",
                          a.reviewer.middle_name ?? a.reviewer.middleName ?? "",
                          a.reviewer.last_name ?? a.reviewer.lastName ?? "",
                      ]
                          .filter(Boolean)
                          .join(" ")
                    : a.reviewer_name || a.reviewerName
            ) || '-',
        location:
            normalizeDisplayValue(a.location || a.address || a.institution_name || a.building_name) || '-',
        condition:
            normalizeDisplayValue(
                a.observed_condition?.name || a.observed_condition_name || a.condition
            ) || '-',
        reviewed: getReviewBucketLabel(a),
        reviewNotCompletedRemark: getReviewNotCompletedRemark(a),
        raw: a,
    });

    // ✅ Fetch valuations from API
    const fetchValuations = useCallback(
        async (pageNumber = 0, pageSize = 10) => {
            if (isReportsView) return;
            try {
                setLoading(true);
                setError(null);

                let result = {};
                let paginationResult = {};
                if (tableMode === "assets") {
                    const selectedCategoryCode = String(activeCategoryTab || "").toUpperCase();
                    const shouldFetchByCategory =
                        showCategoryTabs && selectedCategoryCode && selectedCategoryCode !== "ALL";

                    const normalizedAppliedSearch = normalizeSearchFilters(appliedAssetSearchFilters);
                    const shouldUseServerSearch =
                        isDataReviewAssetMode &&
                        Object.values(normalizedAppliedSearch).some((value) => value);
                    const reviewNotCompletedParam =
                        showReviewTabs
                            ? (
                                activeReviewTab === "review-not-completed" || activeReviewTab === REVIEW_REMARK_ISSUE_TAB
                                    ? true
                                    : activeReviewTab === "unreviewed"
                                        ? false
                                        : undefined
                            )
                            : undefined;
                    const reviewRemarkIssueParam =
                        showReviewTabs
                            ? (
                                activeReviewTab === REVIEW_REMARK_ISSUE_TAB
                                    ? true
                                    : activeReviewTab === "review-not-completed"
                                        ? false
                                        : undefined
                            )
                            : undefined;
                    const reviewedParam =
                        showReviewTabs
                            ? (
                                activeReviewTab === "reviewed"
                                    ? true
                                    : activeReviewTab === "unreviewed"
                                        ? false
                                        : undefined
                            )
                            : undefined;

                    const response = shouldUseServerSearch
                        ? await searchAssets({
                              ...normalizedAppliedSearch,
                              categoryCode: shouldFetchByCategory ? selectedCategoryCode : undefined,
                              reviewed: reviewedParam,
                              reviewNotCompleted: reviewNotCompletedParam,
                              reviewRemarkIssue: reviewRemarkIssueParam,
                              page: pageNumber,
                              size: pageSize,
                          })
                        : shouldFetchByCategory
                            ? await getAssetsByCategory({
                                  assetCategoryCode: selectedCategoryCode,
                                  reviewed: reviewedParam,
                                  reviewNotCompleted: reviewNotCompletedParam,
                                  reviewRemarkIssue: reviewRemarkIssueParam,
                                  page: pageNumber,
                                  size: pageSize,
                              })
                            : await getAllAssets(pageNumber, pageSize);
                    const responseData = response?.data || {};
                    result = responseData?.data || responseData;
                    paginationResult =
                        result?.pagination || responseData?.pagination || result;
                    setValuations((result.content || []).map(normalizeAsset));
                } else {
                    const response = await getAllAssetValuations({
                        projectId: filters.projectId || undefined,
                        clientId: filters.clientId || undefined,
                        status: filters.status || undefined,
                        startDate: filters.startDate || undefined,
                        endDate: filters.endDate || undefined,
                        page: pageNumber,
                        size: pageSize,
                    });

                    const { data } = response;
                    result = data.data; // backend wraps inside "data"
                    paginationResult =
                        result?.pagination || data?.pagination || result;
                    setValuations((result.content || []).map(normalizeValuation));
                }
                setPaginationInfo({
                    pageNumber: paginationResult.number ?? paginationResult.page ?? 0,
                    pageSize: paginationResult.size ?? pageSize,
                    totalElements:
                        paginationResult.total_elements ?? paginationResult.totalElements ?? 0,
                    totalPages:
                        paginationResult.total_pages ?? paginationResult.totalPages ?? 0,
                });
            } catch (err) {
                console.error('❌ Error fetching valuations:', err);
                setError(err);
            } finally {
                setLoading(false);
            }
        },
        [
            filters,
            tableMode,
            showCategoryTabs,
            activeCategoryTab,
            isReportsView,
            appliedAssetSearchFilters,
            isDataReviewAssetMode,
            normalizeSearchFilters,
            showReviewTabs,
            activeReviewTab,
        ]
    );

    // ✅ Fetch dropdown data (clients + projects)
    const fetchDropdownData = useCallback(async () => {
        try {
            const [clientsRes, projectsRes] = await Promise.all([
                getAllClients(),
                getAllProjects(),
            ]);

            setClients(
                Array.isArray(clientsRes.data?.data?.content)
                    ? clientsRes.data.data.content
                    : []
            );

            setProjects(
                Array.isArray(projectsRes.data?.content)
                    ? projectsRes.data.content
                    : []
            );
        } catch (err) {
            console.error('Error fetching dropdown data', err);
            setClients([]);
            setProjects([]);
        }
    }, []);

    const fetchCategoryTotals = useCallback(async () => {
        try {
            const reviewNotCompletedParam =
                showReviewTabs
                    ? (activeReviewTab === "review-not-completed" || activeReviewTab === REVIEW_REMARK_ISSUE_TAB ? true : activeReviewTab === "unreviewed" ? false : undefined)
                    : undefined;
            const reviewRemarkIssueParam =
                showReviewTabs
                    ? (activeReviewTab === REVIEW_REMARK_ISSUE_TAB ? true : activeReviewTab === "review-not-completed" ? false : undefined)
                    : undefined;
            const reviewedParam =
                showReviewTabs
                    ? (activeReviewTab === "reviewed" ? true : activeReviewTab === "unreviewed" ? false : undefined)
                    : undefined;
            const response = await getAssetTotalsByCategory(reviewedParam, reviewNotCompletedParam, reviewRemarkIssueParam);
            const payload = response?.data?.data || response?.data || {};
            const rows = Array.isArray(payload?.categories) ? payload.categories : [];
            const totals = rows.reduce((acc, row) => {
                const code = String(row?.category_code || row?.categoryCode || "").toUpperCase();
                if (!code) return acc;
                acc[code] = Number(row?.asset_records ?? row?.assetRecords ?? 0);
                return acc;
            }, {});
            setServerCategoryTotalsByCode(totals);
        } catch (err) {
            console.error("Error fetching category totals:", err);
            setServerCategoryTotalsByCode({});
        }
    }, [showReviewTabs, activeReviewTab]);

    const refreshAssetReviewViews = useCallback(async () => {
        if (tableMode !== "assets" || isReportsView) return;

        const refreshTasks = [
            fetchValuations(paginationInfo.pageNumber ?? 0, paginationInfo.pageSize ?? 10),
        ];

        if (showCategoryTabs && showCategoryStats) {
            refreshTasks.push(fetchCategoryTotals());
        }

        await Promise.all(refreshTasks);
    }, [
        tableMode,
        isReportsView,
        fetchValuations,
        paginationInfo.pageNumber,
        paginationInfo.pageSize,
        showCategoryTabs,
        showCategoryStats,
        fetchCategoryTotals,
    ]);

    useEffect(() => {
        document.title = 'Asset Valuations | Valitan';
    }, []);

    useEffect(() => {
        if (isReportsView) return;
        fetchValuations(0, paginationInfo.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchValuations, isReportsView]);

    useEffect(() => {
        fetchDropdownData();
    }, [fetchDropdownData]);

    useEffect(() => {
        if (tableMode === "assets" && showCategoryTabs && showCategoryStats) {
            fetchCategoryTotals();
        }
    }, [tableMode, showCategoryTabs, showCategoryStats, fetchCategoryTotals]);

    const handlePreviousPage = () => {
        if (paginationInfo.pageNumber > 0) {
            fetchValuations(
                paginationInfo.pageNumber - 1,
                paginationInfo.pageSize
            );
        }
    };

    const handleNextPage = () => {
        if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
            fetchValuations(
                paginationInfo.pageNumber + 1,
                paginationInfo.pageSize
            );
        }
    };

    const handlePageChange = (pageNumber, pageSize) =>
        fetchValuations(pageNumber, pageSize);

    const handlePageSizeChange = (newPageSize) =>
        fetchValuations(0, newPageSize);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleAssetSearchFilterChange = (e) => {
        const { name, value } = e.target;
        setAssetSearchFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleDeleteValuation = async (valuationRow) => {
        const valuationId = valuationRow?.id || valuationRow?.raw?.id;
        if (!valuationId) return;

        const status = String(
            valuationRow?.status ||
            valuationRow?.raw?.valuation_status ||
            valuationRow?.raw?.valuationStatus ||
            ""
        ).toUpperCase();

        if (status !== "DRAFT") {
            enqueueSnackbar("Only DRAFT valuations can be deleted.", { variant: "warning" });
            return;
        }

        const ref =
            valuationRow?.valuationRef ||
            valuationRow?.raw?.valuation_ref ||
            valuationRow?.raw?.valuationRef ||
            "this valuation";

        const confirmation = await Swal.fire({
            title: "Delete Draft Valuation?",
            html: `Delete <strong>${ref}</strong>?<br/><small class="text-muted">Linked assets will be soft-deleted. Only DRAFT valuations can be removed.</small>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc3545",
        });
        if (!confirmation.isConfirmed) return;

        try {
            await deleteAssetValuation(valuationId);
            enqueueSnackbar("Draft valuation deleted successfully.", { variant: "success" });
            await fetchValuations(paginationInfo.pageNumber ?? 0, paginationInfo.pageSize ?? 10);
        } catch (error) {
            console.error("Failed to delete valuation:", error);
            const message =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to delete valuation.";
            enqueueSnackbar(message, { variant: "error" });
        }
    };

    const handleDeleteAssetRow = async (id) => {
        const confirmed = await Swal.fire({
            title: "Delete Asset?",
            text: "Are you sure you want to remove this asset? This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
        });

        if (!confirmed.isConfirmed) return;

        try {
            await deleteAsset(id);
            Swal.fire("Deleted!", "Asset has been removed.", "success");
            fetchValuations();
        } catch (error) {
            console.error("Failed to delete asset:", error);
            Swal.fire("Error", "Could not delete asset. It might have dependencies.", "error");
        }
    };

    const handleApplyFilters = () => {
        if (isDataReviewAssetMode) {
            setSearchTerm("");
            setAppliedAssetSearchFilters(normalizeSearchFilters(assetSearchFilters));
            return;
        }
        fetchValuations(0, paginationInfo.pageSize);
    };

    const handleResetFilters = () => {
        if (isDataReviewAssetMode) {
            const emptySearchFilters = {
                search: "",
                name: "",
                description: "",
                otherName: "",
                valuationRef: "",
                valuer: "",
                institutionName: "",
                condition: "",
            };
            setAssetSearchFilters(emptySearchFilters);
            setAppliedAssetSearchFilters(emptySearchFilters);
            setSearchTerm("");
            return;
        }
        setFilters({
            projectId: '',
            clientId: '',
            status: '',
            startDate: '',
            endDate: '',
        });
        setSearchTerm(''); // Also clear search term
        fetchValuations(0, paginationInfo.pageSize);
    };

    const closeReviewModal = useCallback(() => {
        setReviewModalOpen(false);
        setReviewData(null);
        setReviewStepIndex(0);
        setEditingStepKey(null);
        setStepDrafts({});
        setSavingStepKey(null);
        setReviewLoading(false);
        setAssetPhotosByAssetId({});
        setAssetPhotosLoadingForAssetId(null);
    }, []);

    const closeEditModal = useCallback(() => {
        setEditModalOpen(false);
        setEditModalLoading(false);
        setEditAssets([]);
        setSelectedEditAssetId("");
        setSelectedEditAssetDraft(null);
        setSavingEditAsset(false);
        setEditModalCouncilId("");
        setEditBuildingStepIndex(0);
        setHighestSavedStepIndex(0);
        setSecurityOverviewDraft(createEmptySecurityOverview());
        setSavingSecurityOverview(false);
        setLoadingSecurityOverview(false);
        setEditBuildings([]);
        setLoadingEditBuildings(false);
        setBuildingTypes([]);
        setInstitutionBuildingOptions([]);
        setBuildingObservedConditions([]);
        setBuildingDrafts({});
        setDismissedBuildingSlots(new Set());
        setSavingBuildingSlot(null);
        setBuildingStepTabsBySlot({});
        setSelectedBuildingDetailSlot(null);
        setConstructionDrafts({});
        setSavingConstructionSlot(null);
        setBuildingAccommodationsByBuildingId({});
        setAccommodationDrafts({});
        setLoadingBuildingAccommodationsForBuildingId(null);
        setSavingAccommodationSlot(null);
        setDeletingAccommodationId(null);
        setBuildingPhotosByBuildingId({});
        setLoadingBuildingPhotosForBuildingId(null);
        setUpdatingPhotoId(null);
        setEditSiteWorks([]);
        setLoadingEditSiteWorks(false);
        setSiteWorkDraft(createEmptySiteWorkDraft());
        setSavingSiteWork(false);
        setDeletingSiteWorkId(null);
        setSiteWorkPhotosBySiteWorkId({});
        setLoadingSiteWorkPhotosForSiteWorkId(null);
        setUpdatingSiteWorkPhotoId(null);
        loadedEditSiteWorksKeyRef.current = "";
        setImagePreviewOpen(false);
        setImagePreviewUrl("");
        setImagePreviewTitle("");
    }, []);

    const openImagePreview = useCallback((url, title = "Asset Image") => {
        if (!url) return;
        setImagePreviewUrl(url);
        setImagePreviewTitle(title);
        setImagePreviewOpen(true);
    }, []);

    const openAssetEditModal = useCallback(
        async (valuationRow) => {
            if (tableMode === "assets") {
                const asset = valuationRow?.raw || valuationRow;
                if (!asset) return;
                setEditModalOpen(true);
                setEditModalLoading(false);
                setEditAssets([asset]);
                setSelectedEditAssetId(String(asset.id || ""));
                setSelectedEditAssetDraft(asset);
                const editCouncilId = extractCouncilIdFromAsset(asset);
                setEditModalCouncilId(editCouncilId);
                setEditBuildingStepIndex(0);
                setHighestSavedStepIndex(0);
                return;
            }

            const valuationId = valuationRow?.raw?.id || valuationRow?.id;
            if (!valuationId) return;

            setEditModalOpen(true);
            setEditModalLoading(true);
            setEditAssets([]);
            setSelectedEditAssetId("");
            setEditBuildingStepIndex(0);

            try {
                const assetsRes = await getAssetsByValuation(valuationId);
                const assets = assetsRes?.data?.data || assetsRes?.data || [];
                const safeAssets = Array.isArray(assets) ? assets : [];
                setEditAssets(safeAssets);

                if (safeAssets.length > 0) {
                    const selectedCode = String(activeCategoryTab || "").toUpperCase();
                    const matchedAsset =
                        selectedCode && selectedCode !== "ALL"
                            ? safeAssets.find((asset) => getCategoryCode(asset) === selectedCode)
                            : null;
                    const preferred = matchedAsset || safeAssets[0];
                    setSelectedEditAssetId(String(preferred?.id || ""));
                    setSelectedEditAssetDraft(preferred || null);
                    const valuationCouncilId =
                        valuationRow?.raw?.project?.council?.id ||
                        valuationRow?.raw?.project?.council_id ||
                        valuationRow?.raw?.project?.councilId ||
                        "";
                    setEditModalCouncilId(
                        extractCouncilIdFromAsset(preferred) || (valuationCouncilId ? String(valuationCouncilId) : "")
                    );
                }
            } catch (error) {
                console.error("Failed to load assets for edit modal:", error);
                enqueueSnackbar("Failed to load asset fields.", { variant: "error" });
                closeEditModal();
            } finally {
                setEditModalLoading(false);
            }
        },
        [activeCategoryTab, closeEditModal, tableMode]
    );

    const reviewSteps = useMemo(() => {
        if (!reviewData) return [];

        const { valuation = {}, assets = [], siteWorks = [] } = reviewData;
        const project = valuation.project || {};
        const client = valuation.client || {};
        const valuer = valuation.valuer_user || valuation.valuerUser || {};

        const siteWorkTypes = Array.from(
            new Set(
                siteWorks
                    .map((sw) => normalizeDisplayValue(sw.site_work_type))
                    .filter(Boolean)
            )
        );

        const valuationSummary = [
            { label: "Reference", value: valuation.valuation_ref || valuation.valuation_number || "-" },
            { label: "Status", value: valuation.valuation_status || valuation.status || "-" },
            { label: "Date", value: formatDateValue(valuation.valuation_date) },
            { label: "Currency", value: valuation.currency || "TZS" },
            { label: "Notes", value: valuation.notes || "-" },
        ];

        const projectSummary = [
            { label: "Project Name", value: project.name || "-" },
            { label: "Project Code", value: project.code || "-" },
            { label: "Category", value: project.project_category?.name || project.category?.name || "-" },
            { label: "Council", value: project.council?.name || "-" },
            { label: "District", value: project.council?.district?.name || project.district?.name || "-" },
            { label: "Start Date", value: formatDateValue(project.start_date) },
            { label: "End Date", value: formatDateValue(project.end_date) },
        ];

        const clientType =
            client.client_type?.name ||
            client.type?.name ||
            client.type ||
            client.client_type ||
            (client.is_government ? "Government" : "Private");
        const clientTin = client.tin || client.tin_number || client.tax_id;
        const clientSummary = [
            { label: "Client Name", value: client.name || "-" },
            { label: "Email", value: client.email || "-" },
            { label: "Phone", value: client.phone || client.mobile_number || "-" },
            { label: "TIN", value: clientTin || "-" },
            { label: "Client Type", value: clientType || "-" },
        ];

        const overviewGroups = [
            { title: "Valuation", items: valuationSummary },
            { title: "Project", items: projectSummary },
            { title: "Client", items: clientSummary },
        ];

        const valuerSummary = [
            { label: "Valuer Name", value: `${valuer.first_name || valuer.firstName || ""} ${valuer.last_name || valuer.lastName || ""}`.trim() || "-" },
            { label: "Certificate No.", value: valuer.certificate_number || "-" },
            { label: "Email", value: valuer.email || "-" },
            { label: "Mobile", value: valuer.mobile_number || "-" },
        ];

        const steps = [
            {
                key: "project-overview",
                title: "Project Overview",
                type: "details",
                groups: overviewGroups,
            },
            {
                key: "valuer-details",
                title: "Valuer Information",
                type: "details",
                groups: [{ title: "Valuer Information", items: valuerSummary }],
            },
            {
                key: "assets-list",
                title: "Assets",
                type: "assetsList",
                assets,
                summaryRows: [
                    { label: "Total Assets", value: assets.length.toString() },
                    {
                        label: "Categories Captured",
                        value:
                            Array.from(
                                new Set(
                                    assets
                                        .map((asset) => asset.asset_category?.name || asset.category?.name)
                                        .filter(Boolean)
                                )
                            ).join(", ") || "-",
                    },
                ],
            },
            {
                key: "asset-detail",
                title: "Asset Detail",
                type: "assetDetail",
                asset: detailAsset,
                projectCouncilId:
                    project.council?.id || project.council_id || project.councilId,
            },
            {
                key: "site-works",
                title: "Site Works",
                type: "siteWorks",
                summaryRows: [
                    { label: "Total Site Works", value: siteWorks.length.toString() },
                    { label: "Types Captured", value: siteWorkTypes.join(", ") || "-" },
                ],
                siteWorks,
            },
        ];

        const selectedCategoryCode = getCategoryCode(detailAsset || {});
        const selectedCategoryName =
            detailAsset?.asset_category?.name ||
            detailAsset?.category?.name ||
            detailAsset?.assetCategory?.name ||
            selectedCategoryCode;

        if (
            selectedCategoryCode &&
            CATEGORY_CODES_WITH_POST_PHOTO_TAB.has(selectedCategoryCode)
        ) {
            const categoryAssets = assets.filter(
                (asset) => getCategoryCode(asset) === selectedCategoryCode
            );

            const categoryStep = {
                key: `assets-${selectedCategoryCode.toLowerCase()}`,
                title: `${selectedCategoryName || selectedCategoryCode} Tab`,
                type: "assets",
                assets: categoryAssets,
                summaryRows: [
                    {
                        label: "Category",
                        value: selectedCategoryName || selectedCategoryCode,
                    },
                    {
                        label: "Total Assets",
                        value: String(categoryAssets.length),
                    },
                ],
            };

            const assetDetailIndex = steps.findIndex((step) => step.key === "asset-detail");
            if (assetDetailIndex !== -1) {
                steps.splice(assetDetailIndex + 1, 0, categoryStep);
            }
        }

        if (detailAsset?.id) {
            const photos = assetPhotosByAssetId[detailAsset.id] || [];
            const isFinishStep = !!selectedCategoryCode && !CATEGORY_CODES_WITH_POST_PHOTO_TAB.has(selectedCategoryCode);
            const imageStep = {
                key: "asset-images",
                title: "Asset Images",
                type: "assetImages",
                assetId: detailAsset.id,
                isFinishStep,
                photos,
                loading: assetPhotosLoadingForAssetId === detailAsset.id,
                summaryRows: [
                    {
                        label: "Asset",
                        value:
                            resolveAssetNameDisplay(detailAsset, "") ||
                            detailAsset.description ||
                            "-",
                    },
                    {
                        label: "Total Images",
                        value: String(photos.length),
                    },
                ],
            };

            const assetDetailIndex = steps.findIndex((step) => step.key === "asset-detail");
            if (assetDetailIndex !== -1) {
                steps.splice(assetDetailIndex + 1, 0, imageStep);
            }
        }

        return steps;
    }, [reviewData, detailAsset, assetPhotosByAssetId, assetPhotosLoadingForAssetId]);

    const handleViewAssetDetail = useCallback(
        (asset) => {
            if (!asset) return;
            setDetailAsset(asset);
            const detailIndex = reviewSteps.findIndex((step) => step.key === "asset-detail");
            if (detailIndex !== -1) {
                setReviewStepIndex(detailIndex);
            }
        },
        [reviewSteps]
    );

    const formatInputWithCommas = (val) => {
        if (val === null || val === undefined) return "";
        let clean = String(val).replace(/,/g, "");
        if (clean === "") return "";
        
        let parts = clean.split(".");
        parts[0] = parts[0].replace(/\D/g, "");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        
        if (parts.length > 1) {
            parts[1] = parts[1].replace(/\D/g, "").slice(0, 2);
            return parts[0] + "." + parts[1];
        }
        
        if (clean.endsWith(".")) {
            return parts[0] + ".";
        }
        
        return parts[0];
    };

    const toNumberOrNull = (value) => {
        if (value === null || value === undefined || value === "") return null;
        const cleanValue = typeof value === "string" ? value.replace(/,/g, "") : value;
        const n = Number(cleanValue);
        return Number.isNaN(n) ? null : n;
    };

    const toIntOrNull = (value) => {
        if (value === null || value === undefined || value === "") return null;
        const n = parseInt(value, 10);
        return Number.isNaN(n) ? null : n;
    };

    const toBoolOrFalse = (value) => {
        if (value === true || value === "true") return true;
        if (value === false || value === "false") return false;
        return false;
    };

    const toStringOrNull = (value) => {
        if (value === null || value === undefined) return null;
        const str = String(value).trim();
        return str === "" ? null : str;
    };

    const formatOtherBuildingName = (value) => {
        const str = String(value || "").trim();
        if (!str) return null;
        return str.toUpperCase().replace(/[\s-]+/g, "_");
    };

    const buildAssetUpdatePayload = (asset) => {
        const roomNameOrNumber = toStringOrNull(asset.room_name_or_number || asset.roomNameOrNumber);
        const roomOthers = toStringOrNull(asset.room_others || asset.roomOthers);
        const finalRoomValue = roomNameOrNumber === "OTHERS" ? roomOthers : roomNameOrNumber;

        const buildingName = toStringOrNull(asset.building_name || asset.buildingName);
        const otherBuildingName = toStringOrNull(asset.other_building_name || asset.otherBuildingName);
        const finalBuildingName = buildingName === "OTHERS" ? "OTHERS" : buildingName;
        const finalOtherBuildingName = buildingName === "OTHERS" ? otherBuildingName : null;

        const institutionCategory = toStringOrNull(
            asset.institution_category_code ||
            asset.institution_category ||
            asset.institutionCategory
        );

        const institutionName = toStringOrNull(
            asset.institution_other ||
            asset.institutionOther ||
            asset.institution_name ||
            asset.institutionName
        );

        const payload = {
            asset_valuation_id: toStringOrNull(asset.asset_valuation_id || asset.assetValuationId),
            asset_category_id: toStringOrNull(asset.asset_category_id || asset.assetCategoryId),
            ward_id: toStringOrNull(asset.ward_id || asset.wardId || asset.ward?.id),
            name: toStringOrNull(asset.name),
            quantity: toIntOrNull(asset.quantity) ?? 1,
            barcode: toStringOrNull(asset.barcode),
            description: toStringOrNull(asset.description),
            latitude: toNumberOrNull(asset.latitude),
            longitude: toNumberOrNull(asset.longitude),
            street: toStringOrNull(asset.street),
            type_of_bridge: toStringOrNull(asset.type_of_bridge || asset.typeOfBridge),
            width_m: toNumberOrNull(asset.width_m ?? asset.widthM),
            span_m: toNumberOrNull(asset.span_m ?? asset.spanM),
            thickness_m: toNumberOrNull(asset.thickness_m ?? asset.thicknessM),
            depth_m: toNumberOrNull(asset.depth_m ?? asset.depthM),
            type_of_foundation: toStringOrNull(asset.type_of_foundation || asset.typeOfFoundation),
            deck_type: toStringOrNull(asset.deck_type || asset.deckType),
            handrail_type: toStringOrNull(asset.handrail_type || asset.handrailType),
            number_of_steps: toStringOrNull(asset.number_of_steps || asset.numberOfSteps),
            observed_condition: toStringOrNull(asset.observed_condition || asset.observedCondition),
            acquisition_date: toStringOrNull(asset.acquisition_date || asset.acquisitionDate || asset.acquisition_year),
            building_number: toStringOrNull(asset.building_number || asset.buildingNumber),
            plot_number: toStringOrNull(asset.plot_number || asset.plotNumber),
            block_number: toStringOrNull(asset.block_number || asset.blockNumber),
            certificate_of_title: toStringOrNull(asset.certificate_of_title || asset.certificateOfTitle),
            tenure_years: toIntOrNull(asset.tenure_years || asset.tenureYears),
            plot_size: toNumberOrNull(asset.plot_size || asset.plotSize),
            size_unit: toStringOrNull(asset.size_unit || asset.sizeUnit),
            year_of_manufacture: toStringOrNull(asset.year_of_manufacture || asset.yearOfManufacture),
            chassis_number: toStringOrNull(asset.chassis_number || asset.chassisNumber),
            engine_number: toStringOrNull(asset.engine_number || asset.engineNumber),
            engine_capacity: toNumberOrNull(asset.engine_capacity || asset.engineCapacity),
            odo: toIntOrNull(asset.odo),
            fuel_type: toStringOrNull(asset.fuel_type || asset.fuelType),
            imported_from: toStringOrNull(asset.imported_from || asset.importedFrom),
            imported_from_others: toStringOrNull(asset.imported_from_others || asset.importedFromOthers),
            body_type: toStringOrNull(asset.body_type || asset.bodyType),
            body_type_others: toStringOrNull(asset.body_type_others || asset.bodyTypeOthers),
            registration_number: toStringOrNull(asset.registration_number || asset.registrationNumber),
            make: toStringOrNull(asset.make),
            model: toStringOrNull(asset.model),
            serial_number: toStringOrNull(asset.serial_number || asset.serialNumber),
            has_site_work: toBoolOrFalse(asset.has_site_work ?? asset.hasSiteWork),
            has_water: toBoolOrFalse(asset.has_water ?? asset.hasWater),
            has_electricity: toBoolOrFalse(asset.has_electricity ?? asset.hasElectricity),
            number_of_buildings: toIntOrNull(asset.number_of_buildings || asset.numberOfBuildings),
            institution_name: institutionName,
            building_name: finalBuildingName,
            other_building_name: finalOtherBuildingName,
            floor: toStringOrNull(asset.floor),
            room_name_or_number: finalRoomValue,
            room_others: roomOthers,
            building_use_id: toStringOrNull(asset.building_use_id || asset.buildingUseId || asset.building_use?.id),
            measurement_unit: toStringOrNull(asset.measurement_unit || asset.measurementUnit),
            measurement: toNumberOrNull(asset.measurement),
            is_land_surveyed: toBoolOrFalse(asset.is_land_surveyed ?? asset.isLandSurveyed),
            remark: toStringOrNull(asset.remark || asset.remarks || asset.comment),
            other_name: toStringOrNull(asset.other_name || asset.otherName),
            institution_category: institutionCategory,
            institution_category_other: toStringOrNull(asset.institution_category_other || asset.institutionCategoryOther),
            institution_other: toStringOrNull(asset.institution_other || asset.institutionOther),
            right_of_occupance_start: toStringOrNull(asset.right_of_occupance_start || asset.right_of_occupancy_start),
            right_of_occupance_end: toStringOrNull(asset.right_of_occupance_end || asset.right_of_occupancy_end),
            land_value_per_sqm: toNumberOrNull(asset.land_value_per_sqm || asset.landValuePerSqm),
        };

        return Object.fromEntries(
            Object.entries(payload).filter(([, value]) => value !== null && value !== undefined)
        );
    };

    const toSnakeUpper = (value) => {
        const text = String(value || "").trim();
        if (!text) return "";
        return text.toUpperCase().replace(/\s+/g, "_");
    };

    const fromSnakeToPretty = (value) =>
        String(value || "")
            .toLowerCase()
            .split("_")
            .map((word) => (word ? `${word[0].toUpperCase()}${word.slice(1)}` : word))
            .join(" ");

    const normalizeObservedConditionValue = useCallback(
        (value) => {
            const raw = String(value || "").trim();
            if (!raw) return "";
            const matched = buildingObservedConditions.find(
                (item) =>
                    String(item?.id || "").trim() === raw ||
                    String(item?.code || "").trim() === raw ||
                    String(item?.name || "").trim() === raw
            );
            return matched?.code || raw;
        },
        [buildingObservedConditions]
    );

    const resolveObservedConditionLabel = useCallback(
        (value) => {
            const raw = String(value || "").trim();
            if (!raw) return "-";
            const matched = buildingObservedConditions.find(
                (item) =>
                    String(item?.id || "").trim() === raw ||
                    String(item?.code || "").trim() === raw ||
                    String(item?.name || "").trim() === raw
            );
            return matched?.name || raw;
        },
        [buildingObservedConditions]
    );

    const resolveSiteWorkTypeLabel = useCallback((value, otherSpecify = "") => {
        const raw = String(value || "").trim();
        if (!raw) return "-";
        if (raw === "OTHERS" && String(otherSpecify || "").trim()) {
            return otherSpecify.trim();
        }
        return SITE_WORK_TYPE_OPTIONS.find((item) => item.value === raw)?.label || fromSnakeToPretty(raw);
    }, []);

    const createSiteWorkDraftFromEntity = useCallback(
        (siteWork, assetId = "", valuationId = "") => ({
            id: siteWork?.id || "",
            asset_id: siteWork?.asset_id || siteWork?.assetId || assetId,
            valuation_id:
                siteWork?.valuation_id ||
                siteWork?.valuationId ||
                siteWork?.asset_valuation_id ||
                siteWork?.assetValuationId ||
                valuationId,
            site_work_type: siteWork?.site_work_type || siteWork?.siteWorkType || "",
            other_specify: siteWork?.other_specify || siteWork?.otherSpecify || "",
            description: siteWork?.description || "",
            dimensions: siteWork?.dimensions || "",
            serial_number: siteWork?.serial_number || siteWork?.serialNumber || "",
            observed_condition: normalizeObservedConditionValue(
                siteWork?.observed_condition || siteWork?.observedCondition || ""
            ),
            remark: siteWork?.remark || "",
            measurement: siteWork?.measurement !== undefined && siteWork?.measurement !== null ? String(siteWork.measurement) : "",
            construction_rate: (siteWork?.construction_rate !== undefined && siteWork?.construction_rate !== null)
                ? String(siteWork.construction_rate)
                : (siteWork?.constructionRate !== undefined && siteWork?.constructionRate !== null)
                    ? String(siteWork.constructionRate)
                    : "",
        }),
        [normalizeObservedConditionValue]
    );

    const createEmptyConstructionDraft = (buildingId = "") => ({
        id: "",
        building_id: buildingId,
        roof: [],
        ceiling: [],
        walls: [],
        windows: [],
        doors: [],
        floors: [],
        roof_others: "",
        ceiling_others: "",
        wall_others: "",
        window_others: "",
        door_others: "",
        floor_others: "",
    });

    const createEmptyAccommodationDraft = (floor = "GROUND_FLOOR") => ({
        floor,
        name: "",
        other_name: "",
        total: "",
    });

    const floorCodeToLabel = (floorCode) => {
        const normalized = String(floorCode || "").toUpperCase();
        if (!normalized) return "Ground Floor";
        if (normalized === "GROUND_FLOOR") return "Ground Floor";
        if (normalized === "FIRST_FLOOR") return "First Floor";
        if (normalized === "SECOND_FLOOR") return "Second Floor";
        const match = normalized.match(/^(\d+)(?:ST|ND|RD|TH)_FLOOR$/);
        if (match?.[1]) return `${match[1]}th Floor`;
        return fromSnakeToPretty(normalized);
    };

    const getBuildingFloorCodes = useCallback((buildingDraft) => {
        const floorCount = Math.max(toIntOrNull(buildingDraft?.no_of_floors) || 1, 1);
        return Array.from({ length: floorCount }, (_, index) => {
            if (index === 0) return "GROUND_FLOOR";
            if (index === 1) return "FIRST_FLOOR";
            if (index === 2) return "SECOND_FLOOR";
            return `${index + 1}TH_FLOOR`;
        });
    }, []);

    const getAccommodationDisplayName = (item) =>
        resolveOtherDisplayValue(item?.name, item?.other_name || item?.otherName, "-");

    const getSelectedEditValuationId = useCallback(() => {
        return (
            selectedEditAssetDraft?.asset_valuation_id ||
            selectedEditAssetDraft?.assetValuationId ||
            selectedEditAssetDraft?.asset_valuation?.id ||
            selectedEditAssetDraft?.valuation?.id ||
            ""
        );
    }, [selectedEditAssetDraft]);

    const ensureSiteWorkPhotosLoaded = useCallback(async (siteWorkId) => {
        if (!siteWorkId || siteWorkPhotosBySiteWorkId[siteWorkId]) return;
        try {
            setLoadingSiteWorkPhotosForSiteWorkId(siteWorkId);
            const response = await getSiteWorkPhotosBySiteWork(siteWorkId);
            const photos = parseListResponse(response).map((photo) => ({
                ...photo,
                photo_url: resolveServerFileUrl(
                    photo?.photo_url || photo?.photoUrl || photo?.url || photo?.path || ""
                ),
            }));
            setSiteWorkPhotosBySiteWorkId((prev) => ({
                ...prev,
                [siteWorkId]: photos,
            }));
        } catch (error) {
            console.error("Failed to load site work photos:", error);
            setSiteWorkPhotosBySiteWorkId((prev) => ({
                ...prev,
                [siteWorkId]: [],
            }));
        } finally {
            setLoadingSiteWorkPhotosForSiteWorkId(null);
        }
    }, [siteWorkPhotosBySiteWorkId]);

    const loadSiteWorksForEdit = useCallback(async () => {
        const assetId = selectedEditAssetDraft?.id;
        const valuationId = getSelectedEditValuationId();
        if (!assetId) {
            enqueueSnackbar("Asset reference is missing.", { variant: "warning" });
            return false;
        }

        const embeddedSiteWorks = Array.isArray(selectedEditAssetDraft?.site_works)
            ? selectedEditAssetDraft.site_works
            : Array.isArray(selectedEditAssetDraft?.siteWorks)
                ? selectedEditAssetDraft.siteWorks
                : [];

        setLoadingEditSiteWorks(true);
        try {
            let items = [];

            if (valuationId) {
                const response = await getSiteWorks(0, 500, assetId, valuationId);
                items = parseListResponse(response);
            }

            if (!items.length && embeddedSiteWorks.length) {
                items = embeddedSiteWorks;
            }

            if (!items.length && !valuationId) {
                const response = await getSiteWorks(0, 500, assetId, null);
                items = parseListResponse(response);
            }

            const normalizedItems = items.map((item) => ({
                ...item,
                photo_urls: normalizeUrlList(item?.photo_urls || item?.photoUrls).map((url) => resolveServerFileUrl(url)),
                photos: (item?.photos || []).map((photo) => ({
                    ...photo,
                    photo_url: resolveServerFileUrl(photo?.photo_url || photo?.photoUrl || photo?.url || photo?.path || ""),
                })),
            }));

            setEditSiteWorks(normalizedItems);
            setSiteWorkDraft(createEmptySiteWorkDraft(String(assetId), String(valuationId || normalizedItems?.[0]?.valuation_id || normalizedItems?.[0]?.valuationId || "")));
            setSiteWorkPhotosBySiteWorkId((prev) => {
                const next = { ...prev };
                normalizedItems.forEach((item) => {
                    const siteWorkId = String(item?.id || "");
                    if (!siteWorkId) return;
                    const embeddedPhotos = Array.isArray(item?.photos) ? item.photos : [];
                    if (embeddedPhotos.length) {
                        next[siteWorkId] = embeddedPhotos;
                    } else if (Array.isArray(item?.photo_urls) && item.photo_urls.length) {
                        next[siteWorkId] = item.photo_urls
                            .filter(Boolean)
                            .map((url, idx) => ({
                                id: `${siteWorkId}-fallback-${idx}`,
                                photo_url: resolveServerFileUrl(url),
                                photo_type: "Photo",
                            }));
                    }
                });
                return next;
            });
            await Promise.all(
                normalizedItems
                    .map((item) => item?.id)
                    .filter(Boolean)
                    .filter((id) => {
                        const item = normalizedItems.find((siteWork) => String(siteWork?.id || "") === String(id));
                        const hasEmbeddedPhotos = Array.isArray(item?.photos) && item.photos.length > 0;
                        const hasEmbeddedPhotoUrls = Array.isArray(item?.photo_urls) && item.photo_urls.length > 0;
                        return !hasEmbeddedPhotos && !hasEmbeddedPhotoUrls && !(Array.isArray(siteWorkPhotosBySiteWorkId[String(id)]) && siteWorkPhotosBySiteWorkId[String(id)].length);
                    })
                    .map((id) => ensureSiteWorkPhotosLoaded(String(id)))
            );
            return true;
        } catch (error) {
            console.error("Failed to load site works:", error);
            if (embeddedSiteWorks.length) {
                const normalizedItems = embeddedSiteWorks.map((item) => ({
                    ...item,
                    photo_urls: normalizeUrlList(item?.photo_urls || item?.photoUrls).map((url) => resolveServerFileUrl(url)),
                    photos: (item?.photos || []).map((photo) => ({
                        ...photo,
                        photo_url: resolveServerFileUrl(photo?.photo_url || photo?.photoUrl || photo?.url || photo?.path || ""),
                    })),
                }));
                setEditSiteWorks(normalizedItems);
                setSiteWorkDraft(createEmptySiteWorkDraft(String(assetId), String(valuationId || "")));
                setSiteWorkPhotosBySiteWorkId((prev) => {
                    const next = { ...prev };
                    normalizedItems.forEach((item) => {
                        const siteWorkId = String(item?.id || "");
                        if (!siteWorkId) return;
                        const embeddedPhotos = Array.isArray(item?.photos) ? item.photos : [];
                        if (embeddedPhotos.length) {
                            next[siteWorkId] = embeddedPhotos;
                        } else if (Array.isArray(item?.photo_urls) && item.photo_urls.length) {
                            next[siteWorkId] = item.photo_urls
                                .filter(Boolean)
                                .map((url, idx) => ({
                                    id: `${siteWorkId}-fallback-${idx}`,
                                    photo_url: resolveServerFileUrl(url),
                                    photo_type: "Photo",
                                }));
                        }
                    });
                    return next;
                });
                return true;
            }
            enqueueSnackbar("Failed to load site works.", { variant: "error" });
            return false;
        } finally {
            setLoadingEditSiteWorks(false);
        }
    }, [ensureSiteWorkPhotosLoaded, getSelectedEditValuationId, selectedEditAssetDraft, siteWorkPhotosBySiteWorkId]);

    const handleGoToBuildingSiteWorks = useCallback(async () => {
        const loaded = await loadSiteWorksForEdit();
        if (!loaded) return;
        setSelectedBuildingDetailSlot(null);
        setHighestSavedStepIndex((prev) => Math.max(prev, 3));
        setEditBuildingStepIndex(3);
    }, [loadSiteWorksForEdit]);

    const selectedEditAssetBuildingCount = useMemo(
        () =>
            toIntOrNull(
                selectedEditAssetDraft?.number_of_buildings ||
                    selectedEditAssetDraft?.numberOfBuildings
            ) || null,
        [selectedEditAssetDraft]
    );
    const selectedEditAssetHasSiteWork = useMemo(
        () => toBoolOrFalse(selectedEditAssetDraft?.has_site_work ?? selectedEditAssetDraft?.hasSiteWork),
        [selectedEditAssetDraft]
    );

    const selectedEditAssetValuerName = useMemo(() => {
        const valuer =
            selectedEditAssetDraft?.asset_valuation?.valuer_user ||
            selectedEditAssetDraft?.asset_valuation?.valuerUser ||
            selectedEditAssetDraft?.valuation?.valuer_user ||
            selectedEditAssetDraft?.valuation?.valuerUser;

        return (
            selectedEditAssetDraft?.valuer_name ||
            selectedEditAssetDraft?.valuerName ||
            [
                valuer?.first_name || valuer?.firstName || "",
                valuer?.middle_name || valuer?.middleName || "",
                valuer?.last_name || valuer?.lastName || "",
            ]
                .filter(Boolean)
                .join(" ")
        );
    }, [selectedEditAssetDraft]);

    const loadSecurityOverview = useCallback(async () => {
        const assetId = selectedEditAssetDraft?.id;
        const valuationId = getSelectedEditValuationId();
        if (!assetId || !valuationId) return;

        setLoadingSecurityOverview(true);
        try {
            const response = await getAssetSecurityOverviews({
                assetId: String(assetId),
                assetValuationId: String(valuationId),
                page: 0,
                size: 10,
            });
            const overviews = parseListResponse(response);
            const first = overviews[0] || null;
            if (first) {
                setSecurityOverviewDraft((prev) => ({
                    ...prev,
                    ...createEmptySecurityOverview(),
                    ...first,
                    number_of_buildings:
                        toIntOrNull(
                            first?.number_of_buildings ||
                                first?.numberOfBuildings ||
                                selectedEditAssetBuildingCount
                        ) || null,
                }));
            } else {
                setSecurityOverviewDraft({
                    ...createEmptySecurityOverview(),
                    number_of_buildings: selectedEditAssetBuildingCount,
                });
            }
        } catch (error) {
            console.error("Failed to load security overview:", error);
            setSecurityOverviewDraft({
                ...createEmptySecurityOverview(),
                number_of_buildings: selectedEditAssetBuildingCount,
            });
        } finally {
            setLoadingSecurityOverview(false);
        }
    }, [selectedEditAssetDraft?.id, getSelectedEditValuationId, selectedEditAssetBuildingCount]);

    const loadBuildingsForEdit = useCallback(async () => {
        const assetId = selectedEditAssetDraft?.id;
        const valuationId = getSelectedEditValuationId();
        if (!assetId || !valuationId) return;

        setLoadingEditBuildings(true);
        try {
            const response = await getAssetBuildings({
                assetId: String(assetId),
                valuationId: String(valuationId),
                page: 0,
                size: 200,
            });
            const buildings = parseListResponse(response);
            setEditBuildings(buildings);
        } catch (error) {
            console.error("Failed to load buildings:", error);
            setEditBuildings([]);
        } finally {
            setLoadingEditBuildings(false);
        }
    }, [selectedEditAssetDraft?.id, getSelectedEditValuationId]);

    const loadBuildingFormOptions = useCallback(async () => {
        try {
            const [typesRes, conditionsRes, institutionBuildingsRes] = await Promise.all([
                getAstBuildingTypes(0, 1000),
                getAstAssetObservedConditions(),
                getAllAstInstitutionBuildings(0, 100000),
            ]);
            setBuildingTypes(parseListResponse(typesRes));
            setBuildingObservedConditions(parseListResponse(conditionsRes));
            const uniqueBuildingsByName = new Map();
            parseListResponse(institutionBuildingsRes).forEach((building) => {
                const name = toStringOrNull(building?.name || building?.building_name || building?.buildingName);
                if (name && !uniqueBuildingsByName.has(name)) {
                    uniqueBuildingsByName.set(name, {
                        id: building?.id || building?.code || name,
                        name,
                    });
                }
            });
            setInstitutionBuildingOptions(Array.from(uniqueBuildingsByName.values()));
        } catch (error) {
            console.error("Failed to load building form options:", error);
            setBuildingTypes([]);
            setInstitutionBuildingOptions([]);
            setBuildingObservedConditions([]);
        }
    }, []);

    useEffect(() => {
        if (!isDataReviewAssetMode) return;
        if (buildingObservedConditions.length > 0) return;
        loadBuildingFormOptions();
    }, [isDataReviewAssetMode, buildingObservedConditions.length, loadBuildingFormOptions]);

    const saveBuildingSlotDraft = async (slotNumber) => {
        const assetId = selectedEditAssetDraft?.id;
        const valuationId = getSelectedEditValuationId();
        const draft = buildingDrafts[slotNumber];
        if (!assetId || !valuationId || !draft) {
            enqueueSnackbar("Building context is missing. Please try again.", { variant: "error" });
            return;
        }

        const selectedType = buildingTypes.find(
            (t) => String(t.id || "") === String(draft.building_type_id || "")
        );
        const buildingLabel =
            selectedType?.name || selectedType?.label || selectedType?.code || `Building ${slotNumber}`;
        const selectedBuildingName = toStringOrNull(draft.building_name || draft.buildingName);
        const isOtherBuildingName = isOtherSelection(draft.building_name);
        const otherBuildingName = isOtherBuildingName
            ? formatOtherBuildingName(draft.other_building_name || draft.otherBuildingName || draft.other_name || draft.otherName)
            : null;

        if (!selectedBuildingName) {
            enqueueSnackbar("Please select the building name.", { variant: "warning" });
            return;
        }

        if (isOtherBuildingName && !otherBuildingName) {
            enqueueSnackbar("Please specify the building name.", { variant: "warning" });
            return;
        }

        const confirmation = await Swal.fire({
            title: `Save ${buildingLabel}?`,
            text: "Are you sure you want to save this building form?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, Save",
            cancelButtonText: "Cancel",
            confirmButtonColor: BRAND_BLUE,
        });
        if (!confirmation.isConfirmed) return;

        const formPayload = {
            asset_id: String(assetId),
            asset_valuation_id: String(valuationId),
            is_primary: !!draft.is_primary,
            building_type_id: draft.building_type_id || null,
            building_name: selectedBuildingName,
            other_building_name: otherBuildingName,
            serial_number: draft.building_number || String(slotNumber) || null,
            no_of_floors: toIntOrNull(draft.no_of_floors),
            year_built: toIntOrNull(draft.year_built),
            building_measurement: toNumberOrNull(draft.building_measurement),
            use_status: draft.use_status || null,
            completion_rate: toIntOrNull(draft.completion_rate),
            observed_condition: draft.observed_condition || null,
            useful_life_span_years: toIntOrNull(draft.useful_life_span_years),
            construction_rate_per_sqm: toNumberOrNull(draft.construction_rate_per_sqm),
        };

        if (draft.id) {
            formPayload.id = draft.id;
        }

        const cleanedPayload = Object.fromEntries(
            Object.entries(formPayload).filter(
                ([key, value]) =>
                    key === "other_building_name" ||
                    (value !== null && value !== undefined && value !== "")
            )
        );

        const multipart = new FormData();
        multipart.append("data", JSON.stringify(cleanedPayload));
        if (draft.sketch_image_file instanceof File) {
            multipart.append("sketch_image", draft.sketch_image_file);
        }

        setSavingBuildingSlot(slotNumber);
        try {
            let savedBuildingId = draft.id || "";
            if (draft.id) {
                const response = await updateAssetBuildingMultipart(draft.id, multipart);
                savedBuildingId = response?.data?.data?.id || response?.data?.id || savedBuildingId;
            } else {
                const response = await createAssetBuildingMultipart(multipart);
                savedBuildingId = response?.data?.data?.id || response?.data?.id || savedBuildingId;
            }
            enqueueSnackbar(`Building ${slotNumber} saved successfully.`, { variant: "success" });
            await loadBuildingsForEdit();
            if (savedBuildingId) {
                setBuildingDrafts((prev) => ({
                    ...prev,
                    [slotNumber]: {
                        ...draft,
                        id: savedBuildingId,
                    },
                }));
                await loadConstructionForBuilding(slotNumber, savedBuildingId);
            }
            setBuildingStepTabForSlot(slotNumber, "construction");
        } catch (error) {
            console.error("Failed to save building:", error);
            enqueueSnackbar(`Failed to save building ${slotNumber}.`, { variant: "error" });
        } finally {
            setSavingBuildingSlot(null);
        }
    };

    const loadConstructionForBuilding = useCallback(
        async (slotNumber, buildingId) => {
            const valuationId = getSelectedEditValuationId();
            if (!buildingId || !valuationId) return;

            try {
                const response = await getAssetBuildingConstructions({
                    buildingId: String(buildingId),
                    assetValuationId: String(valuationId),
                    page: 0,
                    size: 10,
                });
                const list = parseListResponse(response);
                const existing = list[0] || null;
                if (existing) {
                    setConstructionDrafts((prev) => ({
                        ...prev,
                        [slotNumber]: {
                            id: existing.id || "",
                            building_id: String(buildingId),
                            roof: String(existing.roof || "").split(",").filter(Boolean),
                            ceiling: String(existing.ceiling || "").split(",").filter(Boolean),
                            walls: String(existing.walls || "").split(",").filter(Boolean),
                            windows: String(existing.windows || "").split(",").filter(Boolean),
                            doors: String(existing.doors || "").split(",").filter(Boolean),
                            floors: String(existing.floors || "").split(",").filter(Boolean),
                            roof_others: fromSnakeToPretty(existing.roof_others || existing.roofOthers),
                            ceiling_others: fromSnakeToPretty(existing.ceiling_others || existing.ceilingOthers),
                            wall_others: fromSnakeToPretty(existing.wall_others || existing.wallOthers),
                            window_others: fromSnakeToPretty(existing.window_others || existing.windowOthers),
                            door_others: fromSnakeToPretty(existing.door_others || existing.doorOthers),
                            floor_others: fromSnakeToPretty(existing.floor_others || existing.floorOthers),
                        },
                    }));
                } else {
                    setConstructionDrafts((prev) => ({
                        ...prev,
                        [slotNumber]: createEmptyConstructionDraft(String(buildingId)),
                    }));
                }
            } catch (error) {
                console.error("Failed to load construction details:", error);
                setConstructionDrafts((prev) => ({
                    ...prev,
                    [slotNumber]: createEmptyConstructionDraft(String(buildingId)),
                }));
            }
        },
        [getSelectedEditValuationId]
    );

    const toggleConstructionSelection = (slotNumber, fieldKey, code) => {
        setConstructionDrafts((prev) => {
            const current = prev[slotNumber] || createEmptyConstructionDraft();
            const selected = Array.isArray(current[fieldKey]) ? current[fieldKey] : [];
            const exists = selected.includes(code);
            return {
                ...prev,
                [slotNumber]: {
                    ...current,
                    [fieldKey]: exists ? selected.filter((x) => x !== code) : [...selected, code],
                },
            };
        });
    };

    const saveConstructionForSlot = async (slotNumber) => {
        const valuationId = getSelectedEditValuationId();
        const buildingId =
            buildingDrafts[slotNumber]?.id ||
            editBuildingsBySlot[slotNumber]?.id ||
            "";
        const draft = constructionDrafts[slotNumber];

        if (!valuationId || !buildingId || !draft) {
            enqueueSnackbar("Construction context is missing. Save building first.", { variant: "error" });
            return;
        }

        const buildingDraft = buildingDrafts[slotNumber] || {};
        const selectedType = buildingTypes.find(
            (t) => String(t.id || "") === String(buildingDraft.building_type_id || "")
        );
        const constructionLabel =
            selectedType?.name || selectedType?.label || selectedType?.code || `Building ${slotNumber}`;

        const confirmation = await Swal.fire({
            title: `Save Construction for ${constructionLabel}?`,
            text: "Save Step 2.1.2b construction details?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Save Construction",
            cancelButtonText: "Cancel",
            confirmButtonColor: BRAND_BLUE,
        });
        if (!confirmation.isConfirmed) return;

        const draftId = String(draft.id || "").trim();
        const isPersistedConstructionId = Boolean(draftId) && !draftId.startsWith("temp_");

        const payload = {
            building_id: String(buildingId),
            asset_valuation_id: String(valuationId),
            roof: (draft.roof || []).join(","),
            ceiling: (draft.ceiling || []).join(","),
            walls: (draft.walls || []).join(","),
            windows: (draft.windows || []).join(","),
            doors: (draft.doors || []).join(","),
            floors: (draft.floors || []).join(","),
            roof_others: toSnakeUpper(draft.roof_others),
            ceiling_others: toSnakeUpper(draft.ceiling_others),
            wall_others: toSnakeUpper(draft.wall_others),
            window_others: toSnakeUpper(draft.window_others),
            door_others: toSnakeUpper(draft.door_others),
            floor_others: toSnakeUpper(draft.floor_others),
        };

        setSavingConstructionSlot(slotNumber);
        try {
            let response;
            if (isPersistedConstructionId) {
                response = await updateAssetBuildingConstruction(draftId, payload);
            } else {
                response = await createAssetBuildingConstruction(payload);
            }
            const saved = response?.data?.data || response?.data || {};
            setConstructionDrafts((prev) => ({
                ...prev,
                [slotNumber]: {
                    ...draft,
                    id: saved.id || (isPersistedConstructionId ? draftId : ""),
                    building_id: String(buildingId),
                },
            }));
            enqueueSnackbar(`Construction for Building ${slotNumber} saved.`, { variant: "success" });
            setBuildingStepTabForSlot(slotNumber, "images");
            ensureBuildingPhotosLoaded(String(buildingId));
        } catch (error) {
            console.error("Failed to save construction:", error);
            const apiMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message;
            enqueueSnackbar(
                `Failed to save construction for Building ${slotNumber}${apiMessage ? `: ${apiMessage}` : "."}`,
                { variant: "error" }
            );
        } finally {
            setSavingConstructionSlot(null);
        }
    };

    const ensureBuildingAccommodationsLoaded = useCallback(
        async (buildingId) => {
            if (!buildingId || buildingAccommodationsByBuildingId[buildingId]) return;
            const valuationId = getSelectedEditValuationId();
            if (!valuationId) return;
            try {
                setLoadingBuildingAccommodationsForBuildingId(buildingId);
                const response = await getAssetBuildingAccommodations({
                    assetValuationId: String(valuationId),
                    buildingId: String(buildingId),
                    page: 0,
                    size: 200,
                });
                const list = parseListResponse(response);
                setBuildingAccommodationsByBuildingId((prev) => ({
                    ...prev,
                    [String(buildingId)]: list,
                }));
            } catch (error) {
                console.error("Failed to load building accommodations:", error);
                setBuildingAccommodationsByBuildingId((prev) => ({
                    ...prev,
                    [String(buildingId)]: [],
                }));
            } finally {
                setLoadingBuildingAccommodationsForBuildingId(null);
            }
        },
        [buildingAccommodationsByBuildingId, getSelectedEditValuationId]
    );

    const saveAccommodationForSlot = async (slotNumber) => {
        const valuationId = getSelectedEditValuationId();
        const buildingId =
            buildingDrafts[slotNumber]?.id ||
            editBuildingsBySlot[slotNumber]?.id ||
            "";
        const draft = accommodationDrafts[slotNumber];

        if (!valuationId || !buildingId || !draft) {
            enqueueSnackbar("Accommodation context is missing. Save building first.", { variant: "error" });
            return;
        }

        const floor = String(draft.floor || "").toUpperCase();
        const name = String(draft.name || "").trim();
        const otherName = String(draft.other_name || "").trim();
        const total = toIntOrNull(draft.total);
        if (!floor || !name || !total || total <= 0) {
            enqueueSnackbar("Select floor, accommodation type and valid units.", { variant: "warning" });
            return;
        }
        if (name === "OTHERS" && !otherName) {
            enqueueSnackbar("Specify the accommodation type for Others.", { variant: "warning" });
            return;
        }

        const currentList = buildingAccommodationsByBuildingId[String(buildingId)] || [];
        const targetDisplay = name === "OTHERS" ? otherName.toLowerCase() : name.toLowerCase();
        const existing = currentList.find((item) => {
            const itemFloor = String(item.floor || "").toUpperCase();
            const itemName = String(item.name || "").toUpperCase();
            const itemDisplay =
                itemName === "OTHERS"
                    ? String(item.other_name || item.otherName || "").trim().toLowerCase()
                    : String(item.name || "").trim().toLowerCase();
            return itemFloor === floor && itemDisplay === targetDisplay;
        });

        const payload = {
            ...(existing?.id ? { id: existing.id } : {}),
            building_id: String(buildingId),
            asset_valuation_id: String(valuationId),
            name,
            other_name: name === "OTHERS" ? otherName : null,
            total,
            floor,
        };

        const confirmation = await Swal.fire({
            title: `Save ${floorCodeToLabel(floor)} accommodation?`,
            text: "Save Step 2.1.2d building accommodation details?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Save Accommodation",
            cancelButtonText: "Cancel",
            confirmButtonColor: BRAND_BLUE,
        });
        if (!confirmation.isConfirmed) return;

        setSavingAccommodationSlot(slotNumber);
        try {
            if (existing?.id) {
                await updateAssetBuildingAccommodations([payload]);
            } else {
                await createAssetBuildingAccommodations([payload]);
            }
            await ensureBuildingAccommodationsLoaded(String(buildingId));
            const refreshed = await getAssetBuildingAccommodations({
                assetValuationId: String(valuationId),
                buildingId: String(buildingId),
                page: 0,
                size: 200,
            });
            setBuildingAccommodationsByBuildingId((prev) => ({
                ...prev,
                [String(buildingId)]: parseListResponse(refreshed),
            }));
            setAccommodationDrafts((prev) => ({
                ...prev,
                [slotNumber]: {
                    ...createEmptyAccommodationDraft(floor),
                    floor,
                },
            }));
            enqueueSnackbar(`Accommodation saved for Building ${slotNumber}.`, { variant: "success" });
        } catch (error) {
            console.error("Failed to save accommodation:", error);
            enqueueSnackbar(`Failed to save accommodation for Building ${slotNumber}.`, { variant: "error" });
        } finally {
            setSavingAccommodationSlot(null);
        }
    };

    const deleteAccommodationItem = async (slotNumber, buildingId, accommodation) => {
        if (!accommodation?.id) return;
        const confirmation = await Swal.fire({
            title: "Delete Accommodation?",
            text: "This accommodation row will be removed for this building.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc3545",
        });
        if (!confirmation.isConfirmed) return;

        setDeletingAccommodationId(accommodation.id);
        try {
            await deleteAssetBuildingAccommodation(accommodation.id);
            setBuildingAccommodationsByBuildingId((prev) => {
                const list = prev[String(buildingId)] || [];
                return {
                    ...prev,
                    [String(buildingId)]: list.filter((item) => String(item.id) !== String(accommodation.id)),
                };
            });
            enqueueSnackbar(`Accommodation removed from Building ${slotNumber}.`, { variant: "success" });
        } catch (error) {
            console.error("Failed to delete accommodation:", error);
            enqueueSnackbar("Failed to delete accommodation row.", { variant: "error" });
        } finally {
            setDeletingAccommodationId(null);
        }
    };

    const dismissBuildingSlot = useCallback((slotNumber) => {
        const normalizedSlot = Number(slotNumber);
        if (!normalizedSlot) return;
        setDismissedBuildingSlots((prev) => {
            const next = new Set(prev);
            next.add(normalizedSlot);
            return next;
        });
        setBuildingDrafts((prev) => {
            const next = { ...prev };
            delete next[normalizedSlot];
            return next;
        });
        setConstructionDrafts((prev) => {
            const next = { ...prev };
            delete next[normalizedSlot];
            return next;
        });
        setBuildingStepTabsBySlot((prev) => {
            const next = { ...prev };
            delete next[String(normalizedSlot)];
            return next;
        });
        if (selectedBuildingDetailSlot === normalizedSlot) {
            setSelectedBuildingDetailSlot(null);
        }
    }, [selectedBuildingDetailSlot]);

    const handleDeletePendingBuildingSlot = async (slotNumber) => {
        const confirmation = await Swal.fire({
            title: "Remove Pending Building?",
            text: "This building slot will be removed from the review list.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Remove",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc3545",
        });
        if (!confirmation.isConfirmed) return;

        dismissBuildingSlot(slotNumber);
    };

    const finishAccommodationForSlot = async (slotNumber) => {
        const buildingId =
            buildingDrafts[slotNumber]?.id ||
            editBuildingsBySlot[slotNumber]?.id ||
            "";
        const savedItems = buildingId
            ? buildingAccommodationsByBuildingId[String(buildingId)] || []
            : [];

        const confirmation = await Swal.fire({
            title: `Finish Building ${slotNumber}?`,
            text: savedItems.length
                ? "Return to Step 2.1.2a and continue with the next building?"
                : "No accommodation saved yet. Return to Step 2.1.2a anyway?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Finish",
            cancelButtonText: "Cancel",
            confirmButtonColor: BRAND_BLUE,
        });
        if (!confirmation.isConfirmed) return;

        const slots = stepBuildingRows.map((row) => row.serial);
        const currentIndex = slots.indexOf(slotNumber);

        setBuildingStepTabForSlot(slotNumber, "form");

        if (currentIndex !== -1 && currentIndex + 1 < slots.length) {
            const nextSlot = slots[currentIndex + 1];
            setBuildingStepTabForSlot(nextSlot, "form");
            setSelectedBuildingDetailSlot(nextSlot);
            enqueueSnackbar(`Building ${slotNumber} finished. Moving to Building ${nextSlot}.`, { variant: "success" });
        } else {
            setSelectedBuildingDetailSlot(null);
            enqueueSnackbar(`Building ${slotNumber} finished. Back to the buildings list.`, { variant: "success" });
        }
    };

    const ensureBuildingPhotosLoaded = useCallback(async (buildingId) => {
        if (!buildingId || buildingPhotosByBuildingId[buildingId]) return;
        try {
            setLoadingBuildingPhotosForBuildingId(buildingId);
            const response = await getAssetPhotosByBuilding(buildingId, 0, 200);
            let photos = parseListResponse(response).map((photo) => ({
                ...photo,
                photo_url: resolveServerFileUrl(
                    photo?.photo_url || photo?.photoUrl || photo?.url || photo?.path || ""
                ),
            }));

            if (!photos.length) {
                const building = editBuildings.find((item) => String(item?.id || "") === String(buildingId));
                const fallbackUrls = Array.isArray(building?.photo_urls)
                    ? building.photo_urls
                    : Array.isArray(building?.photoUrls)
                        ? building.photoUrls
                        : [];
                photos = fallbackUrls
                    .filter(Boolean)
                    .map((url, index) => ({
                        id: `${buildingId}-fallback-${index}`,
                        photo_type: "Photo",
                        photo_url: resolveServerFileUrl(url),
                    }));
            }
            setBuildingPhotosByBuildingId((prev) => ({
                ...prev,
                [buildingId]: photos,
            }));
        } catch (error) {
            console.error("Failed to load building photos:", error);
            setBuildingPhotosByBuildingId((prev) => ({
                ...prev,
                [buildingId]: [],
            }));
        } finally {
            setLoadingBuildingPhotosForBuildingId(null);
        }
    }, [buildingPhotosByBuildingId, editBuildings]);

    const upsertBuildingPhoto = async (buildingId, file = null, photo = null, slotNumber = null, photoTypeOverride = null) => {
        const assetId = selectedEditAssetDraft?.id;
        if (!assetId || !buildingId) return;

        const existingPhotos = buildingPhotosByBuildingId[String(buildingId)] || [];
        const selectedType =
            photo?.photo_type ||
            photo?.type ||
            photoTypeOverride ||
            (photo ? null : await promptForPhotoType(existingPhotos, "Select Building Photo Type"));

        if (!selectedType) return;

        const selectedFile = file || (await pickImageFile());
        if (!selectedFile) return;

        const isReplace = !!photo?.id;

        const photoKey = photo?.id || `new-building-${buildingId}-${selectedType}-${Date.now()}`;
        setUpdatingPhotoId(photoKey);
        try {
            const uploadRes = await uploadAssetFile(selectedFile);
            const uploadedUrl =
                uploadRes?.data?.url ||
                uploadRes?.data?.data?.url ||
                uploadRes?.data?.data?.file_url ||
                uploadRes?.data?.file_url ||
                uploadRes?.data?.path;

            if (!uploadedUrl) {
                throw new Error("Upload did not return a URL.");
            }

            const payload = {
                asset_id: String(assetId),
                building_id: String(buildingId),
                photo_type: selectedType,
                photo_url: uploadedUrl,
            };

            let savedPhoto;
            if (photo?.id) {
                const response = await updateAssetPhoto(photo.id, payload);
                savedPhoto = response?.data?.data || response?.data || {};
            } else {
                const response = await createAssetPhoto(payload);
                savedPhoto = response?.data?.data || response?.data || {};
            }

            setBuildingPhotosByBuildingId((prev) => {
                const list = prev[buildingId] || [];
                if (photo?.id) {
                    return {
                        ...prev,
                        [buildingId]: list.map((p) =>
                            p.id === photo.id
                                ? { ...p, ...savedPhoto, photo_url: uploadedUrl, url: uploadedUrl, path: uploadedUrl }
                                : p
                        ),
                    };
                }
                return {
                    ...prev,
                    [buildingId]: [savedPhoto, ...list],
                };
            });

            enqueueSnackbar(
                isReplace
                    ? `Building ${slotNumber || ""} image updated successfully.`
                    : `Building ${slotNumber || ""} image uploaded successfully.`,
                { variant: "success" }
            );
        } catch (error) {
            console.error("Failed to upsert building photo:", error);
            enqueueSnackbar("Failed to save building image.", { variant: "error" });
        } finally {
            setUpdatingPhotoId(null);
        }
    };

    const resetSiteWorkDraft = useCallback(() => {
        const assetId = selectedEditAssetDraft?.id || "";
        const valuationId = getSelectedEditValuationId() || "";
        setSiteWorkDraft(createEmptySiteWorkDraft(String(assetId), String(valuationId)));
    }, [getSelectedEditValuationId, selectedEditAssetDraft?.id]);

    const handleEditSiteWork = useCallback((siteWork) => {
        const assetId = selectedEditAssetDraft?.id || "";
        const valuationId = getSelectedEditValuationId() || "";
        setSiteWorkDraft(createSiteWorkDraftFromEntity(siteWork, String(assetId), String(valuationId)));
    }, [createSiteWorkDraftFromEntity, getSelectedEditValuationId, selectedEditAssetDraft?.id]);

    const handleSaveSiteWork = useCallback(async () => {
        const assetId = selectedEditAssetDraft?.id;
        const valuationId = getSelectedEditValuationId();
        if (!assetId || !valuationId) {
            enqueueSnackbar("Asset or valuation reference is missing.", { variant: "error" });
            return;
        }
        if (!siteWorkDraft.site_work_type) {
            enqueueSnackbar("Site work type is required.", { variant: "warning" });
            return;
        }
        if (siteWorkDraft.site_work_type === "OTHERS" && !String(siteWorkDraft.other_specify || "").trim()) {
            enqueueSnackbar("Specify the site work type.", { variant: "warning" });
            return;
        }
        if (!siteWorkDraft.observed_condition) {
            enqueueSnackbar("Condition is required.", { variant: "warning" });
            return;
        }

        const payload = Object.fromEntries(
            Object.entries({
                asset_id: String(assetId),
                valuation_id: String(valuationId),
                site_work_type: siteWorkDraft.site_work_type,
                other_specify: siteWorkDraft.other_specify || null,
                description: siteWorkDraft.description || null,
                dimensions: siteWorkDraft.dimensions || null,
                serial_number: siteWorkDraft.serial_number || null,
                observed_condition: siteWorkDraft.observed_condition || null,
                remark: siteWorkDraft.remark || null,
                measurement: siteWorkDraft.measurement ? toNumberOrNull(siteWorkDraft.measurement) : null,
                construction_rate: siteWorkDraft.construction_rate ? toNumberOrNull(siteWorkDraft.construction_rate) : null,
            }).filter(([, value]) => value !== null && value !== undefined && value !== "")
        );

        setSavingSiteWork(true);
        try {
            if (siteWorkDraft.id) {
                await updateSiteWork(siteWorkDraft.id, payload);
                enqueueSnackbar("Site work updated successfully.", { variant: "success" });
            } else {
                await createSiteWork(payload);
                enqueueSnackbar("Site work saved successfully.", { variant: "success" });
            }
            await loadSiteWorksForEdit();
            resetSiteWorkDraft();
        } catch (error) {
            console.error("Failed to save site work:", error);
            enqueueSnackbar("Failed to save site work.", { variant: "error" });
        } finally {
            setSavingSiteWork(false);
        }
    }, [getSelectedEditValuationId, loadSiteWorksForEdit, resetSiteWorkDraft, selectedEditAssetDraft?.id, siteWorkDraft]);

    const handleDeleteSiteWork = useCallback(async (siteWork) => {
        if (!siteWork?.id) return;
        const confirmation = await Swal.fire({
            title: "Remove Site Work?",
            text: "This site work will be removed from the list. It can be restored from the database if needed.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Remove",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc3545",
        });
        if (!confirmation.isConfirmed) return;

        setDeletingSiteWorkId(siteWork.id);
        try {
            await deleteSiteWork(siteWork.id);
            setEditSiteWorks((prev) => prev.filter((item) => String(item?.id || "") !== String(siteWork.id)));
            setSiteWorkPhotosBySiteWorkId((prev) => {
                const next = { ...prev };
                delete next[String(siteWork.id)];
                return next;
            });
            if (String(siteWorkDraft.id || "") === String(siteWork.id)) {
                resetSiteWorkDraft();
            }
            enqueueSnackbar("Site work removed successfully.", { variant: "success" });
        } catch (error) {
            console.error("Failed to delete site work:", error);
            enqueueSnackbar("Failed to remove site work.", { variant: "error" });
        } finally {
            setDeletingSiteWorkId(null);
        }
    }, [resetSiteWorkDraft, siteWorkDraft.id]);

    const handleDeleteBuilding = useCallback(async (building, slotNumber) => {
        if (!building?.id) return;
        const confirmation = await Swal.fire({
            title: "Remove Building?",
            text: "This building will be removed from the list. It can be restored from the database if needed.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Remove",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc3545",
        });
        if (!confirmation.isConfirmed) return;

        const buildingId = String(building.id);
        setDeletingBuildingId(building.id);
        try {
            await deleteAssetBuilding(building.id);
            dismissBuildingSlot(slotNumber);
            setEditBuildings((prev) => prev.filter((item) => String(item?.id || "") !== buildingId));
            setBuildingPhotosByBuildingId((prev) => {
                const next = { ...prev };
                delete next[buildingId];
                return next;
            });
            setBuildingAccommodationsByBuildingId((prev) => {
                const next = { ...prev };
                delete next[buildingId];
                return next;
            });
            await loadBuildingsForEdit();
            enqueueSnackbar("Building removed successfully.", { variant: "success" });
        } catch (error) {
            console.error("Failed to delete building:", error);
            enqueueSnackbar("Failed to remove building.", { variant: "error" });
        } finally {
            setDeletingBuildingId(null);
        }
    }, [dismissBuildingSlot, loadBuildingsForEdit]);

    const handleDeleteDataReviewAsset = useCallback(async (valuationRow) => {
        const assetId = valuationRow?.id || valuationRow?.raw?.id;
        if (!assetId) return;

        const assetName = valuationRow?.assetName || valuationRow?.raw?.name || "this asset";
        const confirmation = await Swal.fire({
            title: "Remove Asset?",
            text: `"${assetName}" will be removed from the list. Related buildings and site works will be marked deleted and can be restored from the database if needed.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Remove",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc3545",
        });
        if (!confirmation.isConfirmed) return;

        setDeletingAssetId(assetId);
        try {
            await deleteAsset(assetId);
            setValuations((prev) => prev.filter((item) => String(item?.id || item?.raw?.id) !== String(assetId)));
            enqueueSnackbar("Asset removed successfully.", { variant: "success" });
            await fetchValuations(paginationInfo.pageNumber ?? 0, paginationInfo.pageSize ?? 10);
        } catch (error) {
            console.error("Failed to remove asset:", error);
            enqueueSnackbar("Failed to remove asset.", { variant: "error" });
        } finally {
            setDeletingAssetId(null);
        }
    }, [fetchValuations, paginationInfo.pageNumber, paginationInfo.pageSize]);

    const upsertSiteWorkPhoto = async (siteWorkId, file = null, photo = null, photoTypeOverride = null) => {
        if (!siteWorkId) return;
        const existingPhotos = siteWorkPhotosBySiteWorkId[String(siteWorkId)] || [];
        const selectedType =
            photo?.photo_type ||
            photo?.type ||
            photoTypeOverride ||
            (photo ? null : await promptForPhotoType(existingPhotos, "Select Site Work Photo Type"));

        if (!selectedType) return;

        const selectedFile = file || (await pickImageFile());
        if (!selectedFile) return;

        const photoKey = photo?.id || `new-site-work-${siteWorkId}-${selectedType}-${Date.now()}`;
        setUpdatingSiteWorkPhotoId(photoKey);
        try {
            const uploadRes = await uploadAssetFile(selectedFile);
            const uploadedUrl =
                uploadRes?.data?.url ||
                uploadRes?.data?.data?.url ||
                uploadRes?.data?.data?.file_url ||
                uploadRes?.data?.file_url ||
                uploadRes?.data?.path;

            if (!uploadedUrl) {
                throw new Error("Upload did not return a URL.");
            }

            const payload = {
                site_work_id: String(siteWorkId),
                photo_type: selectedType,
                photo_url: uploadedUrl,
            };

            let savedPhoto;
            if (photo?.id) {
                const response = await updateSiteWorkPhoto(photo.id, payload);
                savedPhoto = response?.data?.data || response?.data || {};
            } else {
                const response = await createSiteWorkPhoto(payload);
                savedPhoto = response?.data?.data || response?.data || {};
            }

            setSiteWorkPhotosBySiteWorkId((prev) => {
                const list = prev[String(siteWorkId)] || [];
                const normalizedSaved = {
                    ...savedPhoto,
                    photo_url: resolveServerFileUrl(
                        savedPhoto?.photo_url || savedPhoto?.photoUrl || uploadedUrl
                    ),
                };
                if (photo?.id) {
                    return {
                        ...prev,
                        [String(siteWorkId)]: list.map((item) =>
                            item.id === photo.id ? normalizedSaved : item
                        ),
                    };
                }
                return {
                    ...prev,
                    [String(siteWorkId)]: [normalizedSaved, ...list],
                };
            });
            enqueueSnackbar(photo?.id ? "Site work image updated successfully." : "Site work image uploaded successfully.", {
                variant: "success",
            });
        } catch (error) {
            console.error("Failed to save site work photo:", error);
            enqueueSnackbar("Failed to save site work image.", { variant: "error" });
        } finally {
            setUpdatingSiteWorkPhotoId(null);
        }
    };

    const handleDeleteSiteWorkPhoto = async (siteWorkId, photo) => {
        if (!siteWorkId || !photo?.id) return;
        const confirmation = await Swal.fire({
            title: "Delete Site Work Image?",
            text: "This image will be removed from the site work.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc3545",
        });
        if (!confirmation.isConfirmed) return;

        setUpdatingSiteWorkPhotoId(photo.id);
        try {
            await deleteSiteWorkPhoto(photo.id);
            setSiteWorkPhotosBySiteWorkId((prev) => ({
                ...prev,
                [String(siteWorkId)]: (prev[String(siteWorkId)] || []).filter((item) => item.id !== photo.id),
            }));
            enqueueSnackbar("Site work image deleted successfully.", { variant: "success" });
        } catch (error) {
            console.error("Failed to delete site work photo:", error);
            enqueueSnackbar("Failed to delete site work image.", { variant: "error" });
        } finally {
            setUpdatingSiteWorkPhotoId(null);
        }
    };

    const handleReplaceAssetPhoto = async (photo, file, assetIdOverride = null) => {
        const assetId = assetIdOverride || selectedEditAssetDraft?.id;
        if (!assetId || !file) return;

        const confirmation = await Swal.fire({
            title: "Replace Photo?",
            text: "Upload and replace this photo with the selected image?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Replace",
            cancelButtonText: "Cancel",
            confirmButtonColor: BRAND_BLUE,
        });
        if (!confirmation.isConfirmed) return;

        const photoKey = photo?.id || `${photo?.photo_type || photo?.type}-${Date.now()}`;
        setUpdatingPhotoId(photoKey);
        try {
            const uploadRes = await uploadAssetFile(file);
            const uploadedUrl =
                uploadRes?.data?.url ||
                uploadRes?.data?.data?.url ||
                uploadRes?.data?.data?.file_url ||
                uploadRes?.data?.file_url ||
                uploadRes?.data?.path;

            if (!uploadedUrl) {
                throw new Error("Upload did not return a file URL.");
            }

            const payload = Object.fromEntries(
                Object.entries({
                    asset_id: String(assetId),
                    building_id: photo?.building_id || photo?.buildingId || null,
                    photo_type: photo?.photo_type || photo?.type || "GENERAL",
                    photo_url: uploadedUrl,
                }).filter(([, value]) => value !== null && value !== undefined && value !== "")
            );

            if (photo?.id) {
                await updateAssetPhoto(photo.id, payload);
            } else {
                await createAssetPhoto(payload);
            }

            setAssetPhotosByAssetId((prev) => {
                const list = prev[assetId] || [];
                const updated = list.map((p) => {
                    if (photo?.id && p.id === photo.id) {
                        return {
                            ...p,
                            photo_url: uploadedUrl,
                            url: uploadedUrl,
                            path: uploadedUrl,
                        };
                    }
                    return p;
                });
                return {
                    ...prev,
                    [assetId]: updated,
                };
            });

            enqueueSnackbar("Photo replaced successfully.", { variant: "success" });
        } catch (error) {
            console.error("Failed to replace photo:", error);
            enqueueSnackbar("Failed to replace photo.", { variant: "error" });
        } finally {
            setUpdatingPhotoId(null);
        }
    };

    const getAvailablePhotoTypes = useCallback((photos = []) => {
        const usedTypes = new Set(
            (photos || [])
                .map((photo) => String(photo?.photo_type || photo?.type || "").trim())
                .filter(Boolean)
        );

        return ASSET_PHOTO_TYPE_OPTIONS.filter((type) => !usedTypes.has(type));
    }, []);

    const promptForPhotoType = useCallback(async (photos = [], title = "Select Photo Type") => {
        const availableTypes = getAvailablePhotoTypes(photos);
        if (!availableTypes.length) {
            enqueueSnackbar("All photo types have already been used for this item.", { variant: "warning" });
            return null;
        }

        const result = await Swal.fire({
            title,
            input: "select",
            inputOptions: availableTypes.reduce((acc, type) => {
                acc[type] = toHumanReadableAssetName(type);
                return acc;
            }, {}),
            inputPlaceholder: "Select photo type",
            showCancelButton: true,
            confirmButtonText: "Continue",
            cancelButtonText: "Cancel",
            confirmButtonColor: BRAND_BLUE,
            inputValidator: (value) => {
                if (!value) {
                    return "Photo type is required.";
                }
                return null;
            },
        });

        if (!result.isConfirmed || !result.value) return null;
        return result.value;
    }, [getAvailablePhotoTypes]);

    const pickImageFile = useCallback(
        () =>
            new Promise((resolve) => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = "image/*";
                input.onchange = () => resolve(input.files?.[0] || null);
                input.click();
            }),
        []
    );

    const handleCreateAssetPhoto = useCallback(async (assetIdOverride = null, existingPhotos = null) => {
        const assetId = assetIdOverride || selectedEditAssetDraft?.id;
        if (!assetId) {
            enqueueSnackbar("Asset ID is missing. Cannot upload photo.", { variant: "error" });
            return;
        }

        const currentPhotos = Array.isArray(existingPhotos)
            ? existingPhotos
            : assetPhotosByAssetId[assetId] || [];
        const selectedType = await promptForPhotoType(currentPhotos, "Select Asset Photo Type");
        if (!selectedType) return;

        const file = await pickImageFile();
        if (!file) return;

        setUpdatingPhotoId(`new-${assetId}-${selectedType}`);
        try {
            const uploadRes = await uploadAssetFile(file);
            const uploadedUrl =
                uploadRes?.data?.url ||
                uploadRes?.data?.data?.url ||
                uploadRes?.data?.data?.file_url ||
                uploadRes?.data?.file_url ||
                uploadRes?.data?.path;

            if (!uploadedUrl) {
                throw new Error("Upload did not return a file URL.");
            }

            const response = await createAssetPhoto({
                asset_id: String(assetId),
                photo_type: selectedType,
                photo_url: uploadedUrl,
            });

            const savedPhoto = response?.data?.data || response?.data || {};
            setAssetPhotosByAssetId((prev) => ({
                ...prev,
                [assetId]: [savedPhoto, ...(prev[assetId] || [])],
            }));
            enqueueSnackbar("Photo uploaded successfully.", { variant: "success" });
        } catch (error) {
            console.error("Failed to upload asset photo:", error);
            enqueueSnackbar("Failed to upload photo.", { variant: "error" });
        } finally {
            setUpdatingPhotoId(null);
        }
    }, [assetPhotosByAssetId, pickImageFile, promptForPhotoType, selectedEditAssetDraft?.id]);

    const handleUpdateAsset = async () => {
        const assetId = detailAsset?.id;
        if (!assetId) {
            enqueueSnackbar("Asset ID is missing. Cannot update.", { variant: "error" });
            return;
        }

        setUpdatingAsset(true);
        try {
            const payload = buildAssetUpdatePayload(detailAsset);
            const response = await updateAssetMultipart(assetId, payload);
            const updatedAsset = response?.data?.data || {};
            const mergedAsset = {
                ...(detailAsset || {}),
                ...updatedAsset,
            };

            setDetailAsset(mergedAsset);
            setReviewData((prev) => {
                if (!prev?.assets) return prev;
                return {
                    ...prev,
                    assets: prev.assets.map((a) => (a.id === assetId ? { ...a, ...mergedAsset } : a)),
                };
            });

            enqueueSnackbar("Asset updated successfully.", { variant: "success" });
        } catch (error) {
            console.error("Failed to update asset:", error);
            enqueueSnackbar("Failed to update asset.", { variant: "error" });
        } finally {
            setUpdatingAsset(false);
        }
    };

    const saveEditAssetCore = async () => {
        const assetId = selectedEditAssetDraft?.id;
        if (!assetId) {
            enqueueSnackbar("Asset ID is missing. Cannot update.", { variant: "error" });
            return null;
        }

        setSavingEditAsset(true);
        try {
            const payload = buildAssetUpdatePayload(selectedEditAssetDraft);
            const response = await updateAssetMultipart(assetId, payload);
            const updatedAsset = response?.data?.data || {};
            const categoryCode = getCategoryCode(selectedEditAssetDraft || updatedAsset || {});
            const shouldAutoReview = categoryCode !== "BLDG";

            if (shouldAutoReview) {
                const reviewResponse = await reviewAsset(assetId);
                Object.assign(updatedAsset, reviewResponse?.data?.data || {});
            }

            const mergedAsset = {
                ...(selectedEditAssetDraft || {}),
                ...updatedAsset,
                ...(shouldAutoReview
                    ? {
                          is_reviewed: true,
                          isReviewed: true,
                      }
                    : {}),
            };

            setSelectedEditAssetDraft(mergedAsset);
            setEditAssets((prev) => prev.map((a) => (a.id === assetId ? { ...a, ...mergedAsset } : a)));
            setValuations((prev) =>
                prev.map((row) => {
                    if (tableMode === "assets") {
                        if ((row?.raw?.id || row?.id) !== assetId) return row;
                        return normalizeAsset({ ...(row.raw || {}), ...mergedAsset });
                    }
                    return row;
                })
            );

            return {
                assetId,
                shouldAutoReview,
            };
        } catch (error) {
            console.error("Failed to update asset:", error);
            enqueueSnackbar("Failed to update asset.", { variant: "error" });
            return null;
        } finally {
            setSavingEditAsset(false);
        }
    };

    const handleMarkEditAssetReviewNotCompleted = async () => {
        const assetId = selectedEditAssetDraft?.id;
        if (!assetId) {
            enqueueSnackbar("Asset ID is missing. Cannot mark review as not completed.", { variant: "error" });
            return;
        }

        const result = await Swal.fire({
            title: "Review Not Completed",
            input: "textarea",
            inputLabel: "Add the missing information remark",
            inputValue: getReviewNotCompletedRemark(selectedEditAssetDraft) !== "-" ? getReviewNotCompletedRemark(selectedEditAssetDraft) : "",
            inputPlaceholder: "Describe what is missing and what should be reviewed later...",
            inputAttributes: {
                "aria-label": "Review not completed remark",
            },
            showCancelButton: true,
            confirmButtonText: "Save Remark",
            cancelButtonText: "Cancel",
            confirmButtonColor: BRAND_BLUE,
            preConfirm: (value) => {
                const normalized = String(value || "").trim();
                if (!normalized) {
                    Swal.showValidationMessage("Remark is required.");
                    return false;
                }
                return normalized;
            },
        });

        if (!result.isConfirmed || !result.value) return;

        const saveResult = await saveEditAssetCore();
        if (!saveResult) return;

        try {
            const response = await markAssetReviewNotCompleted(assetId, { remark: result.value });
            const updatedAsset = response?.data?.data || response?.data || {};
            mergeUpdatedAssetLocally(updatedAsset);
            await refreshAssetReviewViews();
            enqueueSnackbar("Asset marked as review not completed.", { variant: "warning" });
            closeEditModal();
        } catch (error) {
            console.error("Failed to save review not completed remark:", error);
            enqueueSnackbar("Failed to save review not completed remark.", { variant: "error" });
        }
    };

    const handleSaveEditAsset = async () => {
        const confirmation = await Swal.fire({
            title: "Save Changes?",
            text: "Are you sure you want to save the updated asset details?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, save",
            cancelButtonText: "Cancel",
            confirmButtonColor: BRAND_BLUE,
        });

        if (!confirmation.isConfirmed) {
            return;
        }

        const result = await saveEditAssetCore();
        if (!result) return;

        await refreshAssetReviewViews();
        enqueueSnackbar(
            result.shouldAutoReview
                ? "Asset updated and marked as reviewed."
                : "Asset updated successfully.",
            { variant: "success" }
        );
        closeEditModal();
    };

    const handleSaveBuildingAssetAndNext = async () => {
        const confirmation = await Swal.fire({
            title: "Save Step 2.1?",
            text: "Save building asset information and continue to Security Overview?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Save & Next",
            cancelButtonText: "Cancel",
            confirmButtonColor: BRAND_BLUE,
        });

        if (!confirmation.isConfirmed) return;

        const result = await saveEditAssetCore();
        if (!result) return;

        enqueueSnackbar("Step 2.1 saved successfully.", { variant: "success" });
        setEditBuildingStepIndex(1);
        setHighestSavedStepIndex((prev) => Math.max(prev, 1));
    };

    const handleSaveSecurityOverviewAndNext = async () => {
        const assetId = selectedEditAssetDraft?.id;
        const assetValuationId = getSelectedEditValuationId();
        if (!assetId || !assetValuationId) {
            enqueueSnackbar("Asset or valuation reference is missing.", { variant: "error" });
            return;
        }

        const confirmation = await Swal.fire({
            title: "Save Security Overview?",
            text: "Save step 2.1.1 and continue to Buildings?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Save & Next",
            cancelButtonText: "Cancel",
            confirmButtonColor: BRAND_BLUE,
        });

        if (!confirmation.isConfirmed) return;

        const payload = Object.fromEntries(
            Object.entries({
                asset_id: String(assetId),
                asset_valuation_id: String(assetValuationId),
                accessibility: securityOverviewDraft.accessibility || null,
                street: securityOverviewDraft.street || null,
                landmark: securityOverviewDraft.landmark || null,
                distance_from_landmark: securityOverviewDraft.distance_from_landmark || null,
                neighborhood_type: securityOverviewDraft.neighborhood_type || null,
                neighborhood_density: securityOverviewDraft.neighborhood_density || null,
                amenities: securityOverviewDraft.amenities || null,
                income_level: securityOverviewDraft.income_level || null,
                road_type: securityOverviewDraft.road_type || null,
                distance_from_main_road: securityOverviewDraft.distance_from_main_road || null,
                number_of_buildings:
                    toIntOrNull(
                        selectedEditAssetDraft?.number_of_buildings ||
                            selectedEditAssetDraft?.numberOfBuildings ||
                            securityOverviewDraft.number_of_buildings
                    ) || null,
            }).filter(([, value]) => value !== null && value !== undefined && value !== "")
        );

        setSavingSecurityOverview(true);
        try {
            const overviewId = securityOverviewDraft?.id;
            const response =
                overviewId && !String(overviewId).startsWith("temp_")
                    ? await updateAssetSecurityOverview(overviewId, payload)
                    : await createAssetSecurityOverview(payload);

            const saved =
                response?.data?.data || response?.data || createEmptySecurityOverview();
            setSecurityOverviewDraft((prev) => ({ ...prev, ...saved }));
            enqueueSnackbar("Security overview saved successfully.", { variant: "success" });
            await loadBuildingsForEdit();
            setEditBuildingStepIndex(2);
            setHighestSavedStepIndex((prev) => Math.max(prev, 2));
        } catch (error) {
            console.error("Failed to save security overview:", error);
            enqueueSnackbar("Failed to save security overview.", { variant: "error" });
        } finally {
            setSavingSecurityOverview(false);
        }
    };

    const saveBuildingFormSilent = async (slotNumber) => {
        const assetId = selectedEditAssetDraft?.id;
        const valuationId = getSelectedEditValuationId();
        const draft = buildingDrafts[slotNumber];
        if (!assetId || !valuationId || !draft) return null;

        const isOtherBuildingName = isOtherSelection(draft.building_name);
        const otherBuildingName = isOtherBuildingName
            ? formatOtherBuildingName(draft.other_building_name || draft.otherBuildingName || draft.other_name || draft.otherName)
            : null;

        const formPayload = {
            asset_id: String(assetId),
            asset_valuation_id: String(valuationId),
            is_primary: !!draft.is_primary,
            building_type_id: draft.building_type_id || null,
            building_name: draft.building_name || null,
            other_building_name: otherBuildingName,
            serial_number: draft.building_number || null,
            no_of_floors: toIntOrNull(draft.no_of_floors),
            year_built: toIntOrNull(draft.year_built),
            building_measurement: toNumberOrNull(draft.building_measurement),
            use_status: draft.use_status || null,
            completion_rate: toIntOrNull(draft.completion_rate),
            observed_condition: draft.observed_condition || null,
            useful_life_span_years: toIntOrNull(draft.useful_life_span_years),
            construction_rate_per_sqm: toNumberOrNull(draft.construction_rate_per_sqm),
        };

        if (draft.id) {
            formPayload.id = draft.id;
        }

        const cleanedPayload = Object.fromEntries(
            Object.entries(formPayload).filter(
                ([key, value]) =>
                    key === "other_building_name" ||
                    (value !== null && value !== undefined && value !== "")
            )
        );

        const multipart = new FormData();
        multipart.append("data", JSON.stringify(cleanedPayload));
        if (draft.sketch_image_file instanceof File) {
            multipart.append("sketch_image", draft.sketch_image_file);
        }

        let savedBuildingId = draft.id || "";
        if (draft.id) {
            const response = await updateAssetBuildingMultipart(draft.id, multipart);
            savedBuildingId = response?.data?.data?.id || response?.data?.id || savedBuildingId;
        } else {
            const response = await createAssetBuildingMultipart(multipart);
            savedBuildingId = response?.data?.data?.id || response?.data?.id || savedBuildingId;
        }
        return savedBuildingId;
    };

    const handleFinishBuilding = async (slot) => {
        const slotNumber = slot || selectedBuildingDetailSlot;
        if (slotNumber) {
            try {
                setSavingBuildingSlot(slotNumber);
                await saveBuildingFormSilent(slotNumber);
                enqueueSnackbar(`Building ${slotNumber} saved successfully.`, { variant: "success" });
                await loadBuildingsForEdit();
            } catch (error) {
                console.error("Failed to auto-save building:", error);
                enqueueSnackbar(`Failed to save building ${slotNumber}.`, { variant: "error" });
            } finally {
                setSavingBuildingSlot(null);
            }
        }
        if (slotNumber && slotNumber < expectedBuildingCount) {
            setSelectedBuildingDetailSlot(slotNumber + 1);
        } else {
            setSelectedBuildingDetailSlot(null);
        }
    };

    const handleMarkBuildingAssetReviewed = async () => {
        const assetId = selectedEditAssetDraft?.id;
        if (!assetId) {
            enqueueSnackbar("Asset ID is missing. Cannot review.", { variant: "error" });
            return;
        }

        const slotNumber = selectedBuildingDetailSlot;
        if (slotNumber) {
            try {
                await saveBuildingFormSilent(slotNumber);
                await loadBuildingsForEdit();
            } catch (error) {
                console.error("Failed to auto-save building on finish:", error);
                enqueueSnackbar(`Failed to save building ${slotNumber} before finishing.`, { variant: "error" });
                return;
            }
        }

        const confirmation = await Swal.fire({
            title: "Mark as Reviewed?",
            text: "This will mark the building asset as reviewed and close the modal.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Mark Reviewed",
            cancelButtonText: "Cancel",
            confirmButtonColor: BRAND_BLUE,
        });

        if (!confirmation.isConfirmed) return;

        try {
            const response = await reviewAsset(assetId);
            const updatedAsset = response?.data?.data || response?.data || {};
            mergeUpdatedAssetLocally(updatedAsset);
            await refreshAssetReviewViews();
            enqueueSnackbar("Asset marked as reviewed.", { variant: "success" });
            closeEditModal();
        } catch (error) {
            console.error("Failed to mark asset as reviewed:", error);
            enqueueSnackbar("Failed to mark asset as reviewed.", { variant: "error" });
        }
    };

    const mergeUpdatedAssetLocally = useCallback((updatedAsset) => {
        const assetId = updatedAsset?.id;
        if (!assetId) return;

        setReviewData((prev) => {
            if (!prev?.assets) return prev;
            return {
                ...prev,
                assets: prev.assets.map((a) =>
                    (a.id === assetId ? { ...a, ...updatedAsset } : a)
                ),
            };
        });

        setDetailAsset((prev) => {
            if (!prev || prev.id !== assetId) return prev;
            return { ...prev, ...updatedAsset };
        });

        setSelectedEditAssetDraft((prev) => {
            if (!prev || prev.id !== assetId) return prev;
            return { ...prev, ...updatedAsset };
        });

        setEditAssets((prev) => prev.map((a) => (a.id === assetId ? { ...a, ...updatedAsset } : a)));

        setValuations((prev) =>
            prev.map((row) => {
                if (tableMode !== "assets") return row;
                if ((row?.raw?.id || row?.id) !== assetId) return row;
                return normalizeAsset({ ...(row.raw || {}), ...updatedAsset });
            })
        );
    }, [tableMode]);

    const reviewAssetById = useCallback(
        async (assetId, successMessage = "Asset reviewed successfully.") => {
            const response = await reviewAsset(assetId);
            const updatedAsset = response?.data?.data || response?.data || {};
            mergeUpdatedAssetLocally(updatedAsset);
            await refreshAssetReviewViews();
            enqueueSnackbar(successMessage, { variant: "success" });
        },
        [mergeUpdatedAssetLocally, refreshAssetReviewViews]
    );

    const unreviewAssetById = useCallback(
        async (assetId) => {
            const response = await unreviewAsset(assetId);
            const updatedAsset = response?.data?.data || response?.data || {};
            mergeUpdatedAssetLocally(updatedAsset);
            await refreshAssetReviewViews();
            enqueueSnackbar("Asset unreviewed successfully.", { variant: "success" });
        },
        [mergeUpdatedAssetLocally, refreshAssetReviewViews]
    );

    const clearReviewRemarkIssueByAssetId = useCallback(
        async (assetId) => {
            const response = await clearAssetReviewNotCompletedRemark(assetId);
            const updatedAsset = response?.data?.data || response?.data || {};
            mergeUpdatedAssetLocally(updatedAsset);
            await refreshAssetReviewViews();
            enqueueSnackbar("Remark issue removed. Asset moved to Review Incomplete.", { variant: "success" });
        },
        [mergeUpdatedAssetLocally, refreshAssetReviewViews]
    );

    const handleReviewDataAction = (asset) => {
        if (!asset) return;
        const assetId = asset?.id;
        if (!assetId) return;

        const currentlyReviewed = isAssetReviewed(asset);
        if (!currentlyReviewed) {
            handleViewAssetDetail(asset);
            return;
        }

        confirmDialog({
            message: "Are you sure you want to unreview this asset?",
            header: "Confirm Unreview",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                setReviewToggleAssetId(assetId);
                try {
                    await unreviewAssetById(assetId);
                } catch (error) {
                    console.error("Failed to unreview asset:", error);
                    enqueueSnackbar("Failed to unreview asset.", { variant: "error" });
                } finally {
                    setReviewToggleAssetId(null);
                }
            },
        });
    };

    const editableStepConfig = useMemo(() => ({}), []);

    const activeStep = reviewSteps[reviewStepIndex];
    const isFirstReviewStep = reviewStepIndex === 0;
    const isLastReviewStep = reviewStepIndex === reviewSteps.length - 1;
    const isPhotoFinishStep = activeStep?.type === "assetImages" && !!activeStep?.isFinishStep;
    const activeStepAsset =
        activeStep?.assetId && reviewData?.assets?.length
            ? reviewData.assets.find((asset) => asset.id === activeStep.assetId)
            : detailAsset;
    const isActiveAssetReviewed = isAssetReviewed(activeStepAsset);
    const hideFinishForReviewedAsset = isPhotoFinishStep && isActiveAssetReviewed;
    const nextButtonLabel = isPhotoFinishStep ? (finishingReview ? "Finishing..." : "Finish") : "Next";
    const progressWidth = reviewSteps.length
        ? ((reviewStepIndex + 1) / reviewSteps.length) * 100
        : 0;

    const handleStepDraftChange = (stepKey, fieldKey, value) => {
        setStepDrafts((prev) => ({
            ...prev,
            [stepKey]: {
                ...(prev[stepKey] || {}),
                [fieldKey]: value,
            },
        }));
    };

    const handleEditStep = (stepKey) => {
        const config = editableStepConfig[stepKey];
        if (!config) return;

        setStepDrafts((prev) => ({
            ...prev,
            [stepKey]: prev[stepKey] || config.buildDraft(),
        }));
        setEditingStepKey(stepKey);
    };

    const handleCancelEdit = () => {
        if (editingStepKey) {
            setStepDrafts((prev) => {
                const next = { ...prev };
                delete next[editingStepKey];
                return next;
            });
        }
        setEditingStepKey(null);
    };

    const handleSaveStep = async (stepKey) => {
        const config = editableStepConfig[stepKey];
        if (!config) return;

        const draft = stepDrafts[stepKey];
        if (!draft) return;

        setSavingStepKey(stepKey);
        try {
            const updated = await config.save(draft);
            setReviewData((prev) => {
                if (!prev) return prev;
                const nextValuation = { ...prev.valuation };

                if (stepKey === "project-overview") {
                    const {
                        valuation: valuationUpdates = {},
                        project: projectUpdates = {},
                        client: clientUpdates = {},
                    } = updated || {};

                    if (Object.keys(valuationUpdates).length > 0) {
                        nextValuation.valuation_ref =
                            valuationUpdates.valuation_ref || nextValuation.valuation_ref;
                        nextValuation.valuation_status =
                            valuationUpdates.valuation_status || nextValuation.valuation_status;
                        nextValuation.status =
                            valuationUpdates.valuation_status ||
                            valuationUpdates.status ||
                            nextValuation.status;
                        nextValuation.valuation_date =
                            valuationUpdates.valuation_date || nextValuation.valuation_date;
                        nextValuation.currency =
                            valuationUpdates.currency || nextValuation.currency;
                        nextValuation.notes = valuationUpdates.notes;
                    }

                    if (Object.keys(projectUpdates).length > 0) {
                        nextValuation.project = {
                            ...(nextValuation.project || {}),
                            ...projectUpdates,
                        };
                    }

                    if (Object.keys(clientUpdates).length > 0) {
                        nextValuation.client = {
                            ...(nextValuation.client || {}),
                            ...clientUpdates,
                        };
                    }
                }

                return {
                    ...prev,
                    valuation: nextValuation,
                };
            });

            await fetchValuations(paginationInfo.pageNumber, paginationInfo.pageSize);
            enqueueSnackbar("Step saved successfully.", { variant: "success" });
            setEditingStepKey(null);
        } catch (error) {
            console.error("Step save failed:", error);
            enqueueSnackbar("Failed to save updates. Please try again.", { variant: "error" });
        } finally {
            setSavingStepKey(null);
        }
    };

    const renderStepContent = (step) => {
        if (!step) return null;
        const isEditingStep = editingStepKey === step.key;
        const editableConfig = editableStepConfig[step.key];

        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">{step.title}</h5>
                    {editableConfig && (
                        <Button
                            color="outline-primary"
                            size="sm"
                            onClick={() => handleEditStep(step.key)}
                            disabled={isEditingStep}
                        >
                            {isEditingStep ? "Editing" : "Edit"}
                        </Button>
                    )}
                </div>

                {step.groups?.length ? (
                    <div className="row review-overview-grid gx-3 gy-4">
                        {step.groups.map((group) => (
                            <div
                                key={`${step.key}-${group.title}`}
                                className="col-12 col-md-6 col-xl-4"
                            >
                                <div className="review-group-card p-3 d-flex flex-column h-100">
                                    <div className="group-title text-uppercase text-muted mb-2">
                                        {group.title}
                                    </div>
                                    <div className="mt-2">
                                        {group.items.map((item) => (
                                            <div
                                                key={`${group.title}-${item.label}`}
                                                className="group-row d-flex justify-content-between"
                                            >
                                                <span className="text-muted">{item.label}</span>
                                                <span className="fw-bold text-end">
                                                    {item.label.toLowerCase().includes("status")
                                                        ? renderStatusBadge(item.value)
                                                        : renderCellValue(item.value)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        {step.summaryRows?.map((row) => (
                            <div
                                key={`${step.key}-${row.label}`}
                                className="border rounded p-2"
                                style={{
                                    minWidth: 140,
                                    flex: "1 1 140px",
                                    backgroundColor: "#f8f9fa",
                                }}
                            >
                                <small className="text-muted">{row.label}</small>
                                <div className="fw-bold">
                                    {row.label.toLowerCase().includes("status")
                                        ? renderStatusBadge(row.value)
                                        : renderCellValue(row.value)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {isEditingStep && editableConfig ? (
                    <Form>
                        {editableConfig.fields.map((field) => (
                            <FormGroup key={field.key}>
                                <Label className="fw-bold">{field.label}</Label>
                                {field.type === "textarea" ? (
                                    <Input
                                        type="textarea"
                                        rows={3}
                                        value={stepDrafts[step.key]?.[field.key] ?? ""}
                                        onChange={(e) =>
                                            handleStepDraftChange(step.key, field.key, e.target.value)
                                        }
                                    />
                                ) : (
                                    <Input
                                        type={field.type}
                                        value={stepDrafts[step.key]?.[field.key] ?? ""}
                                        onChange={(e) =>
                                            handleStepDraftChange(step.key, field.key, e.target.value)
                                        }
                                    />
                                )}
                            </FormGroup>
                        ))}
                        <div className="d-flex gap-2 mt-2">
                            <Button
                                color="success"
                                size="sm"
                                onClick={() => handleSaveStep(step.key)}
                                disabled={savingStepKey === step.key}
                            >
                                {savingStepKey === step.key ? "Saving..." : "Save Updates"}
                            </Button>
                            <Button color="secondary" size="sm" onClick={handleCancelEdit}>
                                Cancel
                            </Button>
                        </div>
                    </Form>
                ) : step.type === "assetsList" ? (
                    <div>
                        {step.summaryRows?.length ? (
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {step.summaryRows.map((row) => (
                                    <div
                                        key={`${step.key}-${row.label}`}
                                        className="border rounded p-2"
                                        style={{
                                            minWidth: 140,
                                            flex: "1 1 140px",
                                            backgroundColor: "#f8f9fa",
                                        }}
                                    >
                                        <small className="text-muted">{row.label}</small>
                                        <div className="fw-bold text-uppercase">
                                            {renderCellValue(row.value)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        {step.assets?.length ? (
                            <div className="row g-3">
                                {step.assets.map((asset, index) => {
                                    const reviewed = isAssetReviewed(asset);
                                    const assetName =
                                        resolveAssetNameDisplay(asset, "") ||
                                        normalizeDisplayValue(asset.description) ||
                                        "-";
                                    const serial =
                                        normalizeDisplayValue(asset.serialNumber) ||
                                        normalizeDisplayValue(asset.serial_number) ||
                                        "-";
                                    const categoryName =
                                        asset.asset_category?.name ||
                                        asset.category?.name ||
                                        "-";
                                    const location =
                                        normalizeDisplayValue(asset.location) ||
                                        normalizeDisplayValue(asset.building_name) ||
                                        normalizeDisplayValue(asset.ward_name) ||
                                        "-";
                                    const condition =
                                        normalizeDisplayValue(asset.observed_condition_name) ||
                                        normalizeDisplayValue(asset.observed_condition) ||
                                        "-";
                                    return (
                                        <div key={asset.id || asset.uuid || index} className="col-12 col-md-6 col-xl-4">
                                            <div className={`card asset-list-card h-100 ${reviewed ? "reviewed" : "not-reviewed"}`}>
                                                <div className="card-body">
                                                    <div className="card-meta mb-1">Asset #{index + 1}</div>
                                                    <h5 className="asset-name mb-1">{assetName}</h5>
                                                    <div className="asset-meta text-muted mb-3">{categoryName}</div>
                                                    <div className="asset-field">
                                                        <span>Serial</span>
                                                        <span className="fw-semibold text-end">{serial}</span>
                                                    </div>
                                                    <div className="asset-field">
                                                        <span>Location</span>
                                                        <span className="fw-semibold text-end">{location}</span>
                                                    </div>
                                                    <div className="asset-field">
                                                        <span>Condition</span>
                                                        <span className="fw-semibold text-end">{condition}</span>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2 asset-card-actions mt-3">
                                                        <Button
                                                            color={reviewed ? "danger" : "primary"}
                                                            size="sm"
                                                            className="px-3"
                                                            onClick={() => handleReviewDataAction(asset)}
                                                            disabled={reviewToggleAssetId === asset.id}
                                                        >
                                                            <i className={`${reviewed ? "ri-close-circle-line" : "ri-eye-line"} me-1`}></i>
                                                            {reviewToggleAssetId === asset.id
                                                                ? "Unreviewing..."
                                                                : (reviewed ? "Unreview Data" : "Review Data")}
                                                        </Button>
                                                        {reviewed ? (
                                                            <Button
                                                                color="link"
                                                                size="sm"
                                                                className="text-secondary ms-auto px-0"
                                                                onClick={() => handleViewAssetDetail(asset)}
                                                            >
                                                                <i className="ri-eye-line me-1"></i>
                                                                View Details
                                                            </Button>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center text-muted py-4">
                                No assets were captured yet for this valuation.
                            </div>
                        )}
                    </div>
                ) : step.type === "assetDetail" ? (
                    <div>
                        {step.asset ? (
                            <div className="row g-3">
                                <div className="col-12">
                                    <div className="review-group-card p-3">
                                        <div className="group-title text-uppercase text-muted mb-2">
                                            Asset Detail
                                        </div>
                                        <div className="mt-2">
                                            <AssetDetailForm
                                                asset={step.asset}
                                                projectCouncilId={step.projectCouncilId}
                                                isLocked={isAssetReviewed(step.asset)}
                                                onChange={(updated) => setDetailAsset(updated)}
                                            />
                                        </div>
                                        {!isAssetReviewed(step.asset) ? (
                                            <div className="d-flex justify-content-end mt-3">
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    outline
                                                    onClick={handleUpdateAsset}
                                                    disabled={updatingAsset}
                                                >
                                                    <i className="ri-edit-line me-1" />
                                                    {updatingAsset ? "Updating..." : "Update Asset"}
                                                </Button>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center text-muted py-4">
                                Select an asset from the Assets step to see the details here.
                            </div>
                        )}
                    </div>
                ) : step.type === "siteWorks" ? (
                    <div>
                        <div className="table-responsive" style={{ maxHeight: "60vh" }}>
                            <Table size="sm" bordered hover>
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Type</th>
                                        <th>Description</th>
                                        <th>Dimensions</th>
                                        <th>Location</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {step.siteWorks?.length ? (
                                        step.siteWorks.map((sw, index) => {
                                            const typeValue = renderCellValue(sw.site_work_type);
                                            const descriptionValue = renderCellValue(sw.description);
                                            const dimensionValue = renderCellValue(sw.dimensions);
                                            const locationValue =
                                                renderCellValue(sw.location) ||
                                                renderCellValue(sw.asset?.location) ||
                                                "-";

                                            return (
                                                <tr key={sw.id || index}>
                                                    <td>{index + 1}</td>
                                                    <td>{typeValue}</td>
                                                    <td>{descriptionValue}</td>
                                                    <td>{dimensionValue}</td>
                                                    <td>{locationValue}</td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center text-muted">
                                                No site works recorded yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        <p className="text-muted small mb-0">
                            Use the Site Works tab to edit entries if you need granular control.
                        </p>
                    </div>
                ) : step.type === "assetImages" ? (
                    <div>
                        <AssetPhotoGallery
                            assetId={step.assetId}
                            photos={step.photos || []}
                            loading={step.loading}
                            updatingPhotoId={updatingPhotoId}
                            onCreatePhoto={handleCreateAssetPhoto}
                            onReplacePhoto={(photo, file) => handleReplaceAssetPhoto(photo, file, step.assetId)}
                            onOpenPreview={openImagePreview}
                            cardColClassName="col-12 col-md-6 col-xl-4"
                            inputIdPrefix="review-replace-photo"
                            loadingContent={
                                <div className="text-center py-4">
                                    <Spinner size="sm" color="primary" className="me-2" />
                                    <span className="text-muted">Loading images...</span>
                                </div>
                            }
                            emptyMessage="No images found for this asset."
                        />
                    </div>
                ) : step.type === "assets" ? (
                    <div>
                        <div className="table-responsive" style={{ maxHeight: "60vh" }}>
                            <Table size="sm" bordered hover>
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Asset</th>
                                        <th>Serial</th>
                                        <th>Location</th>
                                        <th>Replacement Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {step.assets?.length ? (
                                        step.assets.map((asset, index) => {
                                            const assetName =
                                                resolveAssetNameDisplay(asset, "") ||
                                                normalizeDisplayValue(asset.description) ||
                                                "-";
                                            const assetSerial =
                                                normalizeDisplayValue(asset.serialNumber) ||
                                                normalizeDisplayValue(asset.serial_number) ||
                                                normalizeDisplayValue(asset.registration_number) ||
                                                "-";
                                            const assetLocation =
                                                normalizeDisplayValue(asset.location) ||
                                                normalizeDisplayValue(asset.building_name) ||
                                                normalizeDisplayValue(asset.ward_name) ||
                                                "-";

                                            return (
                                                <tr key={asset.id || asset.uuid || index}>
                                                    <td>{index + 1}</td>
                                                    <td>{assetName}</td>
                                                    <td>{assetSerial}</td>
                                                    <td>{assetLocation}</td>
                                                    <td>
                                                        {formatNumberValue(
                                                            asset.current_replacement_cost ||
                                                                asset.replacement_cost ||
                                                                asset.currentReplacementCost ||
                                                                asset.replacementCost ||
                                                                asset.fair_value ||
                                                                asset.fairValue ||
                                                                asset.net_book_value
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center text-muted">
                                                No assets captured for this category.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        <p className="text-muted small mb-0">
                            Use the Assets tab to change individual asset details.
                        </p>
                    </div>
                ) : (
                    <div className="text-muted">
                        {step.summaryRows?.map((row) => (
                            <div key={`${step.key}-${row.label}`} className="mb-2">
                                <strong>{row.label}:</strong> {renderCellValue(row.value)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const goToPreviousStep = () => {
        setReviewStepIndex((prev) => Math.max(prev - 1, 0));
    };

    const goToNextStep = async () => {
        if (!reviewSteps.length) return;
        if (isPhotoFinishStep) {
            const assetId = activeStep?.assetId || detailAsset?.id;
            if (assetId && !isActiveAssetReviewed) {
                setFinishingReview(true);
                try {
                    await reviewAssetById(assetId, "Asset reviewed and marked as finished.");
                } catch (error) {
                    console.error("Failed to review asset on finish:", error);
                    enqueueSnackbar("Failed to finish asset review.", { variant: "error" });
                    setFinishingReview(false);
                    return;
                } finally {
                    setFinishingReview(false);
                }
            }
            const assetsTabIndex = reviewSteps.findIndex((step) => step.key === "assets-list");
            if (assetsTabIndex !== -1) {
                setReviewStepIndex(assetsTabIndex);
                return;
            }
        }
        setReviewStepIndex((prev) => Math.min(prev + 1, reviewSteps.length - 1));
    };

    // Filter table rows based on search term (client-side)
    const searchFilteredValuations = useMemo(() => {
        if (isDataReviewAssetMode) return valuations;

        if (!searchTerm.trim()) return valuations;
        
        const term = searchTerm.toLowerCase().trim();

        if (tableMode === "assets") {
            return valuations.filter((v) => {
                const asset = v.raw || {};
                const searchableFields = [
                    v.assetName,
                    v.categoryCode,
                    v.barcode,
                    v.quantity,
                    v.location,
                    v.condition,
                    v.reviewed,
                    asset.valuation?.valuation_ref,
                    asset.valuation_ref,
                    asset.valuation_number,
                    asset.barcode,
                    asset.serial_number,
                ];
                return searchableFields.some(
                    (field) => field && String(field).toLowerCase().includes(term)
                );
            });
        }
        
        return valuations.filter(v => {
            // Basic valuation fields (from normalized data)
            const searchableFields = [
                v.valuationRef,
                v.projectName,
                v.clientName,
                v.valuerName,
                v.status,
                v.valuationDate,
            ];
            
            // Also check raw data for additional reference number fields
            if (v.raw) {
                searchableFields.push(
                    v.raw.valuation_ref,
                    v.raw.valuation_number,
                    v.raw.valuationRef,
                    v.raw.valuationNumber,
                    v.raw.reference_number,
                    v.raw.referenceNumber,
                    v.raw.ref_number,
                    v.raw.refNumber
                );
            }
            
            // Check basic fields first (case-insensitive partial match)
            const basicMatch = searchableFields.some(field => 
                field && String(field).toLowerCase().includes(term)
            );
            
            if (basicMatch) return true;
            
            // Also search through assets if available
            if (v.raw && v.raw.assets && Array.isArray(v.raw.assets)) {
                return v.raw.assets.some(asset => {
                    const assetFields = [
                        asset.name,
                        asset.building_name,
                        asset.buildingName,
                        asset.other_building_name,
                        asset.otherBuildingName,
                        asset.other_name,
                        asset.otherName,
                        asset.description,
                        asset.location,
                        asset.institution_name,
                        asset.asset_category?.name,
                        asset.serial_number,
                        asset.serialNumber,
                        asset.barcode,
                    ];
                    
                    return assetFields.some(field => 
                        field && String(field).toLowerCase().includes(term)
                    );
                });
            }
            
            return false;
        });
    }, [valuations, searchTerm, tableMode, isDataReviewAssetMode]);

    const effectiveReviewFilter = showReviewTabs ? activeReviewTab : reviewFilter;

    const reviewFilteredValuations = useMemo(() => {
        // Data Review asset mode already requests review-filtered rows from the API.
        // Reapplying the tab filter client-side can hide valid server results and desync the table from pagination.
        if (isDataReviewAssetMode) return searchFilteredValuations;

        if (effectiveReviewFilter === "all") return searchFilteredValuations;

        return searchFilteredValuations.filter(
            (valuation) => getReviewBucket(valuation) === effectiveReviewFilter
        );
    }, [searchFilteredValuations, effectiveReviewFilter, isDataReviewAssetMode]);

    const filteredValuations = useMemo(() => {
        // Category selection is also applied on the server in Data Review asset mode.
        // Keep the fetched page intact so the row set matches the pagination metadata.
        if (isDataReviewAssetMode) return reviewFilteredValuations;

        const selectedCode = String(activeCategoryTab || "all").toUpperCase();
        if (selectedCode === "ALL") return reviewFilteredValuations;

        return reviewFilteredValuations.filter((valuation) => {
            const codes = getValuationCategoryCodes(valuation);
            return codes.has(selectedCode);
        });
    }, [reviewFilteredValuations, activeCategoryTab, isDataReviewAssetMode]);

    const showHasSiteWorkColumn = useMemo(() => {
        if (tableMode !== "assets" || filteredValuations.length === 0) return false;
        return filteredValuations.some(
            (row) => String(row?.categoryCode || row?.category_code || "").toUpperCase() === "BLDG"
        );
    }, [filteredValuations, tableMode]);

    const showOdoColumn = useMemo(() => {
        if (tableMode !== "assets" || filteredValuations.length === 0) return false;
        return filteredValuations.some((row) => {
            const code = String(row?.categoryCode || row?.category_code || "").toUpperCase();
            return code === "MACH" || code === "VEH";
        });
    }, [filteredValuations, tableMode]);

    const showBuildingCountColumn = useMemo(() => {
        if (tableMode !== "assets" || filteredValuations.length === 0) return false;
        return filteredValuations.some((row) => {
            const code = String(row?.categoryCode || row?.category_code || "").toUpperCase();
            return code === "BLDG";
        });
    }, [filteredValuations, tableMode]);

    const showDataReviewerColumn =
        tableMode === "assets" &&
        showReviewTabs &&
        activeReviewTab === "reviewed";
    const showReviewRemarkColumn =
        tableMode === "assets" &&
        showReviewTabs &&
        (activeReviewTab === "review-not-completed" || activeReviewTab === REVIEW_REMARK_ISSUE_TAB);
    const isIncompleteTab =
        tableMode === "assets" && showReviewTabs && activeReviewTab === "review-not-completed";
    const isRemarkIssuesTab =
        tableMode === "assets" && showReviewTabs && activeReviewTab === REVIEW_REMARK_ISSUE_TAB;

    const categoryStats = useMemo(() => {
        if (!categoryTabs.length) return [];

        return categoryTabs.map((tab) => {
            const code = String(tab.value || "").toUpperCase();
            const matches = reviewFilteredValuations.filter((valuation) => {
                const codes = getValuationCategoryCodes(valuation);
                return codes.has(code);
            });

            return {
                ...tab,
                total: matches.length,
            };
        });
    }, [categoryTabs, reviewFilteredValuations]);

    const categoryTotalsByCode = useMemo(() => {
        if (Object.keys(serverCategoryTotalsByCode).length > 0) {
            return serverCategoryTotalsByCode;
        }
        return categoryStats.reduce((acc, stat) => {
            acc[String(stat.value || "").toUpperCase()] = stat.total || 0;
            return acc;
        }, {});
    }, [categoryStats, serverCategoryTotalsByCode]);

    const displayPaginationInfo = useMemo(() => {
        if (tableMode !== "assets") return paginationInfo;

        // Use server-provided pagination so page counts stay accurate (e.g. 22,223 pages).
        const inferredTotalPages = Math.ceil(
            (Number(paginationInfo.totalElements) || 0) /
                (paginationInfo.pageSize || 10)
        );

        return {
            ...paginationInfo,
            totalPages: Number(paginationInfo.totalPages) || inferredTotalPages || 1,
        };
    }, [tableMode, paginationInfo]);

    const selectedEditAsset = useMemo(() => {
        if (!editAssets.length) return null;
        if (!selectedEditAssetId) return editAssets[0];
        return editAssets.find((asset) => String(asset.id) === String(selectedEditAssetId)) || editAssets[0];
    }, [editAssets, selectedEditAssetId]);

    const isEditAssetBuildingCategory = useMemo(
        () => getCategoryCode(selectedEditAssetDraft || selectedEditAsset || {}) === "BLDG",
        [selectedEditAssetDraft, selectedEditAsset]
    );

    const expectedBuildingCount = useMemo(
        () =>
            toIntOrNull(
                selectedEditAssetDraft?.number_of_buildings ||
                    selectedEditAssetDraft?.numberOfBuildings
            ) || 0,
        [selectedEditAssetDraft]
    );

    const editBuildingsBySlot = useMemo(() => {
        const bySlot = {};
        editBuildings.forEach((building, index) => {
            const slotFromBuilding = toIntOrNull(
                building?.serial_number ??
                building?.serialNumber ??
                building?.building_number ??
                building?.buildingNumber
            );
            let slot = slotFromBuilding ?? (index + 1);
            while (bySlot[slot]) {
                slot += 1;
            }
            bySlot[slot] = building;
        });
        return bySlot;
    }, [editBuildings]);

    const activeBuildingSlotNumbers = useMemo(() => {
        if (expectedBuildingCount <= 0) return [];
        return Array.from({ length: expectedBuildingCount }, (_, idx) => idx + 1)
            .filter((slot) => !dismissedBuildingSlots.has(slot));
    }, [expectedBuildingCount, dismissedBuildingSlots]);

    const stepBuildingRows = useMemo(() => {
        return activeBuildingSlotNumbers.map((serial) => ({
            serial,
            building: editBuildingsBySlot[serial] || null,
        }));
    }, [activeBuildingSlotNumbers, editBuildingsBySlot]);

    const capturedBuildingCount = useMemo(
        () => activeBuildingSlotNumbers.filter((slot) => !!editBuildingsBySlot[slot]?.id).length,
        [activeBuildingSlotNumbers, editBuildingsBySlot]
    );

    const remainingBuildingSlots = useMemo(
        () => activeBuildingSlotNumbers.filter((slot) => !editBuildingsBySlot[slot]?.id).length,
        [activeBuildingSlotNumbers, editBuildingsBySlot]
    );

    const getBuildingStepTab = useCallback(
        (slotNumber) => buildingStepTabsBySlot[String(slotNumber)] || "form",
        [buildingStepTabsBySlot]
    );

    const setBuildingStepTabForSlot = useCallback((slotNumber, nextStep) => {
        setBuildingStepTabsBySlot((prev) => ({
            ...prev,
            [String(slotNumber)]: nextStep,
        }));
    }, []);

    const handlePrimaryBuildingToggle = useCallback(
        (slotNumber, checked) => {
            setBuildingDrafts((prev) => {
                const next = { ...prev };

                for (let slot = 1; slot <= expectedBuildingCount; slot += 1) {
                    if (dismissedBuildingSlots.has(slot)) {
                        continue;
                    }
                    const existingDraft =
                        next[slot] || createBuildingDraftFromEntity(editBuildingsBySlot[slot] || null, slot);
                    next[slot] = {
                        ...existingDraft,
                        is_primary: checked ? slot === slotNumber : slot === slotNumber ? false : !!existingDraft.is_primary,
                    };
                }

                return next;
            });
        },
        [expectedBuildingCount, editBuildingsBySlot, dismissedBuildingSlots]
    );

    const canMarkBuildingReviewed = useMemo(
        () =>
            activeBuildingSlotNumbers.length > 0 &&
            activeBuildingSlotNumbers.every((slot) => !!editBuildingsBySlot[slot]?.id),
        [activeBuildingSlotNumbers, editBuildingsBySlot]
    );

    const canCompleteSiteWorkStep = useMemo(() => {
        if (!selectedEditAssetHasSiteWork) return true;
        return editSiteWorks.length > 0;
    }, [editSiteWorks.length, selectedEditAssetHasSiteWork]);

    useEffect(() => {
        if (!editModalOpen || !isEditAssetBuildingCategory) return;
        if (editBuildingStepIndex !== 2) return;

        setBuildingDrafts((prev) => {
            const next = { ...prev };
            for (let slot = 1; slot <= expectedBuildingCount; slot += 1) {
                if (dismissedBuildingSlots.has(slot)) {
                    delete next[slot];
                    continue;
                }
                const existing = editBuildingsBySlot[slot] || null;
                const existingDraft = existing ? createBuildingDraftFromEntity(existing, slot) : null;
                const hasBackendRefresh =
                    existingDraft &&
                    (
                        existingDraft.building_name !== next[slot]?.building_name ||
                        existingDraft.other_building_name !== next[slot]?.other_building_name ||
                        existingDraft.observed_condition !== next[slot]?.observed_condition
                    );

                if (!next[slot] || existing?.id !== next[slot]?.id || hasBackendRefresh) {
                    next[slot] = existingDraft || createBuildingDraftFromEntity(existing, slot);
                }
            }
            Object.keys(next).forEach((key) => {
                const slotNumber = Number(key);
                if (slotNumber > expectedBuildingCount || dismissedBuildingSlots.has(slotNumber)) {
                    delete next[key];
                }
            });
            return next;
        });
    }, [
        editModalOpen,
        isEditAssetBuildingCategory,
        editBuildingStepIndex,
        expectedBuildingCount,
        editBuildingsBySlot,
        dismissedBuildingSlots,
    ]);

    const editAssetPhotos = useMemo(() => {
        const assetId = selectedEditAssetDraft?.id;
        if (!assetId) return [];
        return assetPhotosByAssetId[assetId] || [];
    }, [selectedEditAssetDraft?.id, assetPhotosByAssetId]);

    const editAssetPhotosLoading =
        !!selectedEditAssetDraft?.id &&
        assetPhotosLoadingForAssetId === selectedEditAssetDraft.id;

    useEffect(() => {
        setSelectedEditAssetDraft(selectedEditAsset || null);
    }, [selectedEditAsset]);

    useEffect(() => {
        if (!editModalOpen) return;
        const assetId = selectedEditAssetDraft?.id;
        if (assetId) {
            ensureAssetPhotosLoaded(assetId);
        }
    }, [editModalOpen, selectedEditAssetDraft?.id, ensureAssetPhotosLoaded]);

    useEffect(() => {
        if (!editModalOpen) return;
        if (!isEditAssetBuildingCategory) return;
        setEditBuildingStepIndex(0);
        setHighestSavedStepIndex(0);
        setSecurityOverviewDraft(createEmptySecurityOverview());
        setEditBuildings([]);
        setBuildingAccommodationsByBuildingId({});
        setAccommodationDrafts({});
        setEditSiteWorks([]);
        setSiteWorkPhotosBySiteWorkId({});
        loadedEditSiteWorksKeyRef.current = "";
    }, [editModalOpen, selectedEditAssetDraft?.id, isEditAssetBuildingCategory]);

    useEffect(() => {
        if (!editModalOpen || !isEditAssetBuildingCategory) return;
        if (editBuildingStepIndex === 1) {
            loadSecurityOverview();
        }
        if (editBuildingStepIndex === 2) {
            loadBuildingsForEdit();
            loadBuildingFormOptions();
        }
        if (editBuildingStepIndex === 3) {
            const assetId = selectedEditAssetDraft?.id || "";
            const valuationId = getSelectedEditValuationId();
            const loadKey = `${assetId}:${valuationId || ""}`;
            const loadingKey = `loading:${loadKey}`;
            const currentKey = loadedEditSiteWorksKeyRef.current;
            if (assetId && currentKey !== loadKey && currentKey !== loadingKey) {
                loadedEditSiteWorksKeyRef.current = loadingKey;
                loadSiteWorksForEdit().then((loaded) => {
                    loadedEditSiteWorksKeyRef.current = loaded ? loadKey : "";
                });
            }
        }
    }, [
        editModalOpen,
        isEditAssetBuildingCategory,
        editBuildingStepIndex,
        loadSecurityOverview,
        loadBuildingsForEdit,
        loadBuildingFormOptions,
        loadSiteWorksForEdit,
        getSelectedEditValuationId,
        selectedEditAssetDraft?.id,
    ]);

    useEffect(() => {
        const directCouncilId = extractCouncilIdFromAsset(selectedEditAssetDraft);
        if (directCouncilId) {
            setEditModalCouncilId(directCouncilId);
            return;
        }

        const valuationId =
            selectedEditAssetDraft?.asset_valuation_id ||
            selectedEditAssetDraft?.assetValuationId ||
            selectedEditAssetDraft?.asset_valuation?.id ||
            selectedEditAssetDraft?.valuation?.id;

        if (!valuationId) {
            setEditModalCouncilId("");
            return;
        }

        let isActive = true;
        getAssetValuation(valuationId)
            .then((response) => {
                const valuation = response?.data?.data || response?.data || {};
                const councilId =
                    valuation?.project?.council?.id ||
                    valuation?.project?.council_id ||
                    valuation?.project?.councilId ||
                    "";
                if (isActive) {
                    setEditModalCouncilId(councilId ? String(councilId) : "");
                }
            })
            .catch(() => {
                if (isActive) setEditModalCouncilId("");
            });

        return () => {
            isActive = false;
        };
    }, [selectedEditAssetDraft]);


    // Fetch all valuations with their assets for export
    const fetchValuationsWithAssets = useCallback(async (searchReference = null) => {
        try {
            let allValuationsWithAssets = [];
            let pageNumber = 0;
            const pageSize = 100; // Fetch valuations in batches
            let hasMore = true;
            
            while (hasMore) {
                const response = await getAllAssetValuations({
                    projectId: filters.projectId || undefined,
                    clientId: filters.clientId || undefined,
                    status: filters.status || undefined,
                    startDate: filters.startDate || undefined,
                    endDate: filters.endDate || undefined,
                    page: pageNumber,
                    size: pageSize,
                });
                
                const { data } = response;
                const result = data.data; // backend wraps inside "data"
                let valuations = result.content || [];
                
                // If search reference is provided, filter valuations by reference number
                if (searchReference && searchReference.trim()) {
                    const searchTerm = searchReference.toLowerCase().trim();
                    valuations = valuations.filter(valuation => {
                        const refFields = [
                            valuation.valuation_ref,
                            valuation.valuation_number,
                            valuation.valuationRef,
                            valuation.valuationNumber,
                            valuation.reference_number,
                            valuation.referenceNumber,
                            valuation.ref_number,
                            valuation.refNumber
                        ];
                        
                        return refFields.some(field => 
                            field && String(field).toLowerCase().includes(searchTerm)
                        );
                    });
                }
                
                // If no data returned, break to prevent infinite loop
                if (!valuations || valuations.length === 0) {
                    break;
                }
                
                // For each valuation, fetch its assets
                for (const valuation of valuations) {
                    try {
                        const assetsRes = await getAssetsByValuation(valuation.id);
                        const assets = assetsRes.data?.data || assetsRes.data || [];
                        
                        // Create rows: each asset with valuation details
                        const rows = assets.map(asset => ({
                            // Valuation details (repeated for each asset)
                            valuation_id: valuation.id,
                            valuation_number: valuation.valuation_ref || valuation.valuation_number || "",
                            client_name: valuation.client?.name || valuation.client_name || "",
                            project_name: valuation.project?.name || valuation.project_name || "",
                            valuation_date: valuation.valuation_date || "",
                            status: valuation.valuation_status || valuation.status || "",
                            created_at: valuation.created_at || "",
                            created_by: valuation.created_by || "",
                            
                            // Asset details
                            ...asset,
                        }));
                        
                        allValuationsWithAssets = [...allValuationsWithAssets, ...rows];
                    } catch (err) {
                        console.error(`Failed to fetch assets for valuation ${valuation.id}:`, err);
                        // Continue with next valuation even if one fails
                    }
                }
                
                // If we filtered by reference and found results, we can stop
                if (searchReference && searchReference.trim() && allValuationsWithAssets.length > 0) {
                    hasMore = false;
                    break;
                }
                
                // Check both camelCase and snake_case for pagination
                const totalPages = result.totalPages || result.total_pages || 1;
                
                // If we got less data than pageSize, we're on the last page
                if (valuations.length < pageSize) {
                    hasMore = false;
                } else {
                    // Continue if there are more pages
                    hasMore = pageNumber < totalPages - 1;
                }
                
                pageNumber++;
            }
            
            return allValuationsWithAssets;
        } catch (err) {
            console.error("Failed to fetch valuations with assets:", err);
            return [];
        }
    }, [filters]);

    // Export by Valuation handler
    const handleExportByValuation = async (exportType) => {
        setIsExporting(true);
        
        try {
            // Check if there's a search term (reference number)
            const searchRef = searchTerm.trim() || null;
            
            enqueueSnackbar(
                searchRef 
                    ? `Fetching assets for valuation with reference "${searchRef}"...`
                    : 'Fetching valuations and assets... This may take a while.',
                {
                    variant: 'info',
                    autoHideDuration: 3000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
            
            const valuationsWithAssets = await fetchValuationsWithAssets(searchRef);
            
            if (valuationsWithAssets.length === 0) {
                enqueueSnackbar(
                    'No valuations with assets found to export.',
                    {
                        variant: 'warning',
                        autoHideDuration: 4000,
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        },
                    }
                );
                setIsExporting(false);
                return;
            }
            
            // Prepare headers for export (valuation fields + asset fields)
            const valuationHeaders = [
                { key: "valuation_id", label: "Valuation ID" },
                { key: "valuation_number", label: "Valuation Number" },
                { key: "client_name", label: "Client Name" },
                { key: "project_name", label: "Project Name" },
                { key: "valuation_date", label: "Valuation Date" },
                { key: "status", label: "Status" },
            ];
            
            // Common asset headers
            const assetHeaders = [
                { key: "serialNumber", label: "#" },
                { key: "name", label: "Asset Name" },
                { key: "building_name", label: "Building Name" },
                { key: "description", label: "Description" },
                { key: "category_name", label: "Category" },
                { key: "location", label: "Location" },
                { key: "observed_condition_name", label: "Condition" },
                { key: "remark", label: "Remark" },
                { key: "current_replacement_cost", label: "Replacement Cost" },
                { key: "fair_value", label: "Fair Value" },
                { key: "net_book_value", label: "Net Book Value" },
            ];
            
            // Combine headers (valuation first, then assets)
            const allHeaders = [...valuationHeaders, ...assetHeaders];
            
            // Process data for export
            const processedData = valuationsWithAssets.map((row) => {
                const processed = { ...row };
                
                // Format utilities
                if (row.has_water !== undefined && row.has_electricity !== undefined) {
                    const water = row.has_water ? "Water" : "";
                    const electricity = row.has_electricity ? "Electricity" : "";
                    processed.utilities = [water, electricity].filter(Boolean).join(", ") || "None";
                }
                
                processed.name = resolveAssetNameDisplay(row);
                processed.building_name = resolveBuildingNameDisplay(row, processed.building_name || "-");
                
                return processed;
            });
            
            // Perform export
            const refSuffix = searchRef ? `_${searchRef.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
            const filename = `valuations_with_assets${refSuffix}_${new Date().toISOString().split('T')[0]}`;
            
            if (exportType === 'csv') {
                exportToCSV(processedData, allHeaders, `${filename}.csv`);
            } else if (exportType === 'excel') {
                exportToExcel(processedData, allHeaders, `${filename}.xlsx`);
            } else if (exportType === 'pdf') {
                // Get current logged-in user info
                let exportedBy = "System User";
                try {
                    const authUser = localStorage.getItem("authUser");
                    if (authUser) {
                        const user = JSON.parse(authUser);
                        if (user) {
                            const firstName = user.first_name || user.firstName || "";
                            const lastName = user.last_name || user.lastName || "";
                            exportedBy = `${firstName} ${lastName}`.trim() || user.username || user.email || "System User";
                        }
                    }
                } catch (error) {
                    console.error("Error getting user info:", error);
                }
                
                const title = "Valuations with Assets Report";
                const metadata = {
                    exportedBy: exportedBy,
                    clientName: "", // Multiple clients in this export
                    projectName: "", // Multiple projects in this export
                };
                
                exportToPDF(processedData, allHeaders, `${filename}.pdf`, title, metadata);
            }
            
            enqueueSnackbar(
                `Successfully exported ${valuationsWithAssets.length} records.`,
                {
                    variant: 'success',
                    autoHideDuration: 4000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
        } catch (error) {
            console.error("Error exporting by valuation:", error);
            enqueueSnackbar(
                'Failed to export valuations with assets. Please try again.',
                {
                    variant: 'error',
                    autoHideDuration: 5000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportIncompleteAssets = async () => {
        setIsExporting(true);
        try {
            const response = await exportIncompleteAssets();
            const blob = response?.data;
            if (!blob || blob.size === 0) {
                throw new Error("The generated incomplete assets export file was empty.");
            }

            const disposition = response?.headers?.["content-disposition"] || "";
            const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
            const filename = filenameMatch?.[1] || `incomplete_assets_${new Date().toISOString().slice(0, 10)}.xlsx`;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            enqueueSnackbar("Incomplete assets export downloaded.", { variant: "success" });
        } catch (error) {
            console.error("Error exporting incomplete assets:", error);
            enqueueSnackbar("Failed to export incomplete assets.", { variant: "error" });
        } finally {
            setIsExporting(false);
        }
    };

    const mapBarcodePdfsByCategory = useCallback((files) => {
        const grouped = Object.values(BARCODE_PREFIX_TO_CATEGORY).reduce((acc, label) => {
            acc[label] = [];
            return acc;
        }, {});

        (files || []).forEach((file) => {
            const filename = file?.filename?.toLowerCase?.() || "";
            const matchedPrefix = Object.keys(BARCODE_PREFIX_TO_CATEGORY).find((prefix) =>
                filename.startsWith(`barcodes-${prefix}`)
            );

            if (matchedPrefix) {
                const categoryLabel = BARCODE_PREFIX_TO_CATEGORY[matchedPrefix];
                grouped[categoryLabel].push({
                    ...file,
                    downloadUrl: resolveServerFileUrl(file.url),
                });
            }
        });

        return grouped;
    }, []);

    const handleOpenBarcodePdfModal = async () => {
        setBarcodePdfModalOpen(true);

        if (Object.keys(barcodePdfsByCategory).length > 0) {
            return;
        }

        setLoadingBarcodePdfs(true);
        try {
            const response = await getBarcodePdfFiles();
            const barcodeFiles = response?.data?.data?.["barcode-pdfs"] || [];
            const grouped = mapBarcodePdfsByCategory(barcodeFiles);
            setBarcodePdfsByCategory(grouped);
        } catch (error) {
            console.error("Failed to fetch barcode PDF files:", error);
            enqueueSnackbar("Failed to fetch barcode PDF files.", {
                variant: "error",
                autoHideDuration: 4000,
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "right",
                },
            });
            setBarcodePdfsByCategory({});
        } finally {
            setLoadingBarcodePdfs(false);
        }
    };

    const barcodePdfCategoryEntries = Object.entries(barcodePdfsByCategory);

    if (isReportsView) {
        return (
            <React.Fragment>
                <div className="page-content">
                    <Container fluid>
                        <Breadcrumbs
                            title="Asset Valuation"
                            breadcrumbItem="Reports"
                        />
                        <BarcodeAssetReports />
                    </Container>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <style>{spinnerStyle}</style>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs
                        title={breadcrumbTitle}
                        breadcrumbItem={breadcrumbItem}
                    />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <h4 className="card-title mb-0">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span>{cardTitle}</span>
                                            <div className="d-flex gap-2">
                                                {isDataReviewAssetMode && (
                                                    <Button
                                                        type="button"
                                                        onClick={handleExportIncompleteAssets}
                                                        disabled={isExporting}
                                                        style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                                                    >
                                                        <i className="ri-file-excel-2-line me-2"></i>
                                                        {isExporting ? "Exporting..." : "Export Incomplete Assets"}
                                                    </Button>
                                                )}
                                                {/* Export Dropdown */}
                                                {filteredValuations.length > 0 && tableMode !== "assets" && (
                                                    <Dropdown
                                                        isOpen={exportDropdownOpen}
                                                        toggle={() => setExportDropdownOpen(!exportDropdownOpen)}
                                                    >
                                                        <DropdownToggle
                                                            caret
                                                            style={{
                                                                backgroundColor: BRAND_BLUE,
                                                                borderColor: BRAND_BLUE,
                                                                color: "white",
                                                            }}
                                                            disabled={isExporting}
                                                        >
                                                            <i className="ri-download-line me-2"></i>
                                                            {isExporting ? 'Exporting...' : 'Export'}
                                                        </DropdownToggle>
                                                        <DropdownMenu>
                                                            <DropdownItem onClick={() => handleExportByValuation('csv')}>
                                                                <i className="ri-file-text-line me-2"></i>
                                                                Export by Valuation (CSV)
                                                            </DropdownItem>
                                                            <DropdownItem onClick={() => handleExportByValuation('excel')}>
                                                                <i className="ri-file-excel-line me-2"></i>
                                                                Export by Valuation (Excel)
                                                            </DropdownItem>
                                                            <DropdownItem onClick={() => handleExportByValuation('pdf')}>
                                                                <i className="ri-file-pdf-line me-2"></i>
                                                                Export by Valuation (PDF)
                                                            </DropdownItem>
                                                            <DropdownItem divider />
                                                            <DropdownItem onClick={handleOpenBarcodePdfModal}>
                                                                <i className="ri-barcode-box-line me-2"></i>
                                                                Download Barcode PDFs by Category
                                                            </DropdownItem>
                                                        </DropdownMenu>
                                                    </Dropdown>
                                                )}
                                                {showAddButton && (
                                                    <Button
                                                        onClick={(e) => e.preventDefault()}
                                                        className="add-btn"
                                                        id="create-btn"
                                                        style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                                                    >
                                                        <i className="ri-add-line align-bottom me-1" /> Add
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </h4>
                                </CardHeader>

                                <CardBody>
                                    {/* 🔍 Filter Bar */}
                                    <Form className="mb-3">
                                        <ReviewFilterTabs
                                            show={showReviewTabs}
                                            tabs={reviewTabs}
                                            activeValue={activeReviewTab}
                                            onChange={setActiveReviewTab}
                                        />
                                        <CategoryFilterTabs
                                            show={showCategoryTabs}
                                            tabs={categoryTabs}
                                            activeValue={activeCategoryTab}
                                            totalsByCode={categoryTotalsByCode}
                                            onChange={setActiveCategoryTab}
                                        />

                                        {tableMode !== "assets" && (
                                            <Row className="g-3">
                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label for="clientId">Client</Label>
                                                        <Input
                                                            type="select"
                                                            name="clientId"
                                                            value={filters.clientId}
                                                            onChange={handleFilterChange}
                                                        >
                                                            <option value="">All Clients</option>
                                                            {clients.map((c) => (
                                                                <option key={c.id} value={c.id}>
                                                                    {c.name}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>

                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label for="projectId">Project</Label>
                                                        <Input
                                                            type="select"
                                                            name="projectId"
                                                            value={filters.projectId}
                                                            onChange={handleFilterChange}
                                                        >
                                                            <option value="">All Projects</option>
                                                            {projects.map((p) => (
                                                                <option key={p.id} value={p.id}>
                                                                    {p.name}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>

                                                <Col md={2}>
                                                    <FormGroup>
                                                        <Label for="status">Status</Label>
                                                        <Input
                                                            type="select"
                                                            name="status"
                                                            value={filters.status}
                                                            onChange={handleFilterChange}
                                                        >
                                                            <option value="">All</option>
                                                            <option value="DRAFT">DRAFT</option>
                                                            <option value="SUBMITTED">SUBMITTED</option>
                                                            <option value="APPROVED">APPROVED</option>
                                                            <option value="REJECTED">REJECTED</option>
                                                        </Input>
                                                    </FormGroup>
                                                </Col>

                                                {showDateFilters && (
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="startDate">Start Date</Label>
                                                            <Input
                                                                type="date"
                                                                name="startDate"
                                                                value={filters.startDate}
                                                                onChange={handleFilterChange}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                )}

                                                {showDateFilters && (
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="endDate">End Date</Label>
                                                            <Input
                                                                type="date"
                                                                name="endDate"
                                                                value={filters.endDate}
                                                                onChange={handleFilterChange}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                )}

                                            </Row>
                                        )}
                                        
                                        {isDataReviewAssetMode ? (
                                            <>
                                                <Row className="g-3">
                                                    <Col md={3}>
                                                        <FormGroup>
                                                            <Label for="asset-search-all-fields">Search All Fields</Label>
                                                            <Input
                                                                id="asset-search-all-fields"
                                                                name="search"
                                                                type="text"
                                                                placeholder="Search across asset fields"
                                                                value={assetSearchFilters.search}
                                                                onChange={handleAssetSearchFilterChange}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="asset-search-name">Asset Name</Label>
                                                            <Input
                                                                id="asset-search-name"
                                                                name="name"
                                                                type="text"
                                                                placeholder="Search asset name"
                                                                value={assetSearchFilters.name}
                                                                onChange={handleAssetSearchFilterChange}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="asset-search-description">Description</Label>
                                                            <Input
                                                                id="asset-search-description"
                                                                name="description"
                                                                type="text"
                                                                placeholder="Search description"
                                                                value={assetSearchFilters.description}
                                                                onChange={handleAssetSearchFilterChange}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="asset-search-other-name">Other Name</Label>
                                                            <Input
                                                                id="asset-search-other-name"
                                                                name="otherName"
                                                                type="text"
                                                                placeholder="Search normalized name"
                                                                value={assetSearchFilters.otherName}
                                                                onChange={handleAssetSearchFilterChange}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="asset-search-valuation-ref">Valuation Ref</Label>
                                                            <Input
                                                                id="asset-search-valuation-ref"
                                                                name="valuationRef"
                                                                type="text"
                                                                placeholder="Search valuation ref"
                                                                value={assetSearchFilters.valuationRef}
                                                                onChange={handleAssetSearchFilterChange}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="asset-search-valuer">Valuer</Label>
                                                            <Input
                                                                id="asset-search-valuer"
                                                                name="valuer"
                                                                type="text"
                                                                placeholder="Search valuer"
                                                                value={assetSearchFilters.valuer}
                                                                onChange={handleAssetSearchFilterChange}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="asset-search-institution-name">Institution Name</Label>
                                                            <Input
                                                                id="asset-search-institution-name"
                                                                name="institutionName"
                                                                type="text"
                                                                placeholder="Search institution"
                                                                value={assetSearchFilters.institutionName}
                                                                onChange={handleAssetSearchFilterChange}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="asset-search-condition">Condition</Label>
                                                            <Input
                                                                id="asset-search-condition"
                                                                name="condition"
                                                                type="select"
                                                                value={assetSearchFilters.condition}
                                                                onChange={handleAssetSearchFilterChange}
                                                            >
                                                                <option value="">All Conditions</option>
                                                                {buildingObservedConditions.map((conditionOption) => {
                                                                    const optionValue =
                                                                        conditionOption?.name ||
                                                                        conditionOption?.label ||
                                                                        conditionOption?.code ||
                                                                        "";
                                                                    return (
                                                                        <option
                                                                            key={conditionOption?.id || optionValue}
                                                                            value={optionValue}
                                                                        >
                                                                            {optionValue}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                                <Row className="g-3 align-items-end">
                                                    <Col md={8} />
                                                    <Col md={4} className="d-flex justify-content-end gap-2">
                                                        <Button
                                                            type="button"
                                                            onClick={handleApplyFilters}
                                                            disabled={loading}
                                                            style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                                                        >
                                                            {loading ? "Searching..." : "Search"}
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            color="secondary"
                                                            onClick={handleResetFilters}
                                                            disabled={loading}
                                                        >
                                                            Reset
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </>
                                        ) : (
                                            <Row className="g-3">
                                                <Col md={tableMode === "assets" ? 12 : 8}>
                                                    <InputGroup>
                                                        <InputGroupText style={{ backgroundColor: "white" }}>
                                                            <i className="ri-search-line"></i>
                                                        </InputGroupText>
                                                        <Input
                                                            type="text"
                                                            placeholder={
                                                                tableMode === "assets"
                                                                    ? "Search by asset name, category, location, condition, barcode, serial, or valuation reference..."
                                                                    : "Search by reference number (e.g., TD-ASST-ARUCC-2026-8TX8Q), project, client, valuer, or asset..."
                                                            }
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            style={{ borderLeft: "none" }}
                                                        />
                                                        {searchTerm && (
                                                            <InputGroupText
                                                                style={{
                                                                    backgroundColor: "white",
                                                                    cursor: "pointer",
                                                                    borderLeft: "none"
                                                                }}
                                                                onClick={() => setSearchTerm("")}
                                                            >
                                                                <i className="ri-close-line"></i>
                                                            </InputGroupText>
                                                        )}
                                                    </InputGroup>
                                                </Col>

                                                {tableMode !== "assets" && (
                                                    <Col md={4} className="d-flex justify-content-end gap-2">
                                                        <Button
                                                            onClick={handleApplyFilters}
                                                            style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                                                        >
                                                            Apply
                                                        </Button>
                                                        <Button
                                                            color="secondary"
                                                            onClick={handleResetFilters}
                                                        >
                                                            Reset
                                                        </Button>
                                                    </Col>
                                                )}
                                            </Row>
                                        )}
                                    </Form>

                                    {/* Search Results Info */}
                                    {!isDataReviewAssetMode && searchTerm.trim() && filteredValuations.length > 0 && (
                                        <div className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                                            <i className="ri-information-line me-1"></i>
                                            Showing {filteredValuations.length} of {valuations.length} {tableMode === "assets" ? "assets" : "valuations"}
                                        </div>
                                    )}

                                    {/* Table */}
                                    {loading ? (
                                        <div>Loading...</div>
                                    ) : error ? (
                                        <div>Error fetching valuations: {error.message}</div>
                                    ) : (
                                        <div
                                            className={`table-responsive table-card mt-3 mb-1 ${showReviewTabs && tableMode === "assets" ? "review-tables-wide-wrapper" : ""}`}
                                        >
                                            <table
                                                className={`table align-middle table-nowrap ${showReviewTabs && tableMode === "assets" ? "review-tables-wide" : ""}`}
                                                id="valuationTable"
                                            >
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        {tableMode === "assets" ? (
                                                            <>
                                                                <th>Asset Name</th>
                                                                <th>Category</th>
                                                                {showHasSiteWorkColumn && <th>Has Site Work</th>}
                                                                {showOdoColumn && <th>Milage</th>}
                                                                {showBuildingCountColumn && <th>No. of Buildings</th>}
                                                                <th>Barcode</th>
                                                                <th>Quantity</th>
                                                                <th>Captured By (Valuer)</th>
                                                                <th>Location</th>
                                                                <th>Condition</th>
                                                                <th>Review Status</th>
                                                                {showReviewRemarkColumn && <th>Review Remark</th>}
                                                                {showDataReviewerColumn && <th>Reviewed By</th>}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <th>Reference</th>
                                                                <th>Project</th>
                                                                <th>Client</th>
                                                                <th>Valuer</th>
                                                                <th>Status</th>
                                                                <th>Valuation Date</th>
                                                            </>
                                                        )}
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredValuations.length > 0 ? (
                                                        filteredValuations.map((v, idx) => (
                                                            <tr key={v.id}>
                                                                <td>
                                                                    {tableMode === "assets"
                                                                        ? idx + 1
                                                                        : paginationInfo.pageNumber *
                                                                            paginationInfo.pageSize +
                                                                            idx +
                                                                            1}
                                                                </td>
                                                                {tableMode === "assets" ? (
                                                                    <>
                                                                <td>{v.assetName}</td>
                                                                <td>{v.categoryCode}</td>
                                                                {showHasSiteWorkColumn && (
                                                                    <td>
                                                                        {String(v?.categoryCode || v?.category_code || "").toUpperCase() === "BLDG"
                                                                            ? (v.raw?.has_site_work ?? v.raw?.hasSiteWork ? "Yes" : "No")
                                                                            : "-"}
                                                                    </td>
                                                                )}
                                                                {showOdoColumn && (
                                                                    <td>
                                                                        {["MACH", "VEH"].includes(String(v?.categoryCode || v?.category_code || "").toUpperCase())
                                                                            ? renderCellValue(v.odo)
                                                                            : "-"}
                                                                    </td>
                                                                )}
                                                                {showBuildingCountColumn && (
                                                                    <td>
                                                                        {String(v?.categoryCode || v?.category_code || "").toUpperCase() === "BLDG"
                                                                            ? renderCellValue(v.raw?.number_of_buildings ?? v.raw?.numberOfBuildings ?? v.number_of_buildings ?? v.numberOfBuildings)
                                                                            : "-"}
                                                                    </td>
                                                                )}
                                                                <td>{renderCellValue(v.barcode)}</td>
                                                                <td>{renderCellValue(v.quantity)}</td>
                                                                <td>{v.valuerName || "-"}</td>
                                                                <td>{v.location}</td>
                                                                <td>{v.condition}</td>
                                                                        <td>
                                                                            <span className="badge" style={{
                                                                                backgroundColor:
                                                                                    v.reviewed === "Reviewed"
                                                                                        ? "#28a745"
                                                                                        : v.reviewed === "Review Incomplete"
                                                                                            ? "#fd7e14"
                                                                                            : "#6c757d",
                                                                                color: "white"
                                                                            }}>
                                                                                {v.reviewed}
                                                                            </span>
                                                                        </td>
                                                                {showReviewRemarkColumn && <td>{v.reviewNotCompletedRemark || "-"}</td>}
                                                                {showDataReviewerColumn && <td>{v.dataReviewerName || "-"}</td>}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <td>{v.valuationRef}</td>
                                                                        <td>{v.projectName}</td>
                                                                        <td>{v.clientName}</td>
                                                                        <td>{v.valuerName}</td>
                                                                        <td>
                                                                            <span className="badge" style={{
                                                                                backgroundColor:
                                                                                    v.status === 'SUBMITTED' ? BRAND_BLUE :
                                                                                        v.status === 'DRAFT' ? '#ffc107' :
                                                                                            v.status === 'APPROVED' ? '#28a745' :
                                                                                                v.status === 'REJECTED' ? '#dc3545' :
                                                                                                    '#6c757d',
                                                                                color: "white"
                                                                            }}>
                                                                                {v.status}
                                                                            </span>
                                                                        </td>
                                                                        <td>{v.valuationDate}</td>
                                                                    </>
                                                                )}
                                                                <td>
                                                                    <div className="btn-group" role="group">
                                                                        <Button
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                openAssetEditModal(v);
                                                                            }}
                                                                            className="btn btn-primary btn-sm"
                                                                            style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                                                                            title="Edit"
                                                                        >
                                                                            <i className="ri-edit-box-line" />
                                                                        </Button>
                                                                        {isDataReviewAssetMode && (
                                                                            <Button
                                                                                color="danger"
                                                                                size="sm"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    handleDeleteDataReviewAsset(v);
                                                                                }}
                                                                                disabled={String(deletingAssetId) === String(v.id)}
                                                                                title="Remove asset"
                                                                            >
                                                                                <i className={String(deletingAssetId) === String(v.id) ? "ri-loader-4-line" : "ri-delete-bin-line"} />
                                                                            </Button>
                                                                        )}
                                                                        {!showEditOnlyAction && (
                                                                            <Button
                                                                                style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                                                                                size="sm"
                                                                                onClick={() => {
                                                                                    setSelectedValuation(v.raw);
                                                                                    setModalOpen(true);
                                                                                }}
                                                                                title="View Details"
                                                                            >
                                                                                <i className="ri-eye-line" />
                                                                            </Button>
                                                                        )}
                                                                        {tableMode === "assets" &&
                                                                            (!showEditOnlyAction || v.reviewed !== "Reviewed") && (
                                                                            <Button
                                                                                color="danger"
                                                                                size="sm"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    handleDeleteAssetRow(v.id);
                                                                                }}
                                                                                title="Delete asset"
                                                                            >
                                                                                <i className="ri-delete-bin-line" />
                                                                            </Button>
                                                                        )}
                                                                        {tableMode !== "assets" &&
                                                                            !showEditOnlyAction &&
                                                                            String(v.status || "").toUpperCase() === "DRAFT" && (
                                                                            <Button
                                                                                color="danger"
                                                                                size="sm"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    handleDeleteValuation(v);
                                                                                }}
                                                                                title="Delete draft valuation"
                                                                            >
                                                                                <i className="ri-delete-bin-line" />
                                                                            </Button>
                                                                        )}
                                                                        {tableMode === "assets" && isIncompleteTab && (
                                                                            <Button
                                                                                color="warning"
                                                                                size="sm"
                                                                                onClick={async (e) => {
                                                                                    e.preventDefault();
                                                                                    const confirmed = await Swal.fire({
                                                                                        title: "Move to Unreviewed?",
                                                                                        text: "This will remove review-incomplete status and move the asset to Un Reviewed list.",
                                                                                        icon: "question",
                                                                                        showCancelButton: true,
                                                                                        confirmButtonText: "Move",
                                                                                        cancelButtonText: "Cancel",
                                                                                        confirmButtonColor: BRAND_BLUE,
                                                                                    });
                                                                                    if (!confirmed.isConfirmed) return;
                                                                                    try {
                                                                                        await unreviewAssetById(v.id);
                                                                                    } catch (error) {
                                                                                        console.error("Failed to move asset to unreviewed:", error);
                                                                                        enqueueSnackbar("Failed to move asset to unreviewed.", { variant: "error" });
                                                                                    }
                                                                                }}
                                                                                title="Move to Unreviewed"
                                                                            >
                                                                                <i className="ri-arrow-go-back-line" />
                                                                            </Button>
                                                                        )}
                                                                        {tableMode === "assets" && isRemarkIssuesTab && (
                                                                            <Button
                                                                                color="info"
                                                                                size="sm"
                                                                                onClick={async (e) => {
                                                                                    e.preventDefault();
                                                                                    const confirmed = await Swal.fire({
                                                                                        title: "Remove Remark Issue?",
                                                                                        text: "This will remove issue keywords and keep the asset under Review Incomplete.",
                                                                                        icon: "question",
                                                                                        showCancelButton: true,
                                                                                        confirmButtonText: "Remove Issue",
                                                                                        cancelButtonText: "Cancel",
                                                                                        confirmButtonColor: BRAND_BLUE,
                                                                                    });
                                                                                    if (!confirmed.isConfirmed) return;
                                                                                    try {
                                                                                        await clearReviewRemarkIssueByAssetId(v.id);
                                                                                    } catch (error) {
                                                                                        console.error("Failed to remove remark issue:", error);
                                                                                        enqueueSnackbar("Failed to remove remark issue.", { variant: "error" });
                                                                                    }
                                                                                }}
                                                                                title="Remove Remark Issue"
                                                                            >
                                                                                <i className="ri-eraser-line" />
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={tableMode === "assets" ? ((showHasSiteWorkColumn ? 1 : 0) + (showOdoColumn ? 1 : 0) + (showBuildingCountColumn ? 1 : 0) + (showReviewRemarkColumn ? 1 : 0) + (showDataReviewerColumn ? 1 : 0) + 9) : 8} className="text-center">
                                                                {searchTerm.trim() 
                                                                    ? `No ${tableMode === "assets" ? "assets" : "valuations"} found matching "${searchTerm}"`
                                                                    : "No records found."
                                                                }
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    <Pagination
                                        paginationInfo={displayPaginationInfo}
                                        handleNextPage={handleNextPage}
                                        handlePreviousPage={handlePreviousPage}
                                        onPageChange={handlePageChange}
                                        onPageSizeChange={handlePageSizeChange}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
                <ReviewWizardModal
                    isOpen={reviewModalOpen}
                    onClose={closeReviewModal}
                    title={`Review data for ${reviewData?.valuation?.valuation_ref || "this valuation"}`}
                    activeStepAssetRemark={activeStepAsset && isReviewNotCompleted(activeStepAsset) ? getReviewNotCompletedRemark(activeStepAsset) : ""}
                    isLoading={reviewLoading}
                    steps={reviewSteps}
                    activeIndex={reviewStepIndex}
                    setActiveIndex={setReviewStepIndex}
                    progressWidth={progressWidth}
                    renderStepContent={renderStepContent}
                    isFirstStep={isFirstReviewStep}
                    hideNextButton={hideFinishForReviewedAsset}
                    nextButtonLabel={nextButtonLabel}
                    onBack={goToPreviousStep}
                    onNext={goToNextStep}
                    backDisabled={reviewLoading || (editingStepKey === activeStep?.key && !!editableStepConfig[activeStep.key])}
                    nextDisabled={(isLastReviewStep && !isPhotoFinishStep) || finishingReview || reviewLoading || (editingStepKey === activeStep?.key && !!editableStepConfig[activeStep.key])}
                />
                <ConfirmDialog />
                <AssetValuationDetailsModal
                    isOpen={modalOpen}
                    toggle={toggleModal}
                    valuation={selectedValuation}
                />
                <BarcodePdfModal
                    isOpen={barcodePdfModalOpen}
                    onToggle={() => setBarcodePdfModalOpen(!barcodePdfModalOpen)}
                    onClose={() => setBarcodePdfModalOpen(false)}
                    isLoading={loadingBarcodePdfs}
                    categoryEntries={barcodePdfCategoryEntries}
                />

                <Modal
                    size="xl"
                    isOpen={editModalOpen}
                    toggle={closeEditModal}
                    scrollable
                >
                    <ModalHeader toggle={closeEditModal}>Asset Fields</ModalHeader>
                    <ModalBody>
                        {editModalLoading ? (
                            <div className="text-center py-4">
                                <Spinner color="primary" />
                            </div>
                        ) : !editAssets.length ? (
                            <p className="text-muted mb-0">
                                {tableMode === "assets"
                                    ? "No asset fields available."
                                    : "No assets found for this valuation."}
                            </p>
                        ) : (
                            <>
                                {editAssets.length > 1 && (
                                    <FormGroup className="mb-3">
                                        <Label for="editAssetPicker">Select Asset</Label>
                                        <Input
                                            id="editAssetPicker"
                                            type="select"
                                            value={selectedEditAssetId}
                                            onChange={(e) => setSelectedEditAssetId(e.target.value)}
                                        >
                                            {editAssets.map((asset, index) => (
                                                <option key={asset.id || `${asset.name || "asset"}-${index}`} value={asset.id}>
                                                    {(resolveAssetNameDisplay(asset, "Asset") || "Asset")} ({getCategoryCode(asset) || "N/A"})
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                )}
                                {!isEditAssetBuildingCategory && (
                                    <div className="mb-4">
                                        <div className="fw-semibold mb-2">Asset Images</div>
                                        <AssetPhotoGallery
                                            assetId={selectedEditAssetDraft?.id}
                                            photos={editAssetPhotos}
                                            loading={editAssetPhotosLoading}
                                            updatingPhotoId={updatingPhotoId}
                                            onCreatePhoto={handleCreateAssetPhoto}
                                            onReplacePhoto={handleReplaceAssetPhoto}
                                            onOpenPreview={openImagePreview}
                                        />
                                    </div>
                                )}
                                <Row className="g-3 mb-3">
                                    <Col md={6}>
                                        <FormGroup className="mb-0">
                                            <Label>Valuation Reference</Label>
                                            <Input
                                                type="text"
                                                value={
                                                    selectedEditAssetDraft?.asset_valuation?.valuation_ref ||
                                                    selectedEditAssetDraft?.valuation?.valuation_ref ||
                                                    selectedEditAssetDraft?.valuation_ref ||
                                                    selectedEditAssetDraft?.asset_valuation?.valuation_number ||
                                                    selectedEditAssetDraft?.valuation_number ||
                                                    ""
                                                }
                                                readOnly
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup className="mb-0">
                                            <Label>Valuer</Label>
                                            <Input
                                                type="text"
                                                value={selectedEditAssetValuerName || ""}
                                                readOnly
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                {selectedEditAssetDraft && isReviewNotCompleted(selectedEditAssetDraft) && (
                                    <div className="alert alert-warning mb-3" role="alert">
                                        <strong>Review not completed remark:</strong> {getReviewNotCompletedRemark(selectedEditAssetDraft)}
                                    </div>
                                )}
                                {isEditAssetBuildingCategory && selectedEditAssetDraft ? (
                                    <>
                                        <BuildingEditStepper
                                            steps={BUILDING_EDIT_STEPS}
                                            activeIndex={editBuildingStepIndex}
                                            showSiteWorks={selectedEditAssetHasSiteWork}
                                            onStepClick={setEditBuildingStepIndex}
                                        />
                                        
                                        {editBuildingStepIndex > highestSavedStepIndex && (
                                            <div className="alert alert-info mt-3 mb-3 d-flex align-items-center">
                                                <i className="ri-information-line me-2 fs-5"></i>
                                                <div>
                                                    <strong>Viewing Mode:</strong> To edit this section, you must first complete and save the preceding steps.
                                                </div>
                                            </div>
                                        )}

                                        {editBuildingStepIndex === 0 ? (
                                            <AssetDetailForm
                                                key={selectedEditAssetDraft.id || "edit-asset-form"}
                                                asset={selectedEditAssetDraft}
                                                projectCouncilId={
                                                    editModalCouncilId || null
                                                }
                                                isLocked={editBuildingStepIndex > highestSavedStepIndex}
                                                onChange={(updated) => setSelectedEditAssetDraft(updated)}
                                            />
                                        ) : null}
                                        {editBuildingStepIndex === 1 ? (
                                            <div style={editBuildingStepIndex > highestSavedStepIndex ? { pointerEvents: "none", opacity: 0.7 } : {}}>
                                                {loadingSecurityOverview ? (
                                                    <div className="text-center py-3">
                                                        <Spinner size="sm" color="primary" className="me-2" />
                                                        <span className="text-muted">Loading security overview...</span>
                                                    </div>
                                                ) : (
                                                    <Row className="g-3">
                                                        {[
                                                            { key: "accessibility", label: "Accessibility" },
                                                            { key: "street", label: "Street" },
                                                            { key: "landmark", label: "Landmark" },
                                                            { key: "distance_from_landmark", label: "Distance from Landmark" },
                                                            { key: "neighborhood_type", label: "Neighborhood Type" },
                                                            { key: "neighborhood_density", label: "Neighborhood Density" },
                                                            { key: "amenities", label: "Amenities" },
                                                            {
                                                                key: "income_level",
                                                                label: "Income Level",
                                                                type: "select",
                                                                options: INCOME_LEVEL_OPTIONS,
                                                            },
                                                            {
                                                                key: "road_type",
                                                                label: "Road Type",
                                                                type: "select",
                                                                options: ROAD_TYPE_OPTIONS,
                                                            },
                                                            { key: "distance_from_main_road", label: "Distance from Main Road" },
                                                        ].map((field) => (
                                                            <Col md={6} key={field.key}>
                                                                <FormGroup className="mb-0">
                                                                    <Label>{field.label}</Label>
                                                                    {field.type === "select" ? (
                                                                        <Input
                                                                            type="select"
                                                                            value={securityOverviewDraft?.[field.key] || ""}
                                                                            onChange={(e) =>
                                                                                setSecurityOverviewDraft((prev) => ({
                                                                                    ...prev,
                                                                                    [field.key]: e.target.value,
                                                                                }))
                                                                            }
                                                                            disabled={editBuildingStepIndex > highestSavedStepIndex}
                                                                        >
                                                                            <option value="">Select {field.label}</option>
                                                                            {field.options.map((optionValue) => (
                                                                                <option key={`${field.key}-${optionValue}`} value={optionValue}>
                                                                                    {optionValue}
                                                                                </option>
                                                                            ))}
                                                                        </Input>
                                                                    ) : (
                                                                        <Input
                                                                            type="text"
                                                                            value={securityOverviewDraft?.[field.key] || ""}
                                                                            onChange={(e) =>
                                                                                setSecurityOverviewDraft((prev) => ({
                                                                                    ...prev,
                                                                                    [field.key]: e.target.value,
                                                                                }))
                                                                            }
                                                                            disabled={editBuildingStepIndex > highestSavedStepIndex}
                                                                        />
                                                                    )}
                                                                </FormGroup>
                                                            </Col>
                                                        ))}
                                                    </Row>
                                                )}
                                            </div>
                                        ) : null}
                                        {editBuildingStepIndex === 2 ? (
                                            <div style={editBuildingStepIndex > highestSavedStepIndex ? { pointerEvents: "none", opacity: 0.7 } : {}}>
                                                <Row className="g-3 mb-2">
                                                    <Col md={4}>
                                                        <BuildingSummaryCard title="Expected Buildings" value={activeBuildingSlotNumbers.length} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <BuildingSummaryCard title="Captured Buildings" value={capturedBuildingCount} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <BuildingSummaryCard title="Remaining" value={remainingBuildingSlots} />
                                                    </Col>
                                                </Row>
                                                {loadingEditBuildings ? (
                                                    <div className="text-center py-3">
                                                        <Spinner size="sm" color="primary" className="me-2" />
                                                        <span className="text-muted">Loading buildings...</span>
                                                    </div>
                                                ) : expectedBuildingCount > 0 ? (
                                                    <div className="row g-3">
                                                        {stepBuildingRows.map((row) => {
                                                            const draft =
                                                                buildingDrafts[row.serial] ||
                                                                createBuildingDraftFromEntity(row.building, row.serial);
                                                            const selectedBuildingType = buildingTypes.find(
                                                                (t) => String(t.id || "") === String(draft.building_type_id || "")
                                                            );
                                                            const buildingTypeLabel =
                                                                selectedBuildingType?.name ||
                                                                selectedBuildingType?.label ||
                                                                selectedBuildingType?.code ||
                                                                "";
                                                            const displayBuildingSource = {
                                                                ...(row.building || {}),
                                                                ...draft,
                                                                other_building_name:
                                                                    draft.other_building_name ||
                                                                    draft.otherBuildingName ||
                                                                    row.building?.other_building_name ||
                                                                    row.building?.otherBuildingName,
                                                            };
                                                            const buildingDisplayName =
                                                                resolveBuildingNameDisplay(displayBuildingSource, "Not Specified");
                                                            const buildingNameSelectValue = isOtherSelection(draft.building_name)
                                                                ? "OTHERS"
                                                                : draft.building_name || "";
                                                            const hasCurrentBuildingNameOption =
                                                                !buildingNameSelectValue ||
                                                                buildingNameSelectValue === "OTHERS" ||
                                                                institutionBuildingOptions.some(
                                                                    (building) => building.name === buildingNameSelectValue
                                                                );
                                                            const conditionLabel =
                                                                draft.observed_condition
                                                                    ? resolveObservedConditionLabel(draft.observed_condition)
                                                                    : "Condition not set";
                                                            if (!selectedBuildingDetailSlot) {
                                                                return (
                                                                    <div key={`building-list-slot-${row.serial}`} className="col-12">
                                                                        <BuildingListCard
                                                                            serial={row.serial}
                                                                            name={buildingDisplayName}
                                                                            isPrimary={draft.is_primary}
                                                                            conditionLabel={conditionLabel}
                                                                            hasCondition={!!draft.observed_condition}
                                                                            isSaved={!!draft.id}
                                                                            deleting={!!draft.id && String(deletingBuildingId) === String(draft.id)}
                                                                            onDelete={() =>
                                                                                draft.id
                                                                                    ? handleDeleteBuilding(draft, row.serial)
                                                                                    : handleDeletePendingBuildingSlot(row.serial)
                                                                            }
                                                                            onViewDetails={() => {
                                                                                    setSelectedBuildingDetailSlot(row.serial);
                                                                                    setBuildingStepTabForSlot(row.serial, "form");
                                                                            }}
                                                                        />
                                                                    </div>
                                                                );
                                                            }
                                                            if (selectedBuildingDetailSlot !== row.serial) {
                                                                return null;
                                                            }
                                                            const saveBuildingButtonLabel = buildingTypeLabel
                                                                ? `Save ${buildingTypeLabel}`
                                                                : `Save Building ${row.serial}`;
                                                            const saveConstructionButtonLabel = buildingTypeLabel
                                                                ? `Save Construction for ${buildingTypeLabel}`
                                                                : `Save Construction ${row.serial}`;
                                                            const hasExisting = !!draft.id;
                                                            const previewUrl =
                                                                draft.sketch_image_preview ||
                                                                draft.sketch_image_url ||
                                                                "";
                                                            const currentStep = getBuildingStepTab(row.serial);
                                                            const buildingId =
                                                                draft.id ||
                                                                editBuildingsBySlot[row.serial]?.id ||
                                                                "";

                                                            return (
                                                                <div key={`building-slot-${row.serial}`} className="col-12">
                                                                    <div className="building-slot-card">
                                                                        <BuildingDetailHeader
                                                                            serial={row.serial}
                                                                            name={buildingDisplayName}
                                                                            isPrimary={draft.is_primary}
                                                                            conditionLabel={conditionLabel}
                                                                            onBack={() => setSelectedBuildingDetailSlot(null)}
                                                                            deleting={!!buildingId && String(deletingBuildingId) === String(buildingId)}
                                                                            onDelete={
                                                                                buildingId
                                                                                    ? () => handleDeleteBuilding(draft, row.serial)
                                                                                    : () => handleDeletePendingBuildingSlot(row.serial)
                                                                            }
                                                                        />
                                                                        <div className="review-tabs-modern mb-3">
                                                                            <button
                                                                                type="button"
                                                                                className={`review-tab-btn ${currentStep === "form" ? "active" : ""}`}
                                                                                onClick={() => setBuildingStepTabForSlot(row.serial, "form")}
                                                                            >
                                                                                Step 2.1.2a Building Form
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className={`review-tab-btn ${currentStep === "construction" ? "active" : ""}`}
                                                                                onClick={async () => {
                                                                                    setBuildingStepTabForSlot(row.serial, "construction");
                                                                                    if (buildingId && !constructionDrafts[row.serial]) {
                                                                                        await loadConstructionForBuilding(row.serial, buildingId);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                Step 2.1.2b Building Construction
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className={`review-tab-btn ${currentStep === "images" ? "active" : ""}`}
                                                                                onClick={async () => {
                                                                                    setBuildingStepTabForSlot(row.serial, "images");
                                                                                    if (buildingId) {
                                                                                        await ensureBuildingPhotosLoaded(String(buildingId));
                                                                                    }
                                                                                }}
                                                                            >
                                                                                Step 2.1.2c Building Images
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className={`review-tab-btn ${currentStep === "accommodation" ? "active" : ""}`}
                                                                                onClick={async () => {
                                                                                    setBuildingStepTabForSlot(row.serial, "accommodation");
                                                                                    if (buildingId) {
                                                                                        const floorCodes = getBuildingFloorCodes(draft);
                                                                                        const defaultFloor = floorCodes[0] || "GROUND_FLOOR";
                                                                                        setAccommodationDrafts((prev) => ({
                                                                                            ...prev,
                                                                                            [row.serial]: prev[row.serial] || createEmptyAccommodationDraft(defaultFloor),
                                                                                        }));
                                                                                        await ensureBuildingAccommodationsLoaded(String(buildingId));
                                                                                    }
                                                                                }}
                                                                            >
                                                                                Step 2.1.2d Building Accommodation
                                                                            </button>
                                                                        </div>
                                                                        {currentStep === "form" ? (
                                                                        <>
                                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                                            <div className="building-slot-title">
                                                                                Step 2.1.2a - Building {row.serial}
                                                                            </div>
                                                                            <span className={`badge ${hasExisting ? "bg-success" : "bg-warning text-dark"}`}>
                                                                                {hasExisting ? "Saved" : "Pending"}
                                                                            </span>
                                                                        </div>

                                                                        <Row className="g-3">
                                                                            <Col md={3}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label className="d-block" htmlFor={`is-primary-${row.serial}`}>
                                                                                        Is Primary
                                                                                    </Label>
                                                                                    <div className="d-flex align-items-center gap-2 mt-2">
                                                                                        <Switch
                                                                                            id={`is-primary-${row.serial}`}
                                                                                            checked={!!draft.is_primary}
                                                                                            onChange={(checked) =>
                                                                                                handlePrimaryBuildingToggle(
                                                                                                    row.serial,
                                                                                                    checked
                                                                                                )
                                                                                            }
                                                                                            uncheckedIcon={false}
                                                                                            checkedIcon={false}
                                                                                            onColor={BRAND_BLUE}
                                                                                            offColor="#adb5bd"
                                                                                            onHandleColor="#ffffff"
                                                                                            offHandleColor="#ffffff"
                                                                                            height={20}
                                                                                            width={42}
                                                                                        />
                                                                                        <span className="small text-muted">
                                                                                            {draft.is_primary ? "Primary building" : "Not primary"}
                                                                                        </span>
                                                                                    </div>
                                                                                </FormGroup>
                                                                            </Col>
                                                                            <Col md={3}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label>Building Type</Label>
                                                                                    <Input
                                                                                        type="select"
                                                                                        value={draft.building_type_id || ""}
                                                                                        onChange={(e) =>
                                                                                            setBuildingDrafts((prev) => ({
                                                                                                ...prev,
                                                                                                [row.serial]: {
                                                                                                    ...draft,
                                                                                                    building_type_id: e.target.value,
                                                                                                },
                                                                                            }))
                                                                                        }
                                                                                    >
                                                                                        <option value="">Select Building Type</option>
                                                                                        {buildingTypes.map((t) => (
                                                                                            <option key={t.id || t.code} value={t.id}>
                                                                                                {t.name || t.label || t.code}
                                                                                            </option>
                                                                                        ))}
                                                                                    </Input>
                                                                                </FormGroup>
                                                                            </Col>
                                                                            <Col md={3}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label>Building Name</Label>
                                                                                    <Input
                                                                                        type="select"
                                                                                        required
                                                                                        value={buildingNameSelectValue}
                                                                                        onChange={(e) => {
                                                                                            const value = e.target.value;
                                                                                            setBuildingDrafts((prev) => ({
                                                                                                ...prev,
                                                                                                [row.serial]: {
                                                                                                    ...draft,
                                                                                                    building_name: value,
                                                                                                    ...(!isOtherSelection(value)
                                                                                                        ? { other_building_name: "", other_name: "" }
                                                                                                        : {}),
                                                                                                },
                                                                                            }));
                                                                                        }}
                                                                                    >
                                                                                        <option value="">Select Building</option>
                                                                                        {institutionBuildingOptions.map((building) => (
                                                                                            <option key={building.id || building.name} value={building.name}>
                                                                                                {building.name}
                                                                                            </option>
                                                                                        ))}
                                                                                        {!hasCurrentBuildingNameOption ? (
                                                                                            <option value={buildingNameSelectValue}>
                                                                                                {buildingNameSelectValue}
                                                                                            </option>
                                                                                        ) : null}
                                                                                        <option value="OTHERS">OTHERS</option>
                                                                                    </Input>
                                                                                </FormGroup>
                                                                            </Col>
                                                                            {isOtherSelection(draft.building_name) ? (
                                                                                <Col md={3}>
                                                                                    <FormGroup className="mb-0">
                                                                                        <Label>Other Building Name</Label>
                                                                                    <Input
                                                                                        type="text"
                                                                                        required
                                                                                        value={draft.other_building_name || ""}
                                                                                            onChange={(e) =>
                                                                                                setBuildingDrafts((prev) => ({
                                                                                                    ...prev,
                                                                                                    [row.serial]: {
                                                                                                        ...draft,
                                                                                                        other_building_name: e.target.value,
                                                                                                    },
                                                                                                }))
                                                                                            }
                                                                                        />
                                                                                    </FormGroup>
                                                                                </Col>
                                                                            ) : null}
                                                                            <Col md={3}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label>Building Number</Label>
                                                                                    <Input
                                                                                        type="text"
                                                                                        value={draft.building_number || ""}
                                                                                        onChange={(e) =>
                                                                                            setBuildingDrafts((prev) => ({
                                                                                                ...prev,
                                                                                                [row.serial]: {
                                                                                                    ...draft,
                                                                                                    building_number: e.target.value,
                                                                                                },
                                                                                            }))
                                                                                        }
                                                                                    />
                                                                                </FormGroup>
                                                                            </Col>

                                                                            <Col md={3}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label>No of Floors</Label>
                                                                                    <Input
                                                                                        type="number"
                                                                                        min="1"
                                                                                        value={draft.no_of_floors || ""}
                                                                                        onChange={(e) =>
                                                                                            setBuildingDrafts((prev) => ({
                                                                                                ...prev,
                                                                                                [row.serial]: {
                                                                                                    ...draft,
                                                                                                    no_of_floors: e.target.value,
                                                                                                },
                                                                                            }))
                                                                                        }
                                                                                    />
                                                                                </FormGroup>
                                                                            </Col>
                                                                            <Col md={3}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label>Year Built</Label>
                                                                                    <Input
                                                                                        type="select"
                                                                                        value={draft.year_built || ""}
                                                                                        onChange={(e) =>
                                                                                            setBuildingDrafts((prev) => ({
                                                                                                ...prev,
                                                                                                [row.serial]: {
                                                                                                    ...draft,
                                                                                                    year_built: e.target.value,
                                                                                                },
                                                                                            }))
                                                                                        }
                                                                                    >
                                                                                        <option value="">Select Year</option>
                                                                                        {YEAR_OPTIONS.map((year) => (
                                                                                            <option key={year} value={year}>{year}</option>
                                                                                        ))}
                                                                                    </Input>
                                                                                </FormGroup>
                                                                            </Col>
                                                                            <Col md={3}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label>Building Measurement</Label>
                                                                                    <Input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        value={draft.building_measurement || ""}
                                                                                        onChange={(e) =>
                                                                                            setBuildingDrafts((prev) => ({
                                                                                                ...prev,
                                                                                                [row.serial]: {
                                                                                                    ...draft,
                                                                                                    building_measurement: e.target.value,
                                                                                                },
                                                                                            }))
                                                                                        }
                                                                                    />
                                                                                </FormGroup>
                                                                            </Col>
                                                                            <Col md={3}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label>Construction Rate/Sqm (Currency)</Label>
                                                                                    <Input
                                                                                        type="text"
                                                                                        value={formatInputWithCommas(draft.construction_rate_per_sqm || "")}
                                                                                        onChange={(e) =>
                                                                                            setBuildingDrafts((prev) => ({
                                                                                                ...prev,
                                                                                                [row.serial]: {
                                                                                                    ...draft,
                                                                                                    construction_rate_per_sqm: formatInputWithCommas(e.target.value),
                                                                                                },
                                                                                            }))
                                                                                        }
                                                                                    />
                                                                                </FormGroup>
                                                                            </Col>
                                                                            <Col md={3}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label>Completion Rate (%)</Label>
                                                                                    <Input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        max="100"
                                                                                        value={draft.completion_rate || ""}
                                                                                        onChange={(e) =>
                                                                                            setBuildingDrafts((prev) => ({
                                                                                                ...prev,
                                                                                                [row.serial]: {
                                                                                                    ...draft,
                                                                                                    completion_rate: e.target.value,
                                                                                                },
                                                                                            }))
                                                                                        }
                                                                                    />
                                                                                </FormGroup>
                                                                            </Col>

                                                                            <Col md={6}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label>Use Status</Label>
                                                                                    <div className="use-status-group">
                                                                                        {BUILDING_USE_STATUS_OPTIONS.map((option) => (
                                                                                            <button
                                                                                                key={`${row.serial}-${option.value}`}
                                                                                                type="button"
                                                                                                className={`use-status-chip ${draft.use_status === option.value ? "active" : ""}`}
                                                                                                onClick={() =>
                                                                                                    setBuildingDrafts((prev) => ({
                                                                                                        ...prev,
                                                                                                        [row.serial]: {
                                                                                                            ...draft,
                                                                                                            use_status: option.value,
                                                                                                        },
                                                                                                    }))
                                                                                                }
                                                                                            >
                                                                                                {option.label}
                                                                                            </button>
                                                                                        ))}
                                                                                    </div>
                                                                                </FormGroup>
                                                                            </Col>
                                                                            <Col md={3}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label>Observed Condition</Label>
                                                                                    <Input
                                                                                        type="select"
                                                                                        value={draft.observed_condition || ""}
                                                                                        onChange={(e) =>
                                                                                            setBuildingDrafts((prev) => ({
                                                                                                ...prev,
                                                                                                [row.serial]: {
                                                                                                    ...draft,
                                                                                                    observed_condition: e.target.value,
                                                                                                },
                                                                                            }))
                                                                                        }
                                                                                    >
                                                                                        <option value="">Select Observed Condition</option>
                                                                                        {buildingObservedConditions.map((c) => (
                                                                                            <option key={c.id || c.code} value={c.id || c.code}>
                                                                                                {c.name || c.label || c.code}
                                                                                            </option>
                                                                                        ))}
                                                                                    </Input>
                                                                                </FormGroup>
                                                                            </Col>
                                                                            <Col md={3}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label>Useful Life Span (Years)</Label>
                                                                                    <Input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        value={draft.useful_life_span_years || ""}
                                                                                        onChange={(e) =>
                                                                                            setBuildingDrafts((prev) => ({
                                                                                                ...prev,
                                                                                                [row.serial]: {
                                                                                                    ...draft,
                                                                                                    useful_life_span_years: e.target.value,
                                                                                                },
                                                                                            }))
                                                                                        }
                                                                                    />
                                                                                </FormGroup>
                                                                            </Col>

                                                                            <Col md={6}>
                                                                                <FormGroup className="mb-0">
                                                                                    <Label>Sketch Image</Label>
                                                                                    <div className="asset-photo-card-wrap">
                                                                                        <input
                                                                                            id={`sketch-upload-${row.serial}`}
                                                                                            type="file"
                                                                                            accept="image/*"
                                                                                            className="d-none"
                                                                                            onChange={(e) => {
                                                                                                const file = e.target.files?.[0] || null;
                                                                                                if (!file) {
                                                                                                    setBuildingDrafts((prev) => ({
                                                                                                        ...prev,
                                                                                                        [row.serial]: {
                                                                                                            ...draft,
                                                                                                            sketch_image_file: null,
                                                                                                            sketch_image_preview: "",
                                                                                                        },
                                                                                                    }));
                                                                                                    return;
                                                                                                }
                                                                                                const reader = new FileReader();
                                                                                                reader.onload = () => {
                                                                                                    setBuildingDrafts((prev) => ({
                                                                                                        ...prev,
                                                                                                        [row.serial]: {
                                                                                                            ...draft,
                                                                                                            sketch_image_file: file,
                                                                                                            sketch_image_preview:
                                                                                                                typeof reader.result === "string"
                                                                                                                    ? reader.result
                                                                                                                    : "",
                                                                                                        },
                                                                                                    }));
                                                                                                };
                                                                                                reader.readAsDataURL(file);
                                                                                            }}
                                                                                        />
                                                                                        <button
                                                                                            type="button"
                                                                                            className="asset-photo-camera-btn"
                                                                                            onClick={() => {
                                                                                                const input = document.getElementById(`sketch-upload-${row.serial}`);
                                                                                                input?.click();
                                                                                            }}
                                                                                            title="Change sketch image"
                                                                                        >
                                                                                            <i className="ri-camera-line" />
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="asset-photo-card"
                                                                                            onClick={() => previewUrl && openImagePreview(previewUrl, `Sketch ${row.serial}`)}
                                                                                            style={{ cursor: previewUrl ? "zoom-in" : "default" }}
                                                                                            disabled={!previewUrl}
                                                                                        >
                                                                                            {previewUrl ? (
                                                                                                <img
                                                                                                    src={previewUrl}
                                                                                                    alt={`Sketch ${row.serial}`}
                                                                                                    className="asset-photo-card-media"
                                                                                                />
                                                                                            ) : (
                                                                                                <div className="asset-photo-card-media d-flex align-items-center justify-content-center text-muted">
                                                                                                    No sketch image
                                                                                                </div>
                                                                                            )}
                                                                                            <div className="asset-photo-card-body">
                                                                                                <div className="asset-photo-card-title">{`Sketch ${row.serial}`}</div>
                                                                                                <p className="asset-photo-card-subtitle mb-0">
                                                                                                    Tap camera icon to capture/change
                                                                                                </p>
                                                                                            </div>
                                                                                        </button>
                                                                                    </div>
                                                                                </FormGroup>
                                                                            </Col>
                                                                        </Row>

                                                                        <div className="d-flex justify-content-end mt-3">
                                                                            <Button
                                                                                color="primary"
                                                                                size="sm"
                                                                                onClick={() => saveBuildingSlotDraft(row.serial)}
                                                                                disabled={savingBuildingSlot === row.serial}
                                                                            >
                                                                                {savingBuildingSlot === row.serial
                                                                                    ? "Saving..."
                                                                                    : saveBuildingButtonLabel}
                                                                            </Button>
                                                                        </div>
                                                                        </>
                                                                        ) : null}

                                                                        {currentStep === "construction" ? (
                                                                            <div className="mt-3 border-top pt-3">
                                                                                <h6 className="mb-3">
                                                                                    Step 2.1.2b - Building Construction (Building {row.serial})
                                                                                </h6>
                                                                                {(() => {
                                                                                    const cDraft =
                                                                                        constructionDrafts[row.serial] ||
                                                                                        createEmptyConstructionDraft(draft.id || "");
                                                                                    const constructionFields = [
                                                                                        { key: "roof", label: "Roof Type(s)", options: ROOF_OPTIONS, othersKey: "roof_others" },
                                                                                        { key: "ceiling", label: "Ceiling Type(s)", options: CEILING_OPTIONS, othersKey: "ceiling_others" },
                                                                                        { key: "walls", label: "Wall Type(s)", options: WALL_OPTIONS, othersKey: "wall_others" },
                                                                                        { key: "windows", label: "Window Type(s)", options: WINDOW_OPTIONS, othersKey: "window_others" },
                                                                                        { key: "doors", label: "Door Type(s)", options: DOOR_OPTIONS, othersKey: "door_others" },
                                                                                        { key: "floors", label: "Floor Type(s)", options: FLOOR_OPTIONS, othersKey: "floor_others" },
                                                                                    ];

                                                                                    return (
                                                                                        <div>
                                                                                            {constructionFields.map((group) => (
                                                                                                <div key={`${row.serial}-${group.key}`} className="mb-3">
                                                                                                    <div className="fw-semibold mb-2">{group.label}</div>
                                                                                                    <div className="use-status-group">
                                                                                                        {group.options.map((option) => {
                                                                                                            const selected = (cDraft[group.key] || []).includes(option.code);
                                                                                                            return (
                                                                                                                <button
                                                                                                                    type="button"
                                                                                                                    key={`${row.serial}-${group.key}-${option.code}`}
                                                                                                                    className={`use-status-chip ${selected ? "active" : ""}`}
                                                                                                                    onClick={() =>
                                                                                                                        toggleConstructionSelection(
                                                                                                                            row.serial,
                                                                                                                            group.key,
                                                                                                                            option.code
                                                                                                                        )
                                                                                                                    }
                                                                                                                >
                                                                                                                    {option.name}
                                                                                                                </button>
                                                                                                            );
                                                                                                        })}
                                                                                                    </div>
                                                                                                    {(cDraft[group.key] || []).includes("OTHERS") ? (
                                                                                                        <div className="mt-2">
                                                                                                            <Input
                                                                                                                type="text"
                                                                                                                placeholder="Specify Other"
                                                                                                                value={cDraft[group.othersKey] || ""}
                                                                                                                onChange={(e) =>
                                                                                                                    setConstructionDrafts((prev) => ({
                                                                                                                        ...prev,
                                                                                                                        [row.serial]: {
                                                                                                                            ...cDraft,
                                                                                                                            [group.othersKey]: e.target.value,
                                                                                                                        },
                                                                                                                    }))
                                                                                                                }
                                                                                                            />
                                                                                                        </div>
                                                                                                    ) : null}
                                                                                                </div>
                                                                                            ))}
                                                                                            <div className="d-flex justify-content-end">
                                                                                                <Button
                                                                                                    color="success"
                                                                                                    size="sm"
                                                                                                    onClick={() => saveConstructionForSlot(row.serial)}
                                                                                                    disabled={savingConstructionSlot === row.serial}
                                                                                                >
                                                                                                    {savingConstructionSlot === row.serial
                                                                                                        ? "Saving..."
                                                                                                        : saveConstructionButtonLabel}
                                                                                                </Button>
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                })()}
                                                                            </div>
                                                                        ) : null}

                                                                        {currentStep === "images" ? (
                                                                            <div className="mt-3 border-top pt-3">
                                                                                <h6 className="mb-3">
                                                                                    Step 2.1.2c - Building Images (Building {row.serial})
                                                                                </h6>
                                                                                {(() => {
                                                                                    const buildingId =
                                                                                        draft.id ||
                                                                                        editBuildingsBySlot[row.serial]?.id ||
                                                                                        "";
                                                                                    const hasBuildingId = Boolean(buildingId);
                                                                                    const photos = hasBuildingId
                                                                                        ? (buildingPhotosByBuildingId[String(buildingId)] || [])
                                                                                        : [];
                                                                                    const loading = hasBuildingId
                                                                                        && loadingBuildingPhotosForBuildingId === String(buildingId);

                                                                                    return (
                                                                                        <>
                                                                                        <div className="row g-3">
                                                                                            <div className="col-12 col-sm-6 col-md-4 col-xl-3">
                                                                                                <div className="asset-photo-card-wrap h-100">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="asset-photo-camera-btn"
                                                                                                        onClick={() => hasBuildingId && upsertBuildingPhoto(String(buildingId), null, null, row.serial)}
                                                                                                        title="Upload building image"
                                                                                                        disabled={!hasBuildingId || String(updatingPhotoId || "").startsWith(`new-building-${buildingId}-`)}
                                                                                                    >
                                                                                                        <i
                                                                                                            className={
                                                                                                                hasBuildingId && String(updatingPhotoId || "").startsWith(`new-building-${buildingId}-`)
                                                                                                                    ? "ri-loader-4-line"
                                                                                                                    : "ri-camera-line"
                                                                                                            }
                                                                                                        />
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="asset-photo-card"
                                                                                                        onClick={() => hasBuildingId && upsertBuildingPhoto(String(buildingId), null, null, row.serial)}
                                                                                                        disabled={!hasBuildingId}
                                                                                                    >
                                                                                                        <div className="asset-photo-card-media d-flex align-items-center justify-content-center">
                                                                                                            <div className="text-center">
                                                                                                                <i className="ri-camera-line fs-2 d-block mb-2"></i>
                                                                                                                <span className="small">Upload Building Image</span>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                        <div className="asset-photo-card-body">
                                                                                                            <div className="asset-photo-card-title">New Photo</div>
                                                                                                            <p className="asset-photo-card-subtitle mb-0">Choose photo type, then upload</p>
                                                                                                        </div>
                                                                                                    </button>
                                                                                                </div>
                                                                                            </div>

                                                                                            {loading ? (
                                                                                                <div className="col-12 text-muted">Loading building images...</div>
                                                                                            ) : photos.length ? (
                                                                                                photos.map((photo, index) => {
                                                                                                    const imageUrl = photo.photo_url || photo.url || photo.path || "";
                                                                                                    const photoType = photo.photo_type || photo.type || `Image ${index + 1}`;
                                                                                                    const photoInputId = `replace-building-photo-${row.serial}-${photo.id || index}`;
                                                                                                    const photoKey = photo?.id || `${photoType}-${row.serial}-${index}`;
                                                                                                    return (
                                                                                                        <div
                                                                                                            key={photo.id || `${buildingId}-${index}`}
                                                                                                            className="col-12 col-sm-6 col-md-4 col-xl-3"
                                                                                                        >
                                                                                                            <div className="asset-photo-card-wrap h-100">
                                                                                                                <input
                                                                                                                    id={photoInputId}
                                                                                                                    type="file"
                                                                                                                    accept="image/*"
                                                                                                                    className="d-none"
                                                                                                                    onChange={(e) => {
                                                                                                                        const file = e.target.files?.[0];
                                                                                                                        if (file) {
                                                                                                                            upsertBuildingPhoto(String(buildingId), file, photo, row.serial);
                                                                                                                        }
                                                                                                                        e.target.value = "";
                                                                                                                    }}
                                                                                                                />
                                                                                                                <button
                                                                                                                    type="button"
                                                                                                                    className="asset-photo-camera-btn"
                                                                                                                    onClick={(e) => {
                                                                                                                        e.preventDefault();
                                                                                                                        e.stopPropagation();
                                                                                                                        const input = document.getElementById(photoInputId);
                                                                                                                        input?.click();
                                                                                                                    }}
                                                                                                                    title="Change building image"
                                                                                                                    disabled={updatingPhotoId === photoKey}
                                                                                                                >
                                                                                                                    <i className={updatingPhotoId === photoKey ? "ri-loader-4-line" : "ri-camera-line"} />
                                                                                                                </button>
                                                                                                                <button
                                                                                                                    type="button"
                                                                                                                    className="asset-photo-card"
                                                                                                                    onClick={() => openImagePreview(imageUrl, `${photoType} - Building ${row.serial}`)}
                                                                                                                    style={{ cursor: imageUrl ? "zoom-in" : "default" }}
                                                                                                                    disabled={!imageUrl}
                                                                                                                >
                                                                                                                    {imageUrl ? (
                                                                                                                        <img
                                                                                                                            src={imageUrl}
                                                                                                                            alt={`${photoType} - Building ${row.serial}`}
                                                                                                                            className="asset-photo-card-media"
                                                                                                                        />
                                                                                                                    ) : (
                                                                                                                        <div className="asset-photo-card-media d-flex align-items-center justify-content-center text-muted">
                                                                                                                            No image URL
                                                                                                                        </div>
                                                                                                                    )}
                                                                                                                    <div className="asset-photo-card-body">
                                                                                                                        <div className="asset-photo-card-title">{photoType}</div>
                                                                                                                        <p className="asset-photo-card-subtitle mb-0">
                                                                                                                            {updatingPhotoId === photoKey ? "Updating photo..." : "Tap to view full image"}
                                                                                                                        </p>
                                                                                                                    </div>
                                                                                                                </button>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    );
                                                                                                })
                                                                                            ) : (
                                                                                                <div className="col-12 text-muted">No images uploaded for this building yet.</div>
                                                                                            )}
                                                                                        </div>
                                                                                        <div className="d-flex justify-content-end mt-3">
                                                                                            <Button
                                                                                                color="primary"
                                                                                                size="sm"
                                                                                                onClick={async () => {
                                                                                                    setBuildingStepTabForSlot(row.serial, "accommodation");
                                                                                                    const nextBuildingId =
                                                                                                        draft.id ||
                                                                                                        editBuildingsBySlot[row.serial]?.id ||
                                                                                                        "";
                                                                                                    if (nextBuildingId) {
                                                                                                        const floorCodes = getBuildingFloorCodes(draft);
                                                                                                        const defaultFloor = floorCodes[0] || "GROUND_FLOOR";
                                                                                                        setAccommodationDrafts((prev) => ({
                                                                                                            ...prev,
                                                                                                            [row.serial]: prev[row.serial] || createEmptyAccommodationDraft(defaultFloor),
                                                                                                        }));
                                                                                                        await ensureBuildingAccommodationsLoaded(String(nextBuildingId));
                                                                                                    }
                                                                                                }}
                                                                                            >
                                                                                                Next: Step 2.1.2d Building Accommodation
                                                                                            </Button>
                                                                                        </div>
                                                                                        </>
                                                                                    );
                                                                                })()}
                                                                            </div>
                                                                        ) : null}

                                                                        {currentStep === "accommodation" ? (
                                                                            <div className="mt-3 border-top pt-3">
                                                                                <h6 className="mb-3">
                                                                                    Step 2.1.2d - Building Accommodation (Building {row.serial})
                                                                                </h6>
                                                                                {(() => {
                                                                                    const buildingId =
                                                                                        draft.id ||
                                                                                        editBuildingsBySlot[row.serial]?.id ||
                                                                                        "";
                                                                                    if (!buildingId) {
                                                                                        return (
                                                                                            <p className="text-muted mb-0">
                                                                                                Save building form first to manage accommodations.
                                                                                            </p>
                                                                                        );
                                                                                    }

                                                                                    const floorCodes = getBuildingFloorCodes(draft);
                                                                                    const aDraft =
                                                                                        accommodationDrafts[row.serial] ||
                                                                                        createEmptyAccommodationDraft(floorCodes[0] || "GROUND_FLOOR");
                                                                                    const items =
                                                                                        buildingAccommodationsByBuildingId[String(buildingId)] || [];
                                                                                    const loading =
                                                                                        loadingBuildingAccommodationsForBuildingId === String(buildingId);
                                                                                    const usedNamesForSelectedFloor = new Set(
                                                                                        items
                                                                                            .filter(
                                                                                                (item) =>
                                                                                                    String(item.floor || "").toUpperCase() ===
                                                                                                    String(aDraft.floor || "").toUpperCase()
                                                                                            )
                                                                                            .map((item) =>
                                                                                                String(item.name || "").toUpperCase() === "OTHERS"
                                                                                                    ? String(item.other_name || item.otherName || "")
                                                                                                          .trim()
                                                                                                          .toLowerCase()
                                                                                                    : String(item.name || "")
                                                                                                          .trim()
                                                                                                          .toLowerCase()
                                                                                            )
                                                                                            .filter(Boolean)
                                                                                    );
                                                                                    const availableTypes = ACCOMMODATION_TYPE_OPTIONS.filter((type) => {
                                                                                        if (type === "OTHERS") return true;
                                                                                        return !usedNamesForSelectedFloor.has(type.toLowerCase());
                                                                                    });

                                                                                    return (
                                                                                        <div>
                                                                                            <Row className="g-3">
                                                                                                <Col md={3}>
                                                                                                    <FormGroup className="mb-0">
                                                                                                        <Label>Floor</Label>
                                                                                                        <Input
                                                                                                            type="select"
                                                                                                            value={aDraft.floor || floorCodes[0] || "GROUND_FLOOR"}
                                                                                                            onChange={(e) =>
                                                                                                                setAccommodationDrafts((prev) => ({
                                                                                                                    ...prev,
                                                                                                                    [row.serial]: {
                                                                                                                        ...aDraft,
                                                                                                                        floor: e.target.value,
                                                                                                                    },
                                                                                                                }))
                                                                                                            }
                                                                                                        >
                                                                                                            {floorCodes.map((floorCode) => (
                                                                                                                <option key={`${row.serial}-${floorCode}`} value={floorCode}>
                                                                                                                    {floorCodeToLabel(floorCode)}
                                                                                                                </option>
                                                                                                            ))}
                                                                                                        </Input>
                                                                                                    </FormGroup>
                                                                                                </Col>
                                                                                                <Col md={4}>
                                                                                                    <FormGroup className="mb-0">
                                                                                                        <Label>Accommodation Type</Label>
                                                                                                        <Input
                                                                                                            type="select"
                                                                                                            value={aDraft.name || ""}
                                                                                                            onChange={(e) =>
                                                                                                                setAccommodationDrafts((prev) => ({
                                                                                                                    ...prev,
                                                                                                                    [row.serial]: {
                                                                                                                        ...aDraft,
                                                                                                                        name: e.target.value,
                                                                                                                        other_name:
                                                                                                                            e.target.value === "OTHERS"
                                                                                                                                ? aDraft.other_name || ""
                                                                                                                                : "",
                                                                                                                    },
                                                                                                                }))
                                                                                                            }
                                                                                                        >
                                                                                                            <option value="">Select Type</option>
                                                                                                            {availableTypes.map((type) => (
                                                                                                                <option key={`${row.serial}-${type}`} value={type}>
                                                                                                                    {type === "OTHERS" ? "Others (Specify)" : type}
                                                                                                                </option>
                                                                                                            ))}
                                                                                                        </Input>
                                                                                                    </FormGroup>
                                                                                                </Col>
                                                                                                <Col md={2}>
                                                                                                    <FormGroup className="mb-0">
                                                                                                        <Label>Units</Label>
                                                                                                        <Input
                                                                                                            type="number"
                                                                                                            min="1"
                                                                                                            value={aDraft.total || ""}
                                                                                                            onChange={(e) =>
                                                                                                                setAccommodationDrafts((prev) => ({
                                                                                                                    ...prev,
                                                                                                                    [row.serial]: {
                                                                                                                        ...aDraft,
                                                                                                                        total: e.target.value,
                                                                                                                    },
                                                                                                                }))
                                                                                                            }
                                                                                                        />
                                                                                                    </FormGroup>
                                                                                                </Col>
                                                                                                <Col md={3}>
                                                                                                    <div className="d-flex h-100 align-items-end gap-2 w-100">
                                                                                                        <Button
                                                                                                            color="success"
                                                                                                            className="flex-fill"
                                                                                                            size="sm"
                                                                                                            onClick={() => saveAccommodationForSlot(row.serial)}
                                                                                                            disabled={savingAccommodationSlot === row.serial}
                                                                                                        >
                                                                                                            {savingAccommodationSlot === row.serial
                                                                                                                ? "Saving..."
                                                                                                                : "Save Accommodation"}
                                                                                                        </Button>
                                                                                                        <Button
                                                                                                            color="primary"
                                                                                                            outline
                                                                                                            className="flex-fill"
                                                                                                            size="sm"
                                                                                                            onClick={() => finishAccommodationForSlot(row.serial)}
                                                                                                        >
                                                                                                            Finish
                                                                                                        </Button>
                                                                                                    </div>
                                                                                                </Col>
                                                                                                {aDraft.name === "OTHERS" ? (
                                                                                                    <Col md={6}>
                                                                                                        <FormGroup className="mb-0">
                                                                                                            <Label>Specify Other Type</Label>
                                                                                                            <Input
                                                                                                                type="text"
                                                                                                                value={aDraft.other_name || ""}
                                                                                                                onChange={(e) =>
                                                                                                                    setAccommodationDrafts((prev) => ({
                                                                                                                        ...prev,
                                                                                                                        [row.serial]: {
                                                                                                                            ...aDraft,
                                                                                                                            other_name: e.target.value,
                                                                                                                        },
                                                                                                                    }))
                                                                                                                }
                                                                                                            />
                                                                                                        </FormGroup>
                                                                                                    </Col>
                                                                                                ) : null}
                                                                                            </Row>

                                                                                            <div className="mt-3">
                                                                                                {loading ? (
                                                                                                    <div className="text-muted">Loading accommodations...</div>
                                                                                                ) : floorCodes.map((floorCode) => {
                                                                                                    const floorItems = items.filter(
                                                                                                        (item) => String(item.floor || "").toUpperCase() === floorCode
                                                                                                    );
                                                                                                    return (
                                                                                                        <div key={`${row.serial}-floor-${floorCode}`} className="border rounded p-2 mb-2">
                                                                                                            <div className="fw-semibold mb-1">
                                                                                                                {floorCodeToLabel(floorCode)}
                                                                                                            </div>
                                                                                                            {floorItems.length ? (
                                                                                                                <Table responsive size="sm" className="mb-0 align-middle">
                                                                                                                    <thead>
                                                                                                                        <tr>
                                                                                                                            <th>Type</th>
                                                                                                                            <th className="text-end">Units</th>
                                                                                                                            <th className="text-end">Action</th>
                                                                                                                        </tr>
                                                                                                                    </thead>
                                                                                                                    <tbody>
                                                                                                                        {floorItems.map((item) => (
                                                                                                                            <tr key={`${floorCode}-${item.id || getAccommodationDisplayName(item)}`}>
                                                                                                                                <td>{getAccommodationDisplayName(item)}</td>
                                                                                                                                <td className="text-end">{item.total ?? "-"}</td>
                                                                                                                                <td className="text-end">
                                                                                                                                    <Button
                                                                                                                                        color="danger"
                                                                                                                                        size="sm"
                                                                                                                                        outline
                                                                                                                                        onClick={() =>
                                                                                                                                            deleteAccommodationItem(
                                                                                                                                                row.serial,
                                                                                                                                                String(buildingId),
                                                                                                                                                item
                                                                                                                                            )
                                                                                                                                        }
                                                                                                                                        disabled={deletingAccommodationId === item.id}
                                                                                                                                    >
                                                                                                                                        {deletingAccommodationId === item.id ? "Deleting..." : "Delete"}
                                                                                                                                    </Button>
                                                                                                                                </td>
                                                                                                                            </tr>
                                                                                                                        ))}
                                                                                                                    </tbody>
                                                                                                                </Table>
                                                                                                            ) : (
                                                                                                                <div className="text-muted small">No accommodations added.</div>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    );
                                                                                                })}
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                })()}
                                                                            </div>
                                                                        ) : null}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <p className="text-muted mb-0">
                                                        Number of buildings is not set in Step 2.1.
                                                    </p>
                                                )}
                                            </div>
                                        ) : null}
                                        {editBuildingStepIndex === 3 ? (
                                            <div style={editBuildingStepIndex > highestSavedStepIndex ? { pointerEvents: "none", opacity: 0.7 } : {}}>
                                            <>
                                                <Row className="g-3 mb-3">
                                                    <Col md={4}>
                                                        <BuildingSummaryCard title="Captured Site Works" value={editSiteWorks.length} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <BuildingSummaryCard
                                                            title="With Images"
                                                            value={editSiteWorks.filter((item) => (siteWorkPhotosBySiteWorkId[String(item?.id || "")] || []).length > 0).length}
                                                        />
                                                    </Col>
                                                    <Col md={4}>
                                                        <BuildingSummaryCard title="Required" value={selectedEditAssetHasSiteWork ? "Yes" : "No"} />
                                                    </Col>
                                                </Row>

                                                <div className="border rounded p-3 mb-3 bg-light-subtle">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <div>
                                                            <h6 className="mb-1">Step 2.1.3 - Site Work Details</h6>
                                                            <div className="text-muted small">
                                                                Capture the same site works and photos handled at the end of the mobile flow.
                                                            </div>
                                                        </div>
                                                        <Button color="light" size="sm" onClick={resetSiteWorkDraft} disabled={savingSiteWork}>
                                                            New Site Work
                                                        </Button>
                                                    </div>
                                                    <Row className="g-3">
                                                        <Col md={4}>
                                                            <FormGroup className="mb-0">
                                                                <Label>Site Work Type</Label>
                                                                <Input
                                                                    type="select"
                                                                    value={siteWorkDraft.site_work_type || ""}
                                                                    onChange={(e) =>
                                                                        setSiteWorkDraft((prev) => ({
                                                                            ...prev,
                                                                            site_work_type: e.target.value,
                                                                            ...(e.target.value === "OTHERS" ? {} : { other_specify: "" }),
                                                                        }))
                                                                    }
                                                                >
                                                                    <option value="">Select Site Work Type</option>
                                                                    {SITE_WORK_TYPE_OPTIONS.map((item) => (
                                                                        <option key={item.value} value={item.value}>
                                                                            {item.label}
                                                                        </option>
                                                                    ))}
                                                                </Input>
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={4}>
                                                            <FormGroup className="mb-0">
                                                                <Label>Condition</Label>
                                                                <Input
                                                                    type="select"
                                                                    value={siteWorkDraft.observed_condition || ""}
                                                                    onChange={(e) =>
                                                                        setSiteWorkDraft((prev) => ({
                                                                            ...prev,
                                                                            observed_condition: e.target.value,
                                                                        }))
                                                                    }
                                                                >
                                                                    <option value="">Select Condition</option>
                                                                    {buildingObservedConditions.map((item) => (
                                                                        <option key={item.id || item.code || item.name} value={item.code || item.name}>
                                                                            {item.name || item.code}
                                                                        </option>
                                                                    ))}
                                                                </Input>
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={4}>
                                                            <FormGroup className="mb-0">
                                                                <Label>Serial Number</Label>
                                                                <Input
                                                                    type="text"
                                                                    value={siteWorkDraft.serial_number || ""}
                                                                    onChange={(e) =>
                                                                        setSiteWorkDraft((prev) => ({
                                                                            ...prev,
                                                                            serial_number: e.target.value,
                                                                        }))
                                                                    }
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        {siteWorkDraft.site_work_type === "OTHERS" ? (
                                                            <Col md={6}>
                                                                <FormGroup className="mb-0">
                                                                    <Label>Other Specify</Label>
                                                                    <Input
                                                                        type="text"
                                                                        value={siteWorkDraft.other_specify || ""}
                                                                        onChange={(e) =>
                                                                            setSiteWorkDraft((prev) => ({
                                                                                ...prev,
                                                                                other_specify: e.target.value,
                                                                            }))
                                                                        }
                                                                    />
                                                                </FormGroup>
                                                            </Col>
                                                        ) : null}
                                                        <Col md={siteWorkDraft.site_work_type === "OTHERS" ? 6 : 4}>
                                                            <FormGroup className="mb-0">
                                                                <Label>Dimensions</Label>
                                                                <Input
                                                                    type="text"
                                                                    value={siteWorkDraft.dimensions || ""}
                                                                    onChange={(e) =>
                                                                        setSiteWorkDraft((prev) => ({
                                                                            ...prev,
                                                                            dimensions: e.target.value,
                                                                        }))
                                                                    }
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={4}>
                                                            <FormGroup className="mb-0">
                                                                <Label>Measurement (sqm)</Label>
                                                                <Input
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={siteWorkDraft.measurement || ""}
                                                                    onChange={(e) =>
                                                                        setSiteWorkDraft((prev) => ({
                                                                            ...prev,
                                                                            measurement: e.target.value,
                                                                        }))
                                                                    }
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={4}>
                                                            <FormGroup className="mb-0">
                                                                <Label>Construction rate/sqm (running meter)</Label>
                                                                <Input
                                                                    type="text"
                                                                    value={formatInputWithCommas(siteWorkDraft.construction_rate || "")}
                                                                    onChange={(e) =>
                                                                        setSiteWorkDraft((prev) => ({
                                                                            ...prev,
                                                                            construction_rate: formatInputWithCommas(e.target.value),
                                                                        }))
                                                                    }
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={8}>
                                                            <FormGroup className="mb-0">
                                                                <Label>Description</Label>
                                                                <Input
                                                                    type="text"
                                                                    value={siteWorkDraft.description || ""}
                                                                    onChange={(e) =>
                                                                        setSiteWorkDraft((prev) => ({
                                                                            ...prev,
                                                                            description: e.target.value,
                                                                        }))
                                                                    }
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={4}>
                                                            <FormGroup className="mb-0">
                                                                <Label>Remark</Label>
                                                                <Input
                                                                    type="text"
                                                                    value={siteWorkDraft.remark || ""}
                                                                    onChange={(e) =>
                                                                        setSiteWorkDraft((prev) => ({
                                                                            ...prev,
                                                                            remark: e.target.value,
                                                                        }))
                                                                    }
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                    <div className="d-flex justify-content-end gap-2 mt-3">
                                                        {siteWorkDraft.id ? (
                                                            <Button color="light" onClick={resetSiteWorkDraft} disabled={savingSiteWork}>
                                                                Cancel Edit
                                                            </Button>
                                                        ) : null}
                                                        <Button color="primary" onClick={handleSaveSiteWork} disabled={savingSiteWork}>
                                                            {savingSiteWork ? "Saving..." : siteWorkDraft.id ? "Update Site Work" : "Save Site Work"}
                                                        </Button>
                                                    </div>
                                                </div>

                                                {loadingEditSiteWorks ? (
                                                    <div className="text-center py-3">
                                                        <Spinner size="sm" color="primary" className="me-2" />
                                                        <span className="text-muted">Loading site works...</span>
                                                    </div>
                                                ) : editSiteWorks.length ? (
                                                    <div className="d-flex flex-column gap-3">
                                                        {editSiteWorks.map((siteWork, index) => {
                                                            const siteWorkId = String(siteWork?.id || "");
                                                            const photos = siteWorkPhotosBySiteWorkId[siteWorkId] || [];
                                                            const loadingPhotos = loadingSiteWorkPhotosForSiteWorkId === siteWorkId;
                                                            return (
                                                                <div key={siteWorkId || `site-work-${index}`} className="border rounded p-3">
                                                                    <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                                                                        <div>
                                                                            <div className="fw-semibold">
                                                                                {index + 1}. {resolveSiteWorkTypeLabel(siteWork?.site_work_type || siteWork?.siteWorkType, siteWork?.other_specify || siteWork?.otherSpecify)}
                                                                            </div>
                                                                            <div className="text-muted small">
                                                                                Condition: {resolveObservedConditionLabel(siteWork?.observed_condition || siteWork?.observedCondition)}
                                                                                {" • "}
                                                                                Serial: {siteWork?.serial_number || siteWork?.serialNumber || "-"}
                                                                            </div>
                                                                            <div className="text-muted small">
                                                                                {siteWork?.description || "No description"}{siteWork?.dimensions ? ` • ${siteWork.dimensions}` : ""}
                                                                            </div>
                                                                        </div>
                                                                        <div className="d-flex gap-2">
                                                                            <Button color="light" size="sm" onClick={() => handleEditSiteWork(siteWork)}>
                                                                                Edit
                                                                            </Button>
                                                                            <Button
                                                                                color="danger"
                                                                                size="sm"
                                                                                onClick={() => handleDeleteSiteWork(siteWork)}
                                                                                disabled={deletingSiteWorkId === siteWorkId}
                                                                                title="Remove site work"
                                                                            >
                                                                                <i className={deletingSiteWorkId === siteWorkId ? "ri-loader-4-line" : "ri-delete-bin-line"} />
                                                                            </Button>
                                                                        </div>
                                                                    </div>

                                                                    <div className="row g-3">
                                                                        <div className="col-12 col-sm-6 col-md-4 col-xl-3">
                                                                            <div className="asset-photo-card-wrap h-100">
                                                                                <button
                                                                                    type="button"
                                                                                    className="asset-photo-camera-btn"
                                                                                    onClick={() => upsertSiteWorkPhoto(siteWorkId)}
                                                                                    title="Upload site work image"
                                                                                    disabled={String(updatingSiteWorkPhotoId || "").startsWith(`new-site-work-${siteWorkId}-`)}
                                                                                >
                                                                                    <i
                                                                                        className={
                                                                                            String(updatingSiteWorkPhotoId || "").startsWith(`new-site-work-${siteWorkId}-`)
                                                                                                ? "ri-loader-4-line"
                                                                                                : "ri-camera-line"
                                                                                        }
                                                                                    />
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="asset-photo-card"
                                                                                    onClick={() => upsertSiteWorkPhoto(siteWorkId)}
                                                                                >
                                                                                    <div className="asset-photo-card-media d-flex align-items-center justify-content-center">
                                                                                        <div className="text-center">
                                                                                            <i className="ri-camera-line fs-2 d-block mb-2"></i>
                                                                                            <span className="small">Upload Site Work Image</span>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="asset-photo-card-body">
                                                                                        <div className="asset-photo-card-title">New Photo</div>
                                                                                        <p className="asset-photo-card-subtitle mb-0">Choose photo type, then upload</p>
                                                                                    </div>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        {loadingPhotos ? (
                                                                            <div className="col-12 text-muted">Loading site work images...</div>
                                                                        ) : photos.length ? (
                                                                            photos.map((photo, photoIndex) => {
                                                                                const imageUrl = photo.photo_url || photo.url || photo.path || "";
                                                                                const photoType = photo.photo_type || photo.type || `Image ${photoIndex + 1}`;
                                                                                const photoInputId = `replace-site-work-photo-${siteWorkId}-${photo.id || photoIndex}`;
                                                                                const photoKey = photo?.id || `${siteWorkId}-${photoType}-${photoIndex}`;
                                                                                return (
                                                                                    <div key={photo.id || `${siteWorkId}-${photoIndex}`} className="col-12 col-sm-6 col-md-4 col-xl-3">
                                                                                        <div className="asset-photo-card-wrap h-100">
                                                                                            <input
                                                                                                id={photoInputId}
                                                                                                type="file"
                                                                                                accept="image/*"
                                                                                                className="d-none"
                                                                                                onChange={(e) => {
                                                                                                    const file = e.target.files?.[0];
                                                                                                    if (file) {
                                                                                                        upsertSiteWorkPhoto(siteWorkId, file, photo);
                                                                                                    }
                                                                                                    e.target.value = "";
                                                                                                }}
                                                                                            />
                                                                                            <button
                                                                                                type="button"
                                                                                                className="asset-photo-camera-btn"
                                                                                                onClick={(e) => {
                                                                                                    e.preventDefault();
                                                                                                    e.stopPropagation();
                                                                                                    const input = document.getElementById(photoInputId);
                                                                                                    input?.click();
                                                                                                }}
                                                                                                title="Change site work image"
                                                                                                disabled={updatingSiteWorkPhotoId === photoKey}
                                                                                            >
                                                                                                <i className={updatingSiteWorkPhotoId === photoKey ? "ri-loader-4-line" : "ri-camera-line"} />
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-sm btn-danger position-absolute"
                                                                                                style={{ top: 10, left: 10, zIndex: 3 }}
                                                                                                onClick={(e) => {
                                                                                                    e.preventDefault();
                                                                                                    e.stopPropagation();
                                                                                                    handleDeleteSiteWorkPhoto(siteWorkId, photo);
                                                                                                }}
                                                                                                disabled={updatingSiteWorkPhotoId === photo.id}
                                                                                            >
                                                                                                <i className="ri-delete-bin-line" />
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="asset-photo-card"
                                                                                                onClick={() => openImagePreview(imageUrl, `${photoType} - ${resolveSiteWorkTypeLabel(siteWork?.site_work_type || siteWork?.siteWorkType, siteWork?.other_specify || siteWork?.otherSpecify)}`)}
                                                                                                style={{ cursor: imageUrl ? "zoom-in" : "default" }}
                                                                                                disabled={!imageUrl}
                                                                                            >
                                                                                                {imageUrl ? (
                                                                                                    <img
                                                                                                        src={imageUrl}
                                                                                                        alt={`${photoType} - Site Work`}
                                                                                                        className="asset-photo-card-media"
                                                                                                    />
                                                                                                ) : (
                                                                                                    <div className="asset-photo-card-media d-flex align-items-center justify-content-center text-muted">
                                                                                                        No image URL
                                                                                                    </div>
                                                                                                )}
                                                                                                <div className="asset-photo-card-body">
                                                                                                    <div className="asset-photo-card-title">{photoType}</div>
                                                                                                    <p className="asset-photo-card-subtitle mb-0">
                                                                                                        {updatingSiteWorkPhotoId === photoKey ? "Updating photo..." : "Tap to view full image"}
                                                                                                    </p>
                                                                                                </div>
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })
                                                                        ) : (
                                                                            <div className="col-12 text-muted">No site work images uploaded yet.</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <p className="text-muted mb-0">
                                                        No site works recorded yet for this asset.
                                                    </p>
                                                )}
                                                </>
                                            </div>
                                        ) : null}
                                    </>
                                ) : (
                                    selectedEditAssetDraft && (
                                        <AssetDetailForm
                                            key={selectedEditAssetDraft.id || "edit-asset-form"}
                                            asset={selectedEditAssetDraft}
                                            projectCouncilId={
                                                editModalCouncilId || null
                                            }
                                            isLocked={editBuildingStepIndex > highestSavedStepIndex}
                                            onChange={(updated) => setSelectedEditAssetDraft(updated)}
                                        />
                                    )
                                )}
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <>
                            <AssetReviewFooter
                                selectedAsset={selectedEditAssetDraft}
                                isBuildingCategory={isEditAssetBuildingCategory}
                                savingEditAsset={savingEditAsset}
                                handleSaveEditAsset={handleSaveEditAsset}
                                handleMarkEditAssetReviewNotCompleted={handleMarkEditAssetReviewNotCompleted}
                                isIncompleteTab={isIncompleteTab}
                                isRemarkIssuesTab={isRemarkIssuesTab}
                                unreviewAssetById={unreviewAssetById}
                                clearReviewRemarkIssueByAssetId={clearReviewRemarkIssueByAssetId}
                                closeEditModal={closeEditModal}
                                isViewOnlyMode={editBuildingStepIndex > highestSavedStepIndex}
                            />

                            {selectedEditAssetDraft && isEditAssetBuildingCategory && (
                                <BuildingReviewFooter
                                    editBuildingStepIndex={editBuildingStepIndex}
                                    selectedBuildingDetailSlot={selectedBuildingDetailSlot}
                                    setSelectedBuildingDetailSlot={handleFinishBuilding}
                                    setEditBuildingStepIndex={setEditBuildingStepIndex}
                                    savingEditAsset={savingEditAsset}
                                    savingSecurityOverview={savingSecurityOverview}
                                    savingSiteWork={savingSiteWork}
                                    handleMarkEditAssetReviewNotCompleted={handleMarkEditAssetReviewNotCompleted}
                                    handleSaveBuildingAssetAndNext={handleSaveBuildingAssetAndNext}
                                    handleSaveSecurityOverviewAndNext={handleSaveSecurityOverviewAndNext}
                                    loadingSecurityOverview={loadingSecurityOverview}
                                    isIncompleteTab={isIncompleteTab}
                                    isRemarkIssuesTab={isRemarkIssuesTab}
                                    selectedEditAssetDraft={selectedEditAssetDraft}
                                    closeEditModal={closeEditModal}
                                    unreviewAssetById={unreviewAssetById}
                                    clearReviewRemarkIssueByAssetId={clearReviewRemarkIssueByAssetId}
                                    loadingEditBuildings={loadingEditBuildings}
                                    loadingEditSiteWorks={loadingEditSiteWorks}
                                    canMarkBuildingReviewed={canMarkBuildingReviewed}
                                    canCompleteSiteWorkStep={canCompleteSiteWorkStep}
                                    selectedEditAssetHasSiteWork={selectedEditAssetHasSiteWork}
                                    handleGoToBuildingSiteWorks={handleGoToBuildingSiteWorks}
                                    isViewOnlyMode={editBuildingStepIndex > highestSavedStepIndex}
                                    handleMarkBuildingAssetReviewed={handleMarkBuildingAssetReviewed}
                                />
                            )}

                            {(!selectedEditAssetDraft || !isEditAssetBuildingCategory) && (
                                <Button color="secondary" onClick={closeEditModal}>
                                    Close
                                </Button>
                            )}
                        </>
                    </ModalFooter>
                </Modal>

                <ImagePreviewModal
                    isOpen={imagePreviewOpen}
                    onClose={() => setImagePreviewOpen(false)}
                    imageUrl={imagePreviewUrl}
                    title={imagePreviewTitle}
                />
            </div>
        </React.Fragment>
    );
};

export default AssetValuationListTables;
